
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import RepairServiceReportModal from './RepairServiceReportModal';
import type { ReferenceImageBlock, ReferenceImageHit } from './TSFADAnalysis.shared';
import { repairApi, tsfadApi } from '../services/api';
import { getAuthToken } from '../services/auth';
import { getBaseUrl } from '../services/baseUrl';
import { showInAppAlert, showInAppConfirm } from '../services/inAppDialog';
import type { DeviceSlaAssignment } from '../types';
import { promptCaseCloseAfterServiceReport } from '../utils/serviceReportCaseClose';
import {
  getCurrentDeviceSlaAssignment,
  getDeviceSlaPeriodLabel
} from '../utils/deviceSla';
import {
  ReportTemplate,
  ServiceReportDocument,
  ServiceReportRecord
} from '../utils/serviceReportTemplate';

const MEASUREMENT_CAPTURE_ENABLED = true;

const BRAND_TEXT = '#123F43';
const BRAND_MUTED = '#64716F';
const BRAND_SURFACE = '#F2EEE7';
const BRAND_SURFACE_SOFT = '#FBF8F2';
const BRAND_BORDER = '#DDD4C8';
const BRAND_CARD = '#FFFDF9';
const BRAND_TEXT_SOFT = 'rgba(18, 63, 67, 0.08)';
const BRAND_TEXT_SOFT_BORDER = 'rgba(18, 63, 67, 0.18)';
const BRAND_ACCENT = '#D99A33';
const BRAND_ACCENT_SOFT = '#F6E7C9';
const BRAND_ACCENT_TEXT = '#8A611E';
const BRAND_SUCCESS_SOFT = '#E7F2EC';
const BRAND_SUCCESS_BORDER = '#BDD4C7';
const BRAND_SUCCESS_TEXT = '#25624D';
const BRAND_DANGER_SOFT = '#FDEDEC';
const BRAND_DANGER_BORDER = '#E7B9B4';
const BRAND_DANGER_TEXT = '#A63B32';

type PartUsage = {
  id: string;
  partNumber: string;
  description: string;
  batchNumber: string;
  quantity: number | null;
};

type ChecklistType = 'verification' | 'repair';

type StepDetailMetadata = Record<string, unknown>;

type FeedbackScope = 'CAUSE' | 'VERIFICATION' | 'REPAIR';

type DeactivationInfo = {
  flaggedAt?: string;
  feedbackId?: string | null;
  feedbackType?: string | null;
  severity?: string | null;
  title?: string | null;
  description?: string | null;
  impact?: string | null;
  scope?: FeedbackScope;
  itemId?: string | null;
  itemLabel?: string | null;
  technicianId?: string | null;
  technicianName?: string | null;
};

type StepCapture = {
  type: 'MEASUREMENT';
  parameter?: string;
  units?: string;
  target?: number | string | null;
  expectedLow?: number | string | null;
  expectedHigh?: number | string | null;
  tolerance?: number | string | null;
  method?: string;
  tool?: string;
  notes?: string;
  options?: Array<number | string>;
};

type StepAttachment = {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  dataUrl: string;
  addedAt: string;
};

type StructuredStepNode = {
  number?: string | null;
  description?: string | null;
  tags?: string[];
  metadata?: StepDetailMetadata | null;
  capture?: StepCapture | null;
  subSteps?: StructuredStepNode[];
};

type StepStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETE';

type StepMetadata = {
  analysisId?: string | null;
  causeId?: string | null;
  probability?: string | null;
  summary?: string | null;
  planNotes?: string[];
  requiredTools?: string[];
  requiredParts?: string[];
  referenceImages?: ReferenceImageHit[];
  referenceImageBlocks?: ReferenceImageBlock[];
  structuredRepairSteps?: StructuredStepNode[];
  structuredTestingSteps?: StructuredStepNode[];
  repairSteps?: string[];
  testingSteps?: string[];
  verificationCompleted?: string[];
  normalizedVerificationCompleted?: string[];
  repairCompleted?: string[];
  normalizedRepairCompleted?: string[];
  partsUsed?: PartUsage[];
  captureValues?: Record<string, string>;
  attachments?: StepAttachment[];
  deactivated?: boolean;
  deactivationInfo?: DeactivationInfo | null;
  [key: string]: unknown;
};

type FeedbackItemOption = {
  id: string;
  label: string;
  type: 'VERIFICATION' | 'REPAIR';
};

type FeedbackModalState = {
  stepId: string;
  stepLabel: string;
  causeId?: string | null;
  analysisId?: string | null;
  summary?: string | null;
  availableItems: FeedbackItemOption[];
};

type FeedbackLaunchPayload = FeedbackModalState & {
  defaultScope: FeedbackScope;
  defaultItemId?: string | null;
};

type ImageFeedbackTargetState = {
  stepId: string;
  stepLabel: string;
  causeId?: string | null;
  analysisId?: string | null;
  summary?: string | null;
  image: ReferenceImageHit;
};

type DocumentationFeedbackForm = {
  feedbackType: string;
  severity: string;
  title: string;
  description: string;
  impact: string;
};

const createDocumentationFeedbackDefaults = (): DocumentationFeedbackForm => ({
  feedbackType: 'WRONG_PROCEDURE',
  severity: 'MEDIUM',
  title: '',
  description: '',
  impact: ''
});

const FEEDBACK_TYPE_OPTIONS = [
  { value: 'MISSING_CAUSE', label: 'Missing Failure Cause' },
  { value: 'WRONG_PROCEDURE', label: 'Incorrect Procedure' },
  { value: 'UNCLEAR_INSTRUCTION', label: 'Unclear Instructions' },
  { value: 'MISSING_DIAGRAM', label: 'Missing/Poor Diagram' },
  { value: 'WRONG_PARTS', label: 'Incorrect Parts Info' },
  { value: 'SAFETY_CONCERN', label: 'Safety Issue' }
];

const FEEDBACK_SEVERITY_OPTIONS = [
  { value: 'LOW', label: 'Low - Minor inconvenience' },
  { value: 'MEDIUM', label: 'Medium - Time wasted' },
  { value: 'HIGH', label: 'High - Significant impact' },
  { value: 'CRITICAL', label: 'Critical - Safety risk' }
];

const MAX_ATTACHMENT_BYTES = 6 * 1024 * 1024;
const MAX_ATTACHMENTS_PER_STEP = 12;

const createPartUsageId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const randomSegment = Math.random().toString(36).slice(2, 10);
  const timestampSegment = Date.now().toString(36);
  return `part-${randomSegment}-${timestampSegment}`;
};

const createAttachmentId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `attachment-${crypto.randomUUID()}`;
  }
  const randomSegment = Math.random().toString(36).slice(2, 10);
  const timestampSegment = Date.now().toString(36);
  return `attachment-${randomSegment}-${timestampSegment}`;
};

const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Unable to process file.'));
      }
    };
    reader.onerror = () => reject(reader.error || new Error('Unable to read file.'));
    reader.readAsDataURL(file);
  });

function AuthenticatedReferenceImage({
  src,
  alt,
  style,
  placeholderStyle,
  placeholderText = 'No preview'
}: {
  src?: string | null;
  alt: string;
  style: React.CSSProperties;
  placeholderStyle: React.CSSProperties;
  placeholderText?: string;
}) {
  const [resolvedSrc, setResolvedSrc] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    if (!src) {
      setResolvedSrc(null);
      setFailed(false);
      return undefined;
    }

    if (/^(?:data|blob):/i.test(src)) {
      setResolvedSrc(src);
      setFailed(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setResolvedSrc(null);
    setFailed(false);

    const fetchImage = async () => {
      const token = getAuthToken();
      const baseUrl = /^(?:https?:|data:|blob:)/i.test(src)
        ? ''
        : await getBaseUrl().catch(() => '');
      const requestUrl =
        baseUrl && !/^(?:https?:|data:|blob:)/i.test(src)
          ? `${baseUrl.replace(/\/+$/, '')}${src.startsWith('/') ? src : `/${src}`}`
          : src;
      const response = await fetch(requestUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      if (!response.ok) throw new Error('Image unavailable');
      return response.blob();
    };

    fetchImage()
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setResolvedSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!src) {
    return <div style={placeholderStyle}>{placeholderText}</div>;
  }

  if (failed) {
    return <div style={placeholderStyle}>Image unavailable</div>;
  }

  if (!resolvedSrc) {
    return <div style={placeholderStyle}>Loading image...</div>;
  }

  return <img src={resolvedSrc} alt={alt} style={style} />;
}

const dedupeReferenceImages = (images: ReferenceImageHit[] = []) => {
  const byId = new Map<string, ReferenceImageHit>();
  images.forEach(image => {
    const imageId = String(image?.id || '').trim();
    if (!imageId || byId.has(imageId)) return;
    byId.set(imageId, image);
  });
  return Array.from(byId.values());
};

const findNonOverlappingPhraseRange = (
  text: string,
  phrase: string,
  occupied: Array<{ start: number; end: number }>
) => {
  const source = String(text || '');
  const target = String(phrase || '').trim();
  if (!source || !target) return null;
  const lowerSource = source.toLowerCase();
  const lowerTarget = target.toLowerCase();
  let fromIndex = 0;
  while (fromIndex < lowerSource.length) {
    const start = lowerSource.indexOf(lowerTarget, fromIndex);
    if (start < 0) return null;
    const end = start + target.length;
    const overlaps = occupied.some(range => start < range.end && end > range.start);
    if (!overlaps) return { start, end };
    fromIndex = start + 1;
  }
  return null;
};

const toSafeString = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.trim();
};

const toSafeQuantity = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value >= 0 ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed >= 0 ? parsed : null;
    }
  }
  return null;
};

const normalizePartUsage = (value: unknown, fallbackId?: string): PartUsage | null => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return {
      id: fallbackId || createPartUsageId(),
      partNumber: '',
      description: value.trim(),
      batchNumber: '',
      quantity: null
    };
  }
  if (typeof value === 'object') {
    const details = value as Record<string, unknown>;
    const rawId = typeof details.id === 'string' ? details.id.trim() : '';
    const rawQuantity = 'quantity' in details ? details.quantity : details.qty;
    return {
      id: rawId || fallbackId || createPartUsageId(),
      partNumber: toSafeString(details.partNumber ?? details.number ?? ''),
      description: toSafeString(details.description ?? details.label ?? ''),
      batchNumber: toSafeString(details.batchNumber ?? details.batch ?? ''),
      quantity: toSafeQuantity(rawQuantity ?? null)
    };
  }
  return null;
};

const normalizePartsUsed = (value: unknown): PartUsage[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry, index) => normalizePartUsage(entry, `legacy-part-${index}`))
    .filter((entry): entry is PartUsage => entry !== null);
};

const FUNCTIONAL_TEST_TYPE_COPY: Record<
  FunctionalTestType,
  {
    shortLabel: string;
    buttonLabel: string;
    title: string;
    description: string;
    unavailableMessage: string;
  }
> = {
  MFTP: {
    shortLabel: 'mFTP',
    buttonLabel: 'Minimal Functional Test (mFTP)',
    title: 'Minimal Functional Test Protocol (mFTP)',
    description: 'Quick functional verification after low-impact repairs.',
    unavailableMessage: 'No Final Functional Test Protocol Available for the Minimal Functional Test.'
  },
  CFTP: {
    shortLabel: 'cFTP',
    buttonLabel: 'Comprehensive Functional Test Protocol (cFTP)',
    title: 'Comprehensive Functional Test Protocol (cFTP)',
    description: 'Full operational/performance verification after high-impact repairs.',
    unavailableMessage:
      'No Final Functional Test Protocol Available for the Comprehensive Functional Test.'
  }
};

const EMPTY_FUNCTIONAL_TEST_STATE: FunctionalTestState = {
  selectedType: null,
  reference: null,
  completedItemIds: [],
  normalizedCompletedItemIds: [],
  notes: '',
  status: 'NONE',
  savedAt: null
};

const EMPTY_REPAIR_TIME_TRACKING_STATE: RepairTimeTrackingState = {
  accumulatedMinutes: 0,
  phase: 'PAUSED',
  updatedAt: null
};

const REPAIR_TIME_TRACKING_STORAGE_PREFIX = 'repair-task-timer-anchor:';

const normalizeFunctionalTestType = (value: unknown): FunctionalTestType | null => {
  const text = String(value || '')
    .trim()
    .toUpperCase();
  return text === 'MFTP' || text === 'CFTP' ? text : null;
};

const normalizeFunctionalTestReferenceChecklistItem = (
  value: unknown,
  fallbackId: string
): FunctionalTestReferenceChecklistItem | null => {
  if (!value || typeof value !== 'object') return null;
  const source = value as Record<string, unknown>;
  const label = typeof source.label === 'string' ? source.label.trim() : '';
  if (!label) return null;
  const rawId = typeof source.id === 'string' ? source.id.trim() : '';
  return {
    id: rawId || fallbackId,
    label,
    spec:
      typeof source.spec === 'string' && source.spec.trim() ? source.spec.trim() : null
  };
};

const normalizeFunctionalTestReferenceStep = (
  value: unknown,
  index: number
): FunctionalTestReferenceStep | null => {
  if (!value || typeof value !== 'object') return null;
  const source = value as Record<string, unknown>;
  const metadata =
    source.metadata && typeof source.metadata === 'object'
      ? (source.metadata as Record<string, unknown>)
      : {};
  const label = typeof source.label === 'string' ? source.label.trim() : '';
  if (!label) return null;
  const stepKey =
    typeof source.stepKey === 'string' && source.stepKey.trim()
      ? source.stepKey.trim()
      : `functional-step-${index + 1}`;
  return {
    stepKey,
    label,
    metadata: {
      checklist: Array.isArray(metadata.checklist)
        ? metadata.checklist
            .map((item, itemIndex) =>
              normalizeFunctionalTestReferenceChecklistItem(
                item,
                `${stepKey}-${itemIndex + 1}`
              )
            )
            .filter((item): item is FunctionalTestReferenceChecklistItem => Boolean(item))
        : [],
      layout:
        typeof metadata.layout === 'string' && metadata.layout.trim()
          ? metadata.layout.trim()
          : null,
      allowUploads: metadata.allowUploads === true,
      allowNotes: metadata.allowNotes === true,
      attachmentLabel:
        typeof metadata.attachmentLabel === 'string' && metadata.attachmentLabel.trim()
          ? metadata.attachmentLabel.trim()
          : null,
      attachmentHint:
        typeof metadata.attachmentHint === 'string' && metadata.attachmentHint.trim()
          ? metadata.attachmentHint.trim()
          : null
    }
  };
};

const normalizeFunctionalTestReference = (value: unknown): FunctionalTestReference | null => {
  if (!value || typeof value !== 'object') return null;
  const source = value as Record<string, unknown>;
  const checklistType = normalizeFunctionalTestType(
    source.checklistType || source.selectedType || source.type
  );
  if (!checklistType) return null;
  return {
    checklistType,
    title:
      typeof source.title === 'string' && source.title.trim()
        ? source.title.trim()
        : FUNCTIONAL_TEST_TYPE_COPY[checklistType].title,
    revisionId:
      typeof source.revisionId === 'string' && source.revisionId.trim()
        ? source.revisionId.trim()
        : null,
    generatedAt:
      typeof source.generatedAt === 'string' && source.generatedAt.trim()
        ? source.generatedAt.trim()
        : null,
    isDefault: source.isDefault === true,
    defaultScope:
      typeof source.defaultScope === 'string' && source.defaultScope.trim()
        ? source.defaultScope.trim()
        : null,
    steps: Array.isArray(source.steps)
      ? source.steps
          .map((step, index) => normalizeFunctionalTestReferenceStep(step, index))
          .filter((step): step is FunctionalTestReferenceStep => Boolean(step))
      : []
  };
};

const collectFunctionalTestChecklistIds = (reference: FunctionalTestReference | null): string[] => {
  if (!reference) return [];
  return reference.steps.flatMap(step => {
    const checklist = Array.isArray(step.metadata?.checklist) ? step.metadata?.checklist ?? [] : [];
    return checklist
      .map(item => (typeof item.id === 'string' ? item.id.trim() : ''))
      .filter(Boolean);
  });
};

const deriveFunctionalTestStatus = (
  reference: FunctionalTestReference | null,
  completedItemIds: string[],
  notes: string
): FunctionalTestState['status'] => {
  if (!reference) return 'NONE';
  const totalIds = collectFunctionalTestChecklistIds(reference);
  if (totalIds.length > 0 && completedItemIds.length === totalIds.length) {
    return 'COMPLETE';
  }
  if (completedItemIds.length > 0 || notes.trim()) {
    return 'IN_PROGRESS';
  }
  return 'PENDING';
};

const normalizeFunctionalTestState = (value: unknown): FunctionalTestState => {
  if (!value || typeof value !== 'object') {
    return EMPTY_FUNCTIONAL_TEST_STATE;
  }
  const source = value as Record<string, unknown>;
  const selectedType = normalizeFunctionalTestType(
    source.selectedType || source.checklistType || source.type
  );
  const reference = normalizeFunctionalTestReference(source.reference || source);
  if (!selectedType || !reference) {
    return EMPTY_FUNCTIONAL_TEST_STATE;
  }
  const validIds = new Set(collectFunctionalTestChecklistIds(reference));
  const rawCompleted = Array.isArray(source.normalizedCompletedItemIds)
    ? source.normalizedCompletedItemIds
    : Array.isArray(source.completedItemIds)
    ? source.completedItemIds
    : [];
  const completedItemIds = rawCompleted
    .map(item => (typeof item === 'string' ? item.trim() : ''))
    .filter(item => item && validIds.has(item));
  const notes = typeof source.notes === 'string' ? source.notes : '';
  return {
    selectedType,
    reference,
    completedItemIds,
    normalizedCompletedItemIds: completedItemIds,
    notes,
    status: deriveFunctionalTestStatus(reference, completedItemIds, notes),
    savedAt:
      typeof source.savedAt === 'string' && source.savedAt.trim() ? source.savedAt.trim() : null
  };
};

const serializeFunctionalTestState = (
  value: FunctionalTestState
): Record<string, unknown> | null => {
  if (!value.selectedType || !value.reference) return null;
  const completedItemIds = Array.from(new Set(value.normalizedCompletedItemIds || value.completedItemIds));
  const notes = value.notes.trim();
  return {
    selectedType: value.selectedType,
    status: deriveFunctionalTestStatus(value.reference, completedItemIds, notes),
    completedItemIds,
    normalizedCompletedItemIds: completedItemIds,
    notes,
    reference: value.reference,
    savedAt: value.savedAt || null
  };
};

const normalizeTimeTrackingPhase = (
  value: unknown,
  fallbackStatus: unknown = 'IN_PROGRESS'
): TimeTrackingPhase => {
  const phase = String(value || '')
    .trim()
    .toUpperCase();
  if (phase === 'RUNNING' || phase === 'PAUSED' || phase === 'STOPPED') {
    return phase;
  }

  const taskStatus = String(fallbackStatus || '')
    .trim()
    .toUpperCase();
  if (taskStatus === 'COMPLETED') return 'STOPPED';
  if (taskStatus === 'ON_HOLD' || taskStatus === 'PENDING') return 'PAUSED';
  return 'RUNNING';
};

const normalizeTimeMinutes = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.round(value * 100) / 100;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed) && parsed >= 0) {
      return Math.round(parsed * 100) / 100;
    }
  }
  return null;
};

const roundMinutesToQuarterHour = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round(value / 15) * 15;
};

const formatHoursFromMinutes = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '';
  }
  const hours = Math.round((Math.max(0, value) / 60) * 100) / 100;
  return String(hours);
};

const parseHoursInputToMinutes = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 60 * 100) / 100;
};

const formatElapsedMinutes = (value: number): string => {
  const safeMinutes = Math.max(0, Math.round(value));
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }
  return `${minutes}m`;
};

const normalizeRepairTimeTrackingState = (
  value: unknown,
  fallbackStatus: unknown,
  fallbackDisplayMinutes: number | null
): RepairTimeTrackingState => {
  const source = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const displayMinutes = normalizeTimeMinutes(fallbackDisplayMinutes) ?? 0;
  const accumulatedMinutes = Math.max(
    normalizeTimeMinutes(source.accumulatedMinutes) ?? 0,
    displayMinutes
  );
  return {
    accumulatedMinutes,
    phase: normalizeTimeTrackingPhase(source.phase ?? source.state, fallbackStatus),
    updatedAt:
      typeof source.updatedAt === 'string' && source.updatedAt.trim()
        ? source.updatedAt.trim()
        : null
  };
};

const serializeRepairTimeTrackingState = (
  value: RepairTimeTrackingState
): Record<string, unknown> => ({
  accumulatedMinutes: Math.round(Math.max(0, value.accumulatedMinutes) * 100) / 100,
  phase: value.phase,
  updatedAt: value.updatedAt || null
});

const getRepairTimeTrackingStorageKey = (runId: string): string =>
  `${REPAIR_TIME_TRACKING_STORAGE_PREFIX}${runId}`;

const readRepairTimeTrackingAnchor = (
  runId: string
): LocalRepairTimeTrackingAnchor | null => {
  if (typeof window === 'undefined' || !runId.trim()) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(getRepairTimeTrackingStorageKey(runId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LocalRepairTimeTrackingAnchor>;
    if (
      typeof parsed?.runId !== 'string' ||
      typeof parsed?.caseId !== 'string' ||
      typeof parsed?.startedAt !== 'string'
    ) {
      return null;
    }
    if (!Number.isFinite(Date.parse(parsed.startedAt))) {
      return null;
    }
    return {
      runId: parsed.runId,
      caseId: parsed.caseId,
      startedAt: parsed.startedAt,
      baseMinutes: normalizeTimeMinutes(parsed.baseMinutes) ?? 0
    };
  } catch (_error) {
    return null;
  }
};

const writeRepairTimeTrackingAnchor = (
  runId: string,
  anchor: LocalRepairTimeTrackingAnchor
): void => {
  if (typeof window === 'undefined' || !runId.trim()) {
    return;
  }
  try {
    window.sessionStorage.setItem(getRepairTimeTrackingStorageKey(runId), JSON.stringify(anchor));
  } catch (_error) {
    // Ignore browser storage write failures; the timer is only a suggestion layer.
  }
};

const clearRepairTimeTrackingAnchor = (runId: string): void => {
  if (typeof window === 'undefined' || !runId.trim()) {
    return;
  }
  try {
    window.sessionStorage.removeItem(getRepairTimeTrackingStorageKey(runId));
  } catch (_error) {
    // Ignore browser storage delete failures; the timer is only a suggestion layer.
  }
};

const normalizePartLookupToken = (value: unknown): string =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '');

const stripPartFailureSuffix = (value: string): string =>
  String(value || '')
    .replace(/\|\s*Failure\s*[:-]?\s*[-+]?\d+(?:\.\d+)?%\s*$/i, '')
    .replace(/\(\s*[-+]?\d+(?:\.\d+)?%\s*\)\s*$/i, '')
    .trim();

const PART_NUMBER_TOKEN_PATTERN = /\b[A-Z0-9]{3,}(?:-[A-Z0-9]{2,})+\b|\b\d{5,}(?:-\d+)*\b/i;

const parsePartOptionText = (value: unknown): { partNumber: string; description: string } | null => {
  const raw = stripPartFailureSuffix(String(value || ''));
  if (!raw) return null;

  const parenthesizedPart = raw.match(/^(.*)\(([A-Z0-9-]{4,})\)\s*$/i);
  if (parenthesizedPart) {
    return {
      partNumber: parenthesizedPart[2].trim(),
      description: parenthesizedPart[1].trim()
    };
  }

  const prefixedPart = raw.match(/^([A-Z0-9-]{4,})\s*[-:]\s*(.+)$/i);
  if (prefixedPart) {
    return {
      partNumber: prefixedPart[1].trim(),
      description: prefixedPart[2].trim()
    };
  }

  const pipeSegments = raw
    .split('|')
    .map(segment => segment.trim())
    .filter(Boolean)
    .filter(segment => !/^failure\b/i.test(segment));

  if (pipeSegments.length > 0) {
    const explicitPartIndex = pipeSegments.findIndex(segment => PART_NUMBER_TOKEN_PATTERN.test(segment));
    const explicitPart = explicitPartIndex >= 0 ? pipeSegments[explicitPartIndex] : '';
    const partNumber = explicitPart ? (explicitPart.match(PART_NUMBER_TOKEN_PATTERN)?.[0] || '') : '';
    const inlineDescription = explicitPart
      ? explicitPart
          .replace(partNumber, ' ')
          .replace(/^p\/?n\b[:#\s-]*/i, '')
          .replace(/[()]/g, ' ')
          .replace(/\s*[-:]\s*/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      : '';
    const description = [
      ...pipeSegments.filter((_, index) => index !== explicitPartIndex),
      inlineDescription
    ]
      .filter(Boolean)
      .join(' | ')
      .trim();
    if (partNumber || description) {
      return {
        partNumber: partNumber.trim(),
        description
      };
    }
  }

  const inlinePart = raw.match(PART_NUMBER_TOKEN_PATTERN);
  if (inlinePart) {
    const partNumber = inlinePart[0].trim();
    const description = raw
      .replace(partNumber, '')
      .replace(/[()]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return {
      partNumber,
      description
    };
  }

  return null;
};

const buildPartSelectionOptions = (
  stepRequiredParts: unknown,
  analysisRequiredParts: unknown,
  partCatalog: unknown
): PartSelectionOption[] => {
  const options: PartSelectionOption[] = [];
  const seen = new Set<string>();
  const catalogItems = Array.isArray(partCatalog) ? partCatalog : [];
  const catalogByKey = new Map<string, RepairPartCatalogItem>();

  catalogItems.forEach(item => {
    if (!item || typeof item !== 'object') return;
    const catalogItem = item as RepairPartCatalogItem;
    const partNumber = toSafeString(catalogItem.partNumber || catalogItem.sku || '');
    const key = normalizePartLookupToken(partNumber);
    if (!key) return;
    if (!catalogByKey.has(key)) {
      catalogByKey.set(key, catalogItem);
    }
  });

  const appendOption = (
    rawValue: unknown,
    source: PartSelectionOption['source'],
    sourceLabel: string
  ) => {
    const parsed = parsePartOptionText(rawValue);
    if (!parsed?.partNumber) return;
    const key = normalizePartLookupToken(parsed.partNumber);
    if (!key || seen.has(key)) return;
    const catalogMatch = catalogByKey.get(key);
    const description =
      toSafeString(parsed.description) ||
      toSafeString(catalogMatch?.description || '');
    seen.add(key);
    options.push({
      key,
      partNumber: parsed.partNumber,
      description,
      source,
      sourceLabel,
      failureRatePercent:
        typeof catalogMatch?.failureRatePercent === 'number'
          ? catalogMatch.failureRatePercent
          : null
    });
  };

  (Array.isArray(stepRequiredParts) ? stepRequiredParts : []).forEach(item => {
    appendOption(item, 'STEP_REQUIRED', 'Suggested for this cause');
  });
  (Array.isArray(analysisRequiredParts) ? analysisRequiredParts : []).forEach(item => {
    appendOption(item, 'ANALYSIS_REQUIRED', 'Suggested in this analysis');
  });
  catalogItems.forEach(item => {
    if (!item || typeof item !== 'object') return;
    const catalogItem = item as RepairPartCatalogItem;
    const partNumber = toSafeString(catalogItem.partNumber || catalogItem.sku || '');
    const key = normalizePartLookupToken(partNumber);
    if (!key || seen.has(key)) return;
    const description = toSafeString(catalogItem.description || '');
    if (!partNumber || !description) return;
    seen.add(key);
    options.push({
      key,
      partNumber,
      description,
      source: 'FAILURE_RATE',
      sourceLabel: 'Failure-rate list',
      failureRatePercent:
        typeof catalogItem.failureRatePercent === 'number'
          ? catalogItem.failureRatePercent
          : null
    });
  });

  return options;
};

const formatPartOptionMeta = (option: PartSelectionOption): string => {
  const details = [option.sourceLabel];
  if (typeof option.failureRatePercent === 'number' && Number.isFinite(option.failureRatePercent)) {
    details.push(`Failure ${option.failureRatePercent}%`);
  }
  return details.join(' | ');
};

const normalizeAttachment = (value: unknown, fallbackId?: string): StepAttachment | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const entry = value as Record<string, unknown>;
  const rawDataUrl = typeof entry.dataUrl === 'string' ? entry.dataUrl.trim() : '';
  if (!rawDataUrl.startsWith('data:')) {
    return null;
  }
  const id =
    typeof entry.id === 'string' && entry.id.trim()
      ? entry.id.trim()
      : fallbackId || createAttachmentId();
  const name =
    typeof entry.name === 'string' && entry.name.trim() ? entry.name.trim() : 'Photo';
  const mimeDelimiter = rawDataUrl.indexOf(';');
  const derivedMime =
    mimeDelimiter > 5 ? rawDataUrl.slice(5, mimeDelimiter) : 'image/jpeg';
  const mimeType =
    typeof entry.mimeType === 'string' && entry.mimeType.trim()
      ? entry.mimeType.trim()
      : derivedMime;
  const size =
    typeof entry.size === 'number' && Number.isFinite(entry.size) && entry.size >= 0
      ? entry.size
      : 0;
  const addedAt =
    typeof entry.addedAt === 'string' && entry.addedAt.trim()
      ? entry.addedAt
      : new Date().toISOString();
  return {
    id,
    name,
    mimeType,
    size,
    dataUrl: rawDataUrl,
    addedAt
  };
};

const normalizeAttachments = (value: unknown): StepAttachment[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry, index) => normalizeAttachment(entry, `attachment-${index}`))
    .filter((entry): entry is StepAttachment => entry !== null);
};

const toDisplayText = (value: number | string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value.toString();
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  return null;
};

const toNumeric = (value?: number | string | null): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const match = value.match(/-?\d+(?:\.\d+)?/);
    if (match) {
      const parsed = Number(match[0]);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
};

type CaptureUiMode = 'hidden' | 'numeric';

const getCaptureUiMode = (capture: StepCapture): CaptureUiMode => {
  const low = toNumeric(capture.expectedLow);
  const high = toNumeric(capture.expectedHigh);
  const target = toNumeric(capture.target);
  const tolerance = toNumeric(capture.tolerance);
  const hasBoundRange =
    (low !== null && high !== null && Math.abs(low - high) > 1e-4) ||
    (low !== null && high === null) ||
    (low === null && high !== null);
  const hasTargetToleranceRange =
    target !== null && tolerance !== null && tolerance > 0;
  return hasBoundRange || hasTargetToleranceRange ? 'numeric' : 'hidden';
};

const formatMeasurementPlaceholder = (capture: StepCapture): string => {
  const mode = getCaptureUiMode(capture);
  if (mode !== 'numeric') {
    return 'Value';
  }

  const low = toDisplayText(capture.expectedLow);
  const high = toDisplayText(capture.expectedHigh);
  if (low && high) {
    const unitSuffix = capture.units ? ` ${capture.units}` : '';
    return `${low}${unitSuffix} - ${high}${unitSuffix}`;
  }

  const target = toDisplayText(capture.target);
  if (target) {
    return capture.units ? `${target} ${capture.units}` : target;
  }

  return capture.units ? `Value (${capture.units})` : 'Value';
};

type CaptureEvaluation =
  | { status: 'valid'; message?: string }
  | { status: 'warning'; message?: string }
  | { status: 'invalid'; message: string };

const evaluateMeasurement = (capture: StepCapture, rawValue: string): CaptureEvaluation => {
  const trimmed = rawValue.trim();
  const mode = getCaptureUiMode(capture);
  if (mode === 'hidden') {
    return { status: 'valid' };
  }

  if (!trimmed) {
    return {
      status: 'invalid',
      message: 'Enter a measured value before completing this step.'
    };
  }

  const numericValue = toNumeric(trimmed);
  if (numericValue === null) {
    return {
      status: 'invalid',
      message: 'Enter a numeric value (digits with an optional decimal).'
    };
  }

  let min = toNumeric(capture.expectedLow);
  let max = toNumeric(capture.expectedHigh);
  const target = toNumeric(capture.target);
  const tolerance = toNumeric(capture.tolerance);

  if (min === null && max === null) {
    if (target !== null && tolerance !== null) {
      min = target - tolerance;
      max = target + tolerance;
    } else if (target !== null) {
      min = target;
      max = target;
    }
  }

  const epsilon = 1e-4;

  if (min !== null && max !== null) {
    const isValid = numericValue >= min - epsilon && numericValue <= max + epsilon;
    if (!isValid) {
      const rangeText =
        min === max
          ? `Target ${min}${capture.units ? ` ${capture.units}` : ''}`
          : `Expected ${min}${capture.units ? ` ${capture.units}` : ''} - ${max}${
              capture.units ? ` ${capture.units}` : ''
            }`;
      return { status: 'invalid', message: rangeText };
    }

    if (target !== null && tolerance !== null) {
      const optimalLow = target - tolerance / 2;
      const optimalHigh = target + tolerance / 2;
      if (numericValue < optimalLow - epsilon || numericValue > optimalHigh + epsilon) {
        return {
          status: 'warning',
          message: `Within tolerance but outside optimal band (${optimalLow} - ${optimalHigh}${
            capture.units ? ` ${capture.units}` : ''
          })`
        };
      }
    }

    return { status: 'valid' };
  }

  if (min !== null) {
    if (numericValue < min - epsilon) {
      return {
        status: 'invalid',
        message: `Must be >= ${min}${capture.units ? ` ${capture.units}` : ''}`
      };
    }
    return { status: 'valid' };
  }

  if (max !== null) {
    if (numericValue > max + epsilon) {
      return {
        status: 'invalid',
        message: `Must be <= ${max}${capture.units ? ` ${capture.units}` : ''}`
      };
    }
    return { status: 'valid' };
  }

  if (target !== null) {
    if (Math.abs(numericValue - target) <= epsilon) {
      return { status: 'valid' };
    }
    return {
      status: 'invalid',
      message: `Target ${target}${capture.units ? ` ${capture.units}` : ''}`
    };
  }

  return { status: 'valid' };
};

const formatMeasurementHint = (capture: StepCapture): string | null => {
  const mode = getCaptureUiMode(capture);
  const details: string[] = [];
  const low = toDisplayText(capture.expectedLow);
  const high = toDisplayText(capture.expectedHigh);
  if (mode === 'numeric' && low && high) {
    const unitSuffix = capture.units ? ` ${capture.units}` : '';
    details.push(`Range ${low}${unitSuffix} - ${high}${unitSuffix}`);
  }
  if (capture.method) {
    details.push(`Method: ${capture.method}`);
  }

  if (details.length === 0) return null;
  return details.join(' | ');
};


type RepairStep = {
  id: string;
  stepKey: string;
  sequence: number;
  label: string;
  status: StepStatus;
  notes: string;
  metadata: StepMetadata;
};

type CaseSummary = {
  id: string;
  caseCode?: string | null;
  caseNumber?: string | null;
  openedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  problemDescription?: string | null;
  customer?: {
    id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
  } | null;
  contact?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  device?: {
    id: string;
    modelNumber?: string | null;
    serialNumber?: string | null;
    manufacturer?: string | null;
    slaAssignments?: DeviceSlaAssignment[];
  } | null;
  location?: {
    name?: string | null;
    address?: string | null;
  } | null;
};

type AnalysisSummary = {
  id: string;
  modelId?: string | null;
  summary?: string | null;
  createdAt?: string | null;
  requiredParts?: string[];
  requiredTools?: string[];
};

type RepairPartCatalogItem = {
  id: string;
  sku?: string | null;
  partNumber?: string | null;
  description?: string | null;
  deviceModel?: string | null;
  criticality?: string | null;
  failureRatePercent?: number | null;
};

type PartSelectionOption = {
  key: string;
  partNumber: string;
  description: string;
  source: 'STEP_REQUIRED' | 'ANALYSIS_REQUIRED' | 'FAILURE_RATE';
  sourceLabel: string;
  failureRatePercent?: number | null;
};

type FunctionalTestType = 'MFTP' | 'CFTP';

type FunctionalTestReferenceChecklistItem = {
  id: string;
  label: string;
  spec?: string | null;
};

type FunctionalTestReferenceStep = {
  stepKey?: string | null;
  label: string;
  metadata?: {
    checklist?: FunctionalTestReferenceChecklistItem[];
    layout?: string | null;
    allowUploads?: boolean;
    allowNotes?: boolean;
    attachmentLabel?: string | null;
    attachmentHint?: string | null;
    [key: string]: unknown;
  };
};

type FunctionalTestReference = {
  checklistType: FunctionalTestType;
  title: string;
  revisionId?: string | null;
  generatedAt?: string | null;
  isDefault?: boolean;
  defaultScope?: string | null;
  steps: FunctionalTestReferenceStep[];
};

type FunctionalTestState = {
  selectedType: FunctionalTestType | null;
  reference: FunctionalTestReference | null;
  completedItemIds: string[];
  normalizedCompletedItemIds: string[];
  notes: string;
  status: 'NONE' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETE';
  savedAt?: string | null;
};

type TimeTrackingPhase = 'RUNNING' | 'PAUSED' | 'STOPPED';

type RepairTimeTrackingState = {
  accumulatedMinutes: number;
  phase: TimeTrackingPhase;
  updatedAt?: string | null;
};

type LocalRepairTimeTrackingAnchor = {
  runId: string;
  caseId: string;
  startedAt: string;
  baseMinutes: number;
};

type FunctionalTestReferenceResponse = {
  type: FunctionalTestType;
  modelId?: string | null;
  reference?: FunctionalTestReference | null;
};

type TaskSummary = {
  id: string;
  status?: string | null;
};

type RunSummary = {
  id: string;
  metadata?: Record<string, unknown>;
  notes?: string | null;
};

type WorkspacePayload = {
  caseSummary: CaseSummary | null;
  analysis: AnalysisSummary | null;
  task: TaskSummary | null;
  run: RunSummary | null;
  steps: RepairStep[];
  partCatalog?: RepairPartCatalogItem[];
  serviceReport?: ServiceReportRecord | null;
  serviceReportTemplate?: ReportTemplate | null;
  access?: {
    canWrite?: boolean;
    assignedUserId?: string | null;
    assignedUserName?: string | null;
  } | null;
};

type ChecklistItem = {
  id: string;
  label: string;
  number?: string;
  description?: string;
  depth: number;
  legacyIds: string[];
  value: string;
  tags?: string[];
  metadata?: StepDetailMetadata | null;
  capture?: StepCapture | null;
};

type StepResolutionState = 'pending' | 'verifying' | 'needsRepair' | 'resolved' | 'inactive';

type StepStatusDetails = {
  verificationItems: ChecklistItem[];
  repairItems: ChecklistItem[];
  verificationCompletedIds: string[];
  repairCompletedIds: string[];
  verificationChecked: number;
  verificationTotal: number;
  repairChecked: number;
  repairTotal: number;
  resolution: StepResolutionState;
  status: StepStatus;
};

type StepCardProps = {
  step: RepairStep;
  analysisRequiredParts: string[];
  partCatalog: RepairPartCatalogItem[];
  onToggleChecklist: (stepId: string, type: ChecklistType, key: string) => void;
  onSaveChecklistDraft: (stepId: string, type: ChecklistType, draft: string) => string | null;
  onSaveStep: (stepId: string) => void;
  onNotesChange: (stepId: string, notes: string) => void;
  onAddPart: (stepId: string, part: PartUsage) => void;
  onRemovePart: (stepId: string, partId: string) => void;
  onAddAttachment: (stepId: string, attachment: StepAttachment) => void;
  onRemoveAttachment: (stepId: string, attachmentId: string) => void;
  onCaptureChange: (stepId: string, itemId: string, value: string) => void;
  onOpenFeedback: (payload: FeedbackLaunchPayload) => void;
  onFlagReferenceImage: (payload: ImageFeedbackTargetState) => void;
  onReactivate: (stepId: string) => void;
  canEditChecklist: boolean;
  canSaveStep: boolean;
  stepDirty: boolean;
  stepSaving: boolean;
};

type DeviceExpertContextOption = {
  key: string;
  stepId: string;
  section: 'VERIFICATION' | 'REPAIR';
  number: string;
  description: string;
  label: string;
};

type DeviceExpertParentOption = {
  id: string;
  sequence: number;
  label: string;
  verificationItems: ChecklistItem[];
  repairItems: ChecklistItem[];
};

type DeviceExpertMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
};

const pageStyles: Record<string, React.CSSProperties> = {
  page: {
    background: BRAND_SURFACE,
    minHeight: '100vh',
    padding: '2.5rem 3rem 4rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.75rem',
    maxWidth: '1600px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box'
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '2.7rem',
    padding: '0.65rem 1rem',
    cursor: 'pointer',
    color: BRAND_TEXT,
    textDecoration: 'none',
    fontWeight: 700,
    backgroundColor: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.8rem',
    boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)'
  },
  saveMeta: {
    color: BRAND_MUTED,
    fontSize: '0.875rem'
  },
  card: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.75rem',
    boxShadow: '0 20px 40px -28px rgba(18, 63, 67, 0.22)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  section: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.75rem',
    boxShadow: '0 15px 35px -30px rgba(18, 63, 67, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem'
  },
  muted: {
    color: BRAND_MUTED,
    fontSize: '0.9rem'
  },
  grid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'
  },
  fieldLabel: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: BRAND_MUTED,
    fontWeight: 700
  },
  fieldValue: {
    color: BRAND_TEXT,
    fontWeight: 600
  },
  buttonPrimary: {
    background: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.65rem 1.4rem',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 10px 20px -16px rgba(18, 63, 67, 0.48)',
    minHeight: '2.7rem'
  },
  buttonSecondary: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.65rem 1.4rem',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(18, 63, 67, 0.08)',
    minHeight: '2.7rem'
  },
  buttonNavy: {
    background: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.75rem 1.5rem',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 16px 32px -20px rgba(18, 63, 67, 0.5)',
    minHeight: '2.9rem'
  },
  functionalTestButton: {
    background: BRAND_SURFACE_SOFT,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.75rem 1.15rem',
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '2.8rem'
  },
  functionalTestButtonActive: {
    background: BRAND_TEXT_SOFT,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    color: BRAND_TEXT,
    boxShadow: '0 12px 24px -20px rgba(18, 63, 67, 0.35)'
  },
  textInput: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.6rem 0.85rem',
    width: '100%',
    fontSize: '0.95rem',
    background: BRAND_CARD,
    color: BRAND_TEXT
  },
  textInputDisabled: {
    background: BRAND_SURFACE_SOFT,
    color: BRAND_MUTED,
    cursor: 'not-allowed'
  },
  statusHeader: {
    display: 'flex',
    gap: '1.75rem',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap'
  },
  statusLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    minWidth: '16rem',
    flex: '1 1 18rem'
  },
  statusChipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  statusMetaRow: {
    display: 'flex',
    gap: '1.25rem',
    flexWrap: 'wrap'
  },
  statusMetaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    minWidth: '10rem'
  },
  statusMetaSubnote: {
    color: BRAND_MUTED,
    fontSize: '0.8rem',
    lineHeight: 1.4
  },
  statusRight: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    alignItems: 'flex-end',
    flex: '0 0 auto'
  },
  primaryActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  statusLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '9999px',
    padding: '0.45rem 1.1rem',
    fontSize: '0.85rem',
    fontWeight: 700,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_TEXT_SOFT,
    color: BRAND_TEXT
  },
  statusActionComplete: {
    background: BRAND_SUCCESS_TEXT,
    border: `1px solid ${BRAND_SUCCESS_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.65rem 1.8rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    minHeight: '2.75rem'
  },
  statusActionHold: {
    background: BRAND_ACCENT_SOFT,
    border: `1px solid ${BRAND_ACCENT}`,
    color: BRAND_ACCENT_TEXT,
    borderRadius: '0.8rem',
    padding: '0.65rem 1.8rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    minHeight: '2.75rem'
  },
  statusActionReopen: {
    background: BRAND_TEXT_SOFT,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.65rem 1.8rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    minHeight: '2.75rem'
  },
  statusActionDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  reportButton: {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.75rem 2.4rem',
    fontWeight: 700,
    fontSize: '0.95rem',
    cursor: 'pointer',
    minWidth: '16rem',
    minHeight: '2.85rem',
    boxShadow: '0 10px 20px -16px rgba(18, 63, 67, 0.48)'
  },
  reportButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  reportError: {
    color: BRAND_DANGER_TEXT,
    fontSize: '0.85rem',
    textAlign: 'right'
  },
  reportInfo: {
    color: BRAND_MUTED,
    fontSize: '0.8rem',
    textAlign: 'right'
  },
  expertSection: {
    background: 'linear-gradient(180deg, #F7F3EA 0%, #F1F6F3 100%)',
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    borderRadius: '1rem',
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    boxShadow: '0 18px 34px -30px rgba(18, 63, 67, 0.26)'
  },
  expertHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  expertHeaderCopy: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    flex: '1 1 20rem'
  },
  expertTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: BRAND_TEXT
  },
  expertSub: {
    color: BRAND_MUTED,
    fontSize: '0.9rem'
  },
  expertGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '0.85rem'
  },
  expertField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem'
  },
  expertLabel: {
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: BRAND_MUTED
  },
  expertSelect: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.7rem 0.85rem',
    width: '100%',
    fontSize: '0.95rem',
    background: '#FCFBF7',
    color: BRAND_TEXT
  },
  expertInput: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.8rem 0.95rem',
    width: '100%',
    fontSize: '0.95rem',
    background: '#ffffff',
    color: BRAND_TEXT
  },
  expertHint: {
    color: BRAND_MUTED,
    fontSize: '0.85rem'
  },
  expertActions: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  expertMessages: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.65rem'
  },
  expertMessage: {
    borderRadius: '0.9rem',
    padding: '0.8rem 0.95rem',
    lineHeight: 1.55,
    fontSize: '0.93rem',
    whiteSpace: 'pre-wrap'
  },
  expertAssistant: {
    background: BRAND_SURFACE_SOFT,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT
  },
  expertUser: {
    background: BRAND_TEXT_SOFT,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    color: BRAND_TEXT
  },
  expertError: {
    borderRadius: '0.8rem',
    padding: '0.75rem 0.9rem',
    background: BRAND_DANGER_SOFT,
    border: `1px solid ${BRAND_DANGER_BORDER}`,
    color: BRAND_DANGER_TEXT,
    fontSize: '0.9rem'
  },
  statusNote: {
    marginTop: '0.4rem',
    color: BRAND_MUTED,
    fontSize: '0.85rem',
    fontStyle: 'italic'
  },
  dialogBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  dialog: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.75rem',
    width: 'min(32rem, 90vw)',
    boxShadow: '0 25px 50px -18px rgba(18, 63, 67, 0.24)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  dialogTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: BRAND_TEXT
  },
  dialogDescription: {
    color: BRAND_MUTED,
    fontSize: '0.95rem'
  },
  feedbackBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(17, 24, 39, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    zIndex: 1050
  },
  feedbackModal: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    width: 'min(640px, 90vw)',
    borderRadius: '1rem',
    boxShadow: '0 24px 60px -24px rgba(18, 63, 67, 0.28)',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh'
  },
  feedbackHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.25rem 1.5rem',
    borderBottom: `1px solid ${BRAND_BORDER}`,
    gap: '0.75rem'
  },
  feedbackTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: BRAND_TEXT
  },
  feedbackClose: {
    border: 'none',
    background: 'transparent',
    color: BRAND_MUTED,
    fontSize: '1.5rem',
    cursor: 'pointer',
    lineHeight: 1
  },
  feedbackBody: {
    padding: '1.25rem 1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    overflowY: 'auto'
  },
  feedbackLabel: {
    display: 'block',
    marginBottom: '0.35rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: BRAND_TEXT
  },
  feedbackSelect: {
    width: '100%',
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.6rem 0.75rem',
    fontSize: '0.95rem',
    background: BRAND_CARD,
    color: BRAND_TEXT
  },
  feedbackInput: {
    width: '100%',
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.6rem 0.75rem',
    fontSize: '0.95rem',
    background: BRAND_CARD,
    color: BRAND_TEXT
  },
  feedbackTextarea: {
    width: '100%',
    minHeight: '8rem',
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.75rem 0.85rem',
    fontSize: '0.95rem',
    lineHeight: 1.55,
    resize: 'vertical',
    background: BRAND_CARD,
    color: BRAND_TEXT
  },
  feedbackContext: {
    background: BRAND_SURFACE_SOFT,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.75rem',
    padding: '0.75rem 1rem',
    fontSize: '0.85rem',
    color: BRAND_MUTED
  },
  feedbackError: {
    color: BRAND_DANGER_TEXT,
    fontSize: '0.9rem'
  },
  feedbackFooter: {
    padding: '1rem 1.5rem',
    borderTop: `1px solid ${BRAND_BORDER}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem'
  },
  feedbackCancel: {
    border: `1px solid ${BRAND_BORDER}`,
    background: BRAND_CARD,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.6rem 1.5rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  feedbackSubmit: {
    border: `1px solid ${BRAND_TEXT}`,
    background: BRAND_TEXT,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.6rem 1.6rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  feedbackSubmitDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  dialogInput: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.75rem',
    minHeight: '6.5rem',
    resize: 'vertical',
    fontSize: '0.95rem',
    background: BRAND_CARD,
    color: BRAND_TEXT
  },
  dialogButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem'
  },
  dialogSecondary: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.55rem 1.4rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  dialogPrimary: {
    background: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.55rem 1.6rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  dialogError: {
    color: BRAND_DANGER_TEXT,
    fontSize: '0.9rem'
  },
  reportHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    alignItems: 'baseline',
    flexWrap: 'wrap'
  },
  reportTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: BRAND_TEXT
  },
  reportMeta: {
    color: BRAND_MUTED,
    fontSize: '0.85rem'
  },
  reportStatusWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.35rem',
    marginTop: '0.7rem'
  },
  reportStatusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '9999px',
    padding: '0.35rem 0.8rem',
    fontSize: '0.8rem',
    fontWeight: 700,
    border: '1px solid transparent'
  },
  reportStatusChipSent: {
    background: BRAND_SUCCESS_SOFT,
    color: BRAND_SUCCESS_TEXT,
    borderColor: BRAND_SUCCESS_BORDER
  },
  reportStatusChipPending: {
    background: BRAND_SURFACE_SOFT,
    color: BRAND_MUTED,
    borderColor: BRAND_BORDER
  },
  reportBody: {
    whiteSpace: 'pre-wrap',
    lineHeight: 1.55,
    color: BRAND_TEXT,
    fontSize: '0.95rem',
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.8rem',
    padding: '1.25rem',
    background: BRAND_CARD,
    boxShadow: 'inset 0 1px 1px rgba(18, 63, 67, 0.04)'
  },
  reportActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  },
  reportEditButton: {
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT,
    background: BRAND_CARD,
    borderRadius: '0.8rem',
    padding: '0.45rem 1.2rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  reportEditButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  reportTextarea: {
    width: '100%',
    minHeight: '14rem',
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '1rem',
    fontSize: '0.95rem',
    lineHeight: 1.55,
    resize: 'vertical',
    fontFamily: 'inherit',
    background: BRAND_CARD,
    color: BRAND_TEXT
  },
  reportEditFooter: {
    marginTop: '0.85rem',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem'
  },
  reportEditSecondary: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.55rem 1.4rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  reportEditPrimary: {
    background: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT}`,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.55rem 1.6rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  reportEditError: {
    color: BRAND_DANGER_TEXT,
    marginTop: '0.6rem',
    fontSize: '0.88rem'
  },
  repairExecutionToggle: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.55rem 1rem',
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: '2.6rem'
  },
  repairStepsCollapsedNotice: {
    borderRadius: '0.9rem',
    background: BRAND_TEXT_SOFT,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    color: BRAND_TEXT,
    padding: '0.9rem 1rem',
    fontWeight: 600
  },
  repairFooterActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
    marginTop: '0.5rem'
  },
  repairFooterLeftActions: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  functionalTestZone: {
    borderRadius: '1rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: `linear-gradient(180deg, ${BRAND_SURFACE_SOFT} 0%, ${BRAND_CARD} 55%)`,
    padding: '1.4rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    boxShadow: '0 20px 36px -28px rgba(18, 63, 67, 0.24)'
  },
  functionalTestZoneHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  functionalTestZoneTitle: {
    fontSize: '1.15rem',
    fontWeight: 700,
    color: BRAND_TEXT
  },
  functionalTestZoneMeta: {
    color: BRAND_MUTED,
    fontSize: '0.9rem',
    lineHeight: 1.5
  },
  functionalTestZoneActions: {
    display: 'flex',
    gap: '0.65rem',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  functionalTestStatusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '9999px',
    padding: '0.3rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 700
  },
  functionalTestStepCard: {
    borderRadius: '0.9rem',
    border: `1px solid ${BRAND_BORDER}`,
    background: BRAND_CARD,
    padding: '1rem 1.1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.85rem'
  },
  functionalTestChecklist: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.85rem'
  },
  functionalTestChecklistRow: {
    display: 'flex',
    gap: '0.8rem',
    alignItems: 'flex-start'
  },
  functionalTestChecklistLabel: {
    fontWeight: 600,
    color: BRAND_TEXT
  },
  functionalTestChecklistSpec: {
    color: BRAND_MUTED,
    lineHeight: 1.5,
    marginTop: '0.15rem'
  },
  functionalTestNotes: {
    minHeight: '7rem',
    borderRadius: '0.9rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.9rem 1rem',
    fontSize: '0.95rem',
    resize: 'vertical',
    fontFamily: 'inherit',
    background: BRAND_CARD,
    color: BRAND_TEXT
  },
  functionalTestEmpty: {
    borderRadius: '0.9rem',
    background: BRAND_SURFACE_SOFT,
    border: `1px dashed ${BRAND_TEXT_SOFT_BORDER}`,
    color: BRAND_MUTED,
    padding: '1rem'
  },
  textarea: {
    borderRadius: '0.8rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.85rem',
    minHeight: '6rem',
    width: '100%',
    background: BRAND_CARD,
    color: BRAND_TEXT
  }
};

const stepStyles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '1rem',
    padding: '1.25rem 1.5rem',
    background: BRAND_SURFACE_SOFT,
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem'
  },
  title: {
    fontWeight: 700,
    color: BRAND_TEXT,
    fontSize: '1rem'
  },
  summary: {
    color: BRAND_MUTED,
    fontSize: '0.9rem'
  },
  chip: {
    background: BRAND_TEXT_SOFT,
    color: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    padding: '0.35rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.7rem',
    fontWeight: 700
  },
  chipRight: {
    marginLeft: 'auto'
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  statusRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  statusPill: {
    borderRadius: '9999px',
    padding: '0.25rem 0.75rem',
    fontSize: '0.75rem',
    fontWeight: 700
  },
  statusHint: {
    color: BRAND_MUTED,
    fontSize: '0.8rem'
  },
  collapseToggle: {
    background: 'none',
    border: 'none',
    color: BRAND_TEXT,
    cursor: 'pointer',
    fontWeight: 600
  },
  checklistSection: {
    background: '#FFFEFB',
    borderRadius: '0.75rem',
    border: `1px solid ${BRAND_BORDER}`
  },
  checklistHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.85rem 1rem',
    background: BRAND_SURFACE,
    borderRadius: '0.75rem 0.75rem 0 0'
  },
  checklistHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.85rem'
  },
  checklistToggle: {
    background: 'none',
    border: 'none',
    color: BRAND_TEXT,
    cursor: 'pointer',
    fontWeight: 600
  },
  checklistBody: {
    background: '#ffffff',
    padding: '0.9rem 1.1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    borderRadius: '0 0 0.75rem 0.75rem'
  },
  checklistEditor: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  checklistEditorActions: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  checklistEditorError: {
    color: BRAND_DANGER_TEXT,
    fontSize: '0.88rem'
  },
  checklistHint: {
    fontSize: '0.78rem',
    color: BRAND_MUTED,
    background: BRAND_SURFACE_SOFT,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.5rem',
    padding: '0.5rem 0.65rem',
    marginBottom: '0.2rem'
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.55rem'
  },
  checklistLabel: {
    fontSize: '0.9rem',
    color: BRAND_TEXT
  },
  inlineImageTextLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    color: BRAND_TEXT,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    cursor: 'pointer',
    font: 'inherit',
    textAlign: 'left'
  },
  checklistContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem'
  },
  captureRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem'
  },
  captureInputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    maxWidth: '16rem'
  },
  captureLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: BRAND_TEXT
  },
  captureInput: {
    borderRadius: '0.6rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.45rem 0.6rem',
    fontSize: '0.9rem',
    color: BRAND_TEXT,
    background: BRAND_CARD
  },
  captureInputValid: {
    borderColor: BRAND_SUCCESS_BORDER,
    background: BRAND_SUCCESS_SOFT
  },
  captureInputWarning: {
    borderColor: BRAND_ACCENT,
    background: BRAND_ACCENT_SOFT
  },
  captureInputInvalid: {
    borderColor: BRAND_DANGER_BORDER,
    background: BRAND_DANGER_SOFT
  },
  captureHint: {
    fontSize: '0.75rem',
    color: BRAND_MUTED
  },
  captureFeedback: {
    fontSize: '0.75rem',
    fontWeight: 600
  },
  previewOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    zIndex: 1200
  },
  previewModal: {
    backgroundColor: BRAND_CARD,
    borderRadius: '8px',
    padding: '0.75rem',
    width: 'min(94vw, 1400px)',
    height: '90vh',
    maxHeight: '940px',
    border: `1px solid ${BRAND_BORDER}`,
    boxShadow: '0 25px 65px rgba(15, 23, 42, 0.35)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.65rem',
    overflow: 'hidden'
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
    padding: 0
  },
  previewTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    minWidth: 0
  },
  previewCounter: {
    color: BRAND_MUTED,
    fontSize: '0.85rem'
  },
  previewCloseButton: {
    border: `1px solid ${BRAND_BORDER}`,
    backgroundColor: BRAND_CARD,
    color: BRAND_TEXT,
    borderRadius: '0.5rem',
    padding: '0.4rem 0.75rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  previewControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.45rem',
    flexWrap: 'wrap',
    borderTop: `1px solid ${BRAND_BORDER}`,
    paddingTop: '0.55rem'
  },
  previewControlButton: {
    border: `1px solid ${BRAND_BORDER}`,
    backgroundColor: BRAND_CARD,
    borderRadius: '6px',
    padding: '0.32rem 0.58rem',
    fontWeight: 600,
    color: BRAND_TEXT,
    cursor: 'pointer',
    minWidth: '2.35rem'
  },
  previewZoomSlider: {
    width: 'min(34vw, 420px)',
    maxWidth: '100%'
  },
  previewZoomValue: {
    color: BRAND_MUTED,
    fontSize: '0.9rem',
    minWidth: '3rem'
  },
  previewGalleryStrip: {
    display: 'flex',
    gap: '0.45rem',
    overflowX: 'auto',
    paddingBottom: '0.15rem'
  },
  previewGalleryThumbButton: {
    width: '86px',
    height: '60px',
    flex: '0 0 auto',
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '6px',
    padding: 0,
    overflow: 'hidden',
    backgroundColor: BRAND_CARD,
    cursor: 'pointer'
  },
  previewGalleryThumbButtonActive: {
    borderColor: BRAND_TEXT,
    boxShadow: '0 0 0 2px rgba(18, 63, 67, 0.18)'
  },
  previewGalleryThumb: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block'
  },
  previewGalleryThumbPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: BRAND_SURFACE_SOFT,
    color: BRAND_MUTED,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontSize: '0.72rem'
  },
  previewImageStage: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: BRAND_SURFACE_SOFT,
    borderRadius: '6px',
    padding: '0.75rem'
  },
  previewImageCanvas: {
    minWidth: '100%',
    minHeight: '100%',
    display: 'block'
  },
  previewImage: {
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'contain',
    display: 'block',
    backgroundColor: BRAND_SURFACE_SOFT
  },
  previewPlaceholder: {
    width: '100%',
    height: '320px',
    color: BRAND_MUTED,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '1rem',
    backgroundColor: BRAND_SURFACE_SOFT,
    borderRadius: '0.65rem'
  },
  notesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.5rem'
  },
  attachmentToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  attachmentButton: {
    border: `1px dashed ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_CARD,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.35rem 1rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  attachmentHint: {
    fontSize: '0.8rem',
    color: BRAND_MUTED
  },
  attachmentList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem'
  },
  attachmentCard: {
    background: BRAND_CARD,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.85rem',
    padding: '0.45rem',
    width: '9rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    alignItems: 'stretch'
  },
  attachmentThumb: {
    width: '100%',
    height: '6rem',
    objectFit: 'cover',
    borderRadius: '0.6rem',
    background: BRAND_SURFACE
  },
  attachmentMeta: {
    textAlign: 'center',
    fontSize: '0.78rem',
    color: BRAND_TEXT
  },
  attachmentName: {
    fontWeight: 600,
    lineHeight: 1.2
  },
  attachmentSize: {
    color: BRAND_MUTED,
    fontSize: '0.75rem'
  },
  attachmentRemove: {
    border: `1px solid ${BRAND_DANGER_BORDER}`,
    background: BRAND_DANGER_SOFT,
    color: BRAND_DANGER_TEXT,
    borderRadius: '0.5rem',
    padding: '0.25rem',
    fontSize: '0.75rem',
    cursor: 'pointer'
  },
  attachmentEmpty: {
    fontSize: '0.85rem',
    color: BRAND_MUTED
  },
  attachmentError: {
    color: BRAND_DANGER_TEXT,
    fontSize: '0.85rem'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },
  headerActionsTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },
  headerActionsMeta: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%'
  },
  flagButton: {
    border: `1px solid ${BRAND_BORDER}`,
    background: BRAND_CARD,
    color: BRAND_TEXT,
    borderRadius: '0.8rem',
    padding: '0.4rem 1rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  flagButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  deactivatedBanner: {
    borderRadius: '0.75rem',
    border: `1px dashed ${BRAND_ACCENT}`,
    background: BRAND_ACCENT_SOFT,
    padding: '0.85rem 1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem'
  },
  deactivatedInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    color: BRAND_ACCENT_TEXT,
    fontSize: '0.9rem'
  },
  flaggedDetail: {
    fontSize: '0.8rem',
    color: BRAND_ACCENT_TEXT
  },
  reactivateButton: {
    border: `1px solid ${BRAND_TEXT}`,
    background: BRAND_TEXT,
    color: '#ffffff',
    borderRadius: '0.8rem',
    padding: '0.45rem 1.1rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  reactivateButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  partsSection: {
    background: BRAND_SURFACE_SOFT,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.85rem',
    padding: '1rem 1.1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.85rem'
  },
  partsSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  partSuggestionList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem'
  },
  partSuggestionButton: {
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_TEXT_SOFT,
    color: BRAND_TEXT,
    borderRadius: '9999px',
    padding: '0.35rem 0.75rem',
    fontWeight: 600,
    fontSize: '0.82rem',
    cursor: 'pointer'
  },
  partSuggestionMeta: {
    fontSize: '0.8rem',
    color: BRAND_MUTED
  },
  partsTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.65rem'
  },
  partsRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1fr) minmax(100px, 0.6fr) auto',
    gap: '0.55rem',
    alignItems: 'center'
  },
  partsHeaderRow: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: BRAND_MUTED,
    fontWeight: 700
  },
  partCellInput: {
    width: '100%',
    borderRadius: '0.65rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.55rem 0.65rem',
    fontSize: '0.9rem',
    background: '#ffffff',
    color: BRAND_TEXT
  },
  partCellValue: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '2.5rem',
    borderRadius: '0.65rem',
    border: `1px solid ${BRAND_BORDER}`,
    padding: '0.55rem 0.65rem',
    background: BRAND_CARD,
    fontSize: '0.9rem',
    color: BRAND_TEXT
  },
  partActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  partRemoveButton: {
    background: BRAND_DANGER_SOFT,
    border: `1px solid ${BRAND_DANGER_BORDER}`,
    color: BRAND_DANGER_TEXT,
    borderRadius: '9999px',
    padding: '0.35rem 0.85rem',
    cursor: 'pointer',
    fontWeight: 600
  },
  partAddButton: {
    background: BRAND_TEXT,
    color: '#ffffff',
    border: `1px solid ${BRAND_TEXT}`,
    borderRadius: '0.8rem',
    padding: '0.65rem 1rem',
    fontWeight: 600,
    cursor: 'pointer',
    alignSelf: 'flex-start'
  },
  partScanButton: {
    background: BRAND_CARD,
    color: BRAND_TEXT,
    border: `1px solid ${BRAND_BORDER}`,
    borderRadius: '0.8rem',
    padding: '0.55rem 0.95rem',
    fontWeight: 600,
    cursor: 'pointer'
  },
  partDraftLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: BRAND_TEXT
  },
  partDraftHint: {
    fontSize: '0.8rem',
    color: BRAND_MUTED
  },
  scanMessage: {
    marginTop: '0.35rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    padding: '0.5rem 0.75rem',
    borderRadius: '0.65rem',
    background: BRAND_SURFACE_SOFT,
    color: BRAND_TEXT,
    border: `1px solid ${BRAND_BORDER}`
  },
  scanMessageSuccess: {
    background: BRAND_SUCCESS_SOFT,
    color: BRAND_SUCCESS_TEXT,
    borderColor: BRAND_SUCCESS_BORDER
  },
  scanMessageError: {
    background: BRAND_DANGER_SOFT,
    color: BRAND_DANGER_TEXT,
    borderColor: BRAND_DANGER_BORDER
  }
};

const STEP_CARD_VARIANTS: Record<StepResolutionState, React.CSSProperties> = {
  pending: {
    border: `1px solid ${BRAND_BORDER}`,
    background: BRAND_SURFACE_SOFT
  },
  verifying: {
    border: `1px solid ${BRAND_ACCENT}`,
    background: '#FFF7E9'
  },
  needsRepair: {
    border: `1px solid ${BRAND_DANGER_BORDER}`,
    background: BRAND_DANGER_SOFT
  },
  resolved: {
    border: `1px solid ${BRAND_SUCCESS_BORDER}`,
    background: BRAND_SUCCESS_SOFT
  },
  inactive: {
    border: `1px dashed ${BRAND_TEXT_SOFT_BORDER}`,
    background: BRAND_SURFACE
  }
};

const STEP_PILL_VARIANTS: Record<StepResolutionState, React.CSSProperties> = {
  pending: {
    background: BRAND_TEXT_SOFT,
    color: BRAND_TEXT
  },
  verifying: {
    background: BRAND_ACCENT_SOFT,
    color: BRAND_ACCENT_TEXT
  },
  needsRepair: {
    background: BRAND_DANGER_SOFT,
    color: BRAND_DANGER_TEXT
  },
  resolved: {
    background: BRAND_SUCCESS_SOFT,
    color: BRAND_SUCCESS_TEXT
  },
  inactive: {
    background: BRAND_SURFACE,
    color: BRAND_MUTED
  }
};

const STATUS_LABELS: Record<string, string> = {
  IN_PROGRESS: 'In progress',
  ON_HOLD: 'On hold',
  COMPLETED: 'Completed'
};

const STATUS_CHIP_VARIANTS: Record<string, React.CSSProperties> = {
  IN_PROGRESS: {
    background: BRAND_TEXT_SOFT,
    color: BRAND_TEXT,
    border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`
  },
  ON_HOLD: {
    background: BRAND_ACCENT_SOFT,
    color: BRAND_ACCENT_TEXT,
    border: `1px solid ${BRAND_ACCENT}`
  },
  COMPLETED: {
    background: BRAND_SUCCESS_SOFT,
    color: BRAND_SUCCESS_TEXT,
    border: `1px solid ${BRAND_SUCCESS_BORDER}`
  }
};
const normalizeWhitespace = (value: string): string =>
  typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';

const toSlug = (value: string): string =>
  normalizeWhitespace(value).toLowerCase();

const buildPlainChecklistItem = (
  type: ChecklistType,
  value: string,
  index: number
): ChecklistItem => {
  const trimmed = normalizeWhitespace(value);
  const slug = toSlug(value);
  const canonicalId = `plain:${type}:${index}`;
  const label = trimmed || `Step ${index + 1}`;
  const persistedValue = trimmed || (value ?? '') || label;
  const legacyIds = new Set<string>();
  if (trimmed) {
    legacyIds.add(trimmed);
    legacyIds.add(toSlug(trimmed));
  }
  if (slug) {
    legacyIds.add(`list:${type}:${slug}`);
  }
  legacyIds.add(`list:${type}:index:${index}`);
  if (value && value !== trimmed) {
    legacyIds.add(value);
  }
  return {
    id: canonicalId,
    label,
    depth: 0,
    legacyIds: Array.from(legacyIds).filter(Boolean),
    value: persistedValue
  };
};

const buildStructuredChecklistItem = (
  type: ChecklistType,
  path: number[],
  node: StructuredStepNode,
  depth: number
): ChecklistItem | null => {
  const number = normalizeWhitespace(node.number ?? '');
  const description = normalizeWhitespace(node.description ?? '');
  if (!number && !description) {
    return null;
  }
  const label =
    number && description ? `${number} ${description}` : number || description;
  const id = `structured:${type}:${path.join('.')}`;
  const legacyIds = [label];
  if (number) {
    legacyIds.push(number);
  }
  const tags = Array.isArray(node.tags) ? node.tags.filter(Boolean) : [];
  const metadata = node.metadata ?? null;
  const capture = node.capture ?? null;
  return {
    id,
    label,
    number: number || undefined,
    description: description || undefined,
    depth,
    legacyIds: legacyIds.filter(Boolean),
    value: label,
    tags: tags.length > 0 ? tags : undefined,
    metadata: metadata && Object.keys(metadata).length > 0 ? metadata : null,
    capture: capture || undefined
  };
};

const findChecklistItem = (items: ChecklistItem[], key: string): ChecklistItem | undefined => {
  const trimmed = normalizeWhitespace(key);
  const slug = toSlug(key);
  return items.find(item => {
    if (item.id === key || item.id === trimmed) return true;
    if (item.value === key || item.value === trimmed) return true;
    if (toSlug(item.value) === slug) return true;
    return item.legacyIds.some(alias => toSlug(alias) === slug);
  });
};

const normalizeChecklistValues = (
  values: string[] | undefined,
  items: ChecklistItem[]
): string[] => {
  if (!Array.isArray(values)) return [];
  const matchedIds: string[] = [];
  values.forEach(raw => {
    const trimmed = normalizeWhitespace(raw);
    if (!trimmed) return;
    const match = findChecklistItem(items, trimmed);
    const canonicalId = match ? match.id : null;
    if (canonicalId && !matchedIds.includes(canonicalId)) {
      matchedIds.push(canonicalId);
    }
  });
  return matchedIds;
};

const CHECKLIST_NUMBER_PATTERN = /^\d+(?:\.\d+)*$/;

const flattenStructuredNodesForEditing = (
  nodes: StructuredStepNode[] | undefined
): StructuredStepNode[] => {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];
  const flattened: StructuredStepNode[] = [];
  const visit = (list: StructuredStepNode[]) => {
    list.forEach(node => {
      if (!node || typeof node !== 'object') return;
      const description = normalizeWhitespace(node.description ?? '');
      const number = normalizeWhitespace(node.number ?? '');
      if (!description && !number) return;
      flattened.push({
        ...(number ? { number } : {}),
        ...(description ? { description } : {}),
        ...(Array.isArray(node.tags) && node.tags.length > 0 ? { tags: [...node.tags] } : {}),
        ...(node.metadata && typeof node.metadata === 'object'
          ? { metadata: { ...(node.metadata as Record<string, unknown>) } }
          : {}),
        ...(node.capture && typeof node.capture === 'object'
          ? { capture: { ...node.capture } }
          : {})
      });
      if (Array.isArray(node.subSteps) && node.subSteps.length > 0) {
        visit(node.subSteps);
      }
    });
  };
  visit(nodes);
  return flattened;
};

const formatChecklistDraft = (metadata: StepMetadata, type: ChecklistType): string => {
  const structuredNodes =
    type === 'verification'
      ? flattenStructuredNodesForEditing(metadata.structuredTestingSteps)
      : flattenStructuredNodesForEditing(metadata.structuredRepairSteps);
  if (structuredNodes.length > 0) {
    return structuredNodes
      .map(node => {
        const number = normalizeWhitespace(node.number ?? '');
        const description = normalizeWhitespace(node.description ?? '');
        return [number, description].filter(Boolean).join(' ');
      })
      .filter(Boolean)
      .join('\n');
  }
  const fallback =
    type === 'verification'
      ? Array.isArray(metadata.testingSteps)
        ? metadata.testingSteps
        : []
      : Array.isArray(metadata.repairSteps)
      ? metadata.repairSteps
      : [];
  return fallback.map(item => normalizeWhitespace(item)).filter(Boolean).join('\n');
};

const parseChecklistDraft = (
  draft: string,
  existingNodes: StructuredStepNode[] | undefined
): { structured: StructuredStepNode[]; flattened: string[] } => {
  const lines = draft
    .split(/\r?\n+/)
    .map(line => line.trim())
    .filter(Boolean);
  if (lines.length === 0) {
    return { structured: [], flattened: [] };
  }

  const existingFlat = flattenStructuredNodesForEditing(existingNodes);
  const existingByNumber = new Map<string, StructuredStepNode>();
  const existingByDescription = new Map<string, StructuredStepNode>();
  existingFlat.forEach(node => {
    const number = normalizeWhitespace(node.number ?? '');
    const descriptionKey = toSlug(node.description ?? '');
    if (number && !existingByNumber.has(number)) {
      existingByNumber.set(number, node);
    }
    if (descriptionKey && !existingByDescription.has(descriptionKey)) {
      existingByDescription.set(descriptionKey, node);
    }
  });

  const parsedNodes = lines.map((line, index) => {
    const match = line.match(/^(\d+(?:\.\d+)*)(?:[.)-])?\s+(.+)$/);
    const number = match ? match[1].trim() : `${index + 1}`;
    const description = normalizeWhitespace(match ? match[2] : line);
    const existingMatch =
      existingByNumber.get(number) || existingByDescription.get(toSlug(description)) || undefined;
    const matchedTags = existingMatch && Array.isArray(existingMatch.tags) ? existingMatch.tags : [];
    const matchedMetadata =
      existingMatch && existingMatch.metadata && typeof existingMatch.metadata === 'object'
        ? existingMatch.metadata
        : null;
    const matchedCapture =
      existingMatch && existingMatch.capture && typeof existingMatch.capture === 'object'
        ? existingMatch.capture
        : null;

    return {
      number,
      node: {
      ...(number ? { number } : {}),
      description,
      ...(matchedTags.length > 0 ? { tags: [...matchedTags] } : {}),
      ...(matchedMetadata ? { metadata: { ...(matchedMetadata as Record<string, unknown>) } } : {}),
      ...(matchedCapture ? { capture: { ...matchedCapture } } : {})
      } satisfies StructuredStepNode
    };
  });

  const nodesByNumber = new Map<string, StructuredStepNode>();
  parsedNodes.forEach(entry => {
    if (entry.number && !nodesByNumber.has(entry.number)) {
      nodesByNumber.set(entry.number, entry.node);
    }
  });

  const structured: StructuredStepNode[] = [];
  parsedNodes.forEach(entry => {
    const parentNumber = entry.number.includes('.')
      ? entry.number.split('.').slice(0, -1).join('.')
      : '';
    const parentNode = parentNumber ? nodesByNumber.get(parentNumber) : undefined;
    if (parentNode) {
      parentNode.subSteps = [...(parentNode.subSteps ?? []), entry.node];
      return;
    }
    structured.push(entry.node);
  });

  const flattened: string[] = [];
  const visit = (nodes: StructuredStepNode[]) => {
    nodes.forEach(node => {
      const description = normalizeWhitespace(node.description ?? '');
      if (description) {
        flattened.push(description);
      }
      if (Array.isArray(node.subSteps) && node.subSteps.length > 0) {
        visit(node.subSteps);
      }
    });
  };
  visit(structured);

  return {
    structured,
    flattened
  };
};

const getChecklistItemNumber = (item: ChecklistItem): string | null =>
  item.legacyIds.find(alias => CHECKLIST_NUMBER_PATTERN.test(normalizeWhitespace(alias))) || null;

const getCurrentCompletedIds = (
  metadata: StepMetadata,
  type: ChecklistType,
  items: ChecklistItem[]
): string[] => {
  const currentRaw =
    type === 'verification'
      ? Array.isArray(metadata.verificationCompleted)
        ? metadata.verificationCompleted
        : []
      : Array.isArray(metadata.repairCompleted)
      ? metadata.repairCompleted
      : [];

  if (type === 'verification') {
    const fromMetadata = Array.isArray(metadata.normalizedVerificationCompleted)
      ? metadata.normalizedVerificationCompleted.filter(id => items.some(item => item.id === id))
      : [];
    return fromMetadata.length > 0 ? fromMetadata : normalizeChecklistValues(currentRaw, items);
  }

  const fromMetadata = Array.isArray(metadata.normalizedRepairCompleted)
    ? metadata.normalizedRepairCompleted.filter(id => items.some(item => item.id === id))
    : [];
  return fromMetadata.length > 0 ? fromMetadata : normalizeChecklistValues(currentRaw, items);
};

const preserveChecklistCompletionAfterEdit = (
  metadata: StepMetadata,
  type: ChecklistType,
  previousItems: ChecklistItem[],
  nextItems: ChecklistItem[]
): { raw: string[]; normalized: string[] } => {
  if (nextItems.length === 0) {
    return { raw: [], normalized: [] };
  }

  const currentRaw =
    type === 'verification'
      ? Array.isArray(metadata.verificationCompleted)
        ? metadata.verificationCompleted
        : []
      : Array.isArray(metadata.repairCompleted)
      ? metadata.repairCompleted
      : [];
  const currentNormalized = getCurrentCompletedIds(metadata, type, previousItems);
  const preservedIds: string[] = [];

  currentNormalized.forEach(id => {
    const previousItem = previousItems.find(item => item.id === id);
    if (!previousItem) return;

    const previousNumber = getChecklistItemNumber(previousItem);
    const matchedByNumber =
      previousNumber !== null
        ? nextItems.find(item => getChecklistItemNumber(item) === previousNumber)
        : undefined;
    const matched =
      matchedByNumber || findChecklistItem(nextItems, previousItem.value) || undefined;
    if (matched && !preservedIds.includes(matched.id)) {
      preservedIds.push(matched.id);
    }
  });

  normalizeChecklistValues(currentRaw, nextItems).forEach(id => {
    if (!preservedIds.includes(id)) {
      preservedIds.push(id);
    }
  });

  const raw = preservedIds
    .map(id => nextItems.find(item => item.id === id)?.value ?? null)
    .filter((value): value is string => Boolean(value));

  return { raw, normalized: preservedIds };
};

const STEP_STATE_LABELS: Record<StepResolutionState, string> = {
  pending: 'Not started',
  verifying: 'Verifying',
  needsRepair: 'Repair required',
  resolved: 'Resolved',
  inactive: 'Flagged'
};

const buildStatusHint = (details: StepStatusDetails): string => {
  const {
    resolution,
    verificationChecked,
    verificationTotal,
    repairChecked,
    repairTotal
  } = details;

  if (resolution === 'resolved') {
    return 'Cause verified and cleared.';
  }
  if (resolution === 'inactive') {
    return 'Flagged for documentation follow-up.';
  }
  if (resolution === 'needsRepair') {
    if (repairTotal > 0) {
      return `${repairChecked}/${repairTotal} repair steps complete.`;
    }
    return 'Document repair actions to resolve.';
  }
  if (resolution === 'verifying') {
    if (verificationTotal > 0) {
      return `${verificationChecked}/${verificationTotal} verification steps complete.`;
    }
    return 'Capture verification outcomes.';
  }
  // pending
  if (verificationTotal > 0) {
    return 'Begin verification steps.';
  }
  if (repairTotal > 0) {
    return 'Log any repair work performed.';
  }
  return 'No guidance available for this cause.';
};

const formatDateTime = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const parseChecklistLabel = (label: string): { number: string; description: string } => {
  const trimmed = normalizeWhitespace(label);
  const match = trimmed.match(/^(\d+(?:\.\d+)*)\s+(.*)$/);
  if (match) {
    return {
      number: match[1].trim(),
      description: normalizeWhitespace(match[2])
    };
  }
  return { number: '', description: trimmed };
};

const buildDeviceExpertContextOptions = (
  items: ChecklistItem[],
  stepId: string,
  section: 'VERIFICATION' | 'REPAIR'
): DeviceExpertContextOption[] =>
  items.map(item => {
    const parsed = parseChecklistLabel(item.label);
    return {
      key: `${stepId}:${section}:${item.id}`,
      stepId,
      section,
      number: parsed.number,
      description: parsed.description || item.label,
      label: item.label
    };
  });

const formatDeviceExpertText = (value: string): string => {
  const text = String(value || '').replace(/\r\n/g, '\n').trim();
  if (!text) return '';
  const withNumberBreaks = text
    .replace(/\s+(\d+\))/g, '\n$1')
    .replace(/\s+-\s+/g, '\n- ');
  return withNumberBreaks.replace(/\n{3,}/g, '\n\n').trim();
};

const flattenStructuredSteps = (
  nodes: StructuredStepNode[] | undefined,
  type: ChecklistType,
  depth = 0,
  path: number[] = []
): ChecklistItem[] => {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];
  const items: ChecklistItem[] = [];
  nodes.forEach((node, index) => {
    if (!node) return;
    const currentPath = [...path, index];
    const item = buildStructuredChecklistItem(type, currentPath, node, depth);
    if (item) {
      items.push(item);
    }
    if (node.subSteps && node.subSteps.length > 0) {
      items.push(...flattenStructuredSteps(node.subSteps, type, depth + 1, currentPath));
    }
  });
  return items;
};

const buildChecklist = (metadata: StepMetadata, type: ChecklistType): ChecklistItem[] => {
  const fromList = (items: string[] = []) =>
    items.map((value, index) => buildPlainChecklistItem(type, value, index));

  if (type === 'verification') {
    const structured = flattenStructuredSteps(metadata.structuredTestingSteps, type);
    if (structured.length > 0) return structured;
    return fromList(metadata.testingSteps);
  }
  const structured = flattenStructuredSteps(metadata.structuredRepairSteps, type);
  if (structured.length > 0) return structured;
  return fromList(metadata.repairSteps);
};

const computeStepStatusDetails = (
  step: RepairStep,
  override?: StepMetadata
): StepStatusDetails => {
  const metadata = { ...step.metadata, ...override };
  const isDeactivated = metadata.deactivated === true;
  const verificationItems = buildChecklist(metadata, 'verification');
  const repairItems = buildChecklist(metadata, 'repair');

  const verificationRaw = Array.isArray(metadata.verificationCompleted)
    ? metadata.verificationCompleted
    : [];
  const repairRaw = Array.isArray(metadata.repairCompleted) ? metadata.repairCompleted : [];

  const verificationNormalizedFromMetadata = Array.isArray(
    metadata.normalizedVerificationCompleted
  )
    ? metadata.normalizedVerificationCompleted.filter(id =>
        verificationItems.some(item => item.id === id)
      )
    : [];
  const repairNormalizedFromMetadata = Array.isArray(metadata.normalizedRepairCompleted)
    ? metadata.normalizedRepairCompleted.filter(id => repairItems.some(item => item.id === id))
    : [];

  const verificationCompletedIds =
    verificationNormalizedFromMetadata.length > 0
      ? verificationNormalizedFromMetadata
      : normalizeChecklistValues(verificationRaw, verificationItems);
  const repairCompletedIds =
    repairNormalizedFromMetadata.length > 0
      ? repairNormalizedFromMetadata
      : normalizeChecklistValues(repairRaw, repairItems);

  const verificationSet = new Set(verificationCompletedIds);
  const repairSet = new Set(repairCompletedIds);

  const verificationTotal = verificationItems.length;
  const verificationChecked = verificationItems.filter(item => verificationSet.has(item.id)).length;
  const repairChecked = repairItems.filter(item => repairSet.has(item.id)).length;
  const repairTotal = repairItems.length;

  const allVerificationChecked = verificationTotal > 0 && verificationChecked === verificationTotal;
  const allRepairChecked = repairTotal > 0 && repairChecked === repairTotal;
  const anyVerificationChecked = verificationChecked > 0;
  const anyRepairChecked = repairChecked > 0;

  let resolution: StepResolutionState = 'pending';
  let status: StepStatus = 'PENDING';

  if (isDeactivated) {
    return {
      verificationItems,
      repairItems,
      verificationCompletedIds,
      repairCompletedIds,
      verificationChecked,
      verificationTotal,
      repairChecked,
      repairTotal,
      resolution: 'inactive',
      status: 'PENDING'
    };
  }

  if (
    (verificationTotal > 0 && allVerificationChecked && !anyRepairChecked) ||
    (repairTotal > 0 && allRepairChecked && anyRepairChecked)
  ) {
    resolution = 'resolved';
    status = 'COMPLETE';
  } else if (anyRepairChecked) {
    resolution = 'needsRepair';
    status = 'IN_PROGRESS';
  } else if (anyVerificationChecked) {
    resolution = 'verifying';
    status = 'IN_PROGRESS';
  } else {
    resolution = 'pending';
    status = 'PENDING';
  }

  return {
    verificationItems,
    repairItems,
    verificationCompletedIds,
    repairCompletedIds,
    verificationChecked,
    verificationTotal,
    repairChecked,
    repairTotal,
    resolution,
    status
  };
};

const normalizeRepairStepForState = (step: RepairStep): RepairStep => {
  const normalizedCaptureValues =
    step.metadata &&
    step.metadata.captureValues &&
    typeof step.metadata.captureValues === 'object'
      ? Object.entries(step.metadata.captureValues).reduce(
          (acc, [key, value]) => {
            if (typeof key !== 'string' || !key) return acc;
            const text =
              typeof value === 'string'
                ? value.trim()
                : value === null || value === undefined
                ? ''
                : String(value).trim();
            if (text) {
              acc[key] = text;
            }
            return acc;
          },
          {} as Record<string, string>
        )
      : {};

  const normalizedMetadata: StepMetadata = {
    ...step.metadata,
    verificationCompleted: step.metadata.verificationCompleted ?? [],
    repairCompleted: step.metadata.repairCompleted ?? [],
    partsUsed: normalizePartsUsed(step.metadata.partsUsed),
    attachments: normalizeAttachments(step.metadata.attachments),
    normalizedVerificationCompleted: Array.isArray(step.metadata.normalizedVerificationCompleted)
      ? step.metadata.normalizedVerificationCompleted
      : step.metadata.verificationCompleted ?? [],
    normalizedRepairCompleted: Array.isArray(step.metadata.normalizedRepairCompleted)
      ? step.metadata.normalizedRepairCompleted
      : step.metadata.repairCompleted ?? [],
    captureValues: normalizedCaptureValues
  };

  const stepWithMetadata: RepairStep = {
    ...step,
    metadata: normalizedMetadata,
    notes: step.notes || ''
  };

  const evaluation = computeStepStatusDetails(stepWithMetadata);
  return {
    ...stepWithMetadata,
    metadata: {
      ...stepWithMetadata.metadata,
      verificationCompleted: stepWithMetadata.metadata.verificationCompleted ?? [],
      repairCompleted: stepWithMetadata.metadata.repairCompleted ?? [],
      normalizedVerificationCompleted: evaluation.verificationCompletedIds,
      normalizedRepairCompleted: evaluation.repairCompletedIds,
      partsUsed: stepWithMetadata.metadata.partsUsed ?? [],
      captureValues: stepWithMetadata.metadata.captureValues ?? {},
      attachments: stepWithMetadata.metadata.attachments ?? []
    },
    status: evaluation.status
  };
};

const StepCard: React.FC<StepCardProps> = ({
  step,
  analysisRequiredParts,
  partCatalog,
  onToggleChecklist,
  onSaveChecklistDraft,
  onSaveStep,
  onNotesChange,
  onAddPart,
  onRemovePart,
  onAddAttachment,
  onRemoveAttachment,
  onCaptureChange,
  onOpenFeedback,
  onFlagReferenceImage,
  onReactivate,
  canEditChecklist,
  canSaveStep,
  stepDirty,
  stepSaving
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showVerification, setShowVerification] = useState(true);
  const [showRepair, setShowRepair] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState<ChecklistType | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const captureValues = useMemo(
    () => step.metadata.captureValues ?? {},
    [step.metadata.captureValues]
  );
  const isDeactivated = step.metadata.deactivated === true;
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const verificationDraftText = useMemo(
    () => formatChecklistDraft(step.metadata, 'verification'),
    [step.metadata]
  );
  const repairDraftText = useMemo(() => formatChecklistDraft(step.metadata, 'repair'), [step.metadata]);
  const [verificationDraft, setVerificationDraft] = useState(verificationDraftText);
  const [repairDraft, setRepairDraft] = useState(repairDraftText);
  const [verificationEditError, setVerificationEditError] = useState<string | null>(null);
  const [repairEditError, setRepairEditError] = useState<string | null>(null);
  const [previewImages, setPreviewImages] = useState<ReferenceImageHit[]>([]);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [previewZoom, setPreviewZoom] = useState(100);

  const openPreviewImages = useCallback((images: ReferenceImageHit[], imageId?: string) => {
    const gallery = dedupeReferenceImages(images.filter(image => image && image.id));
    if (gallery.length === 0) return;
    const requestedIndex = imageId ? gallery.findIndex(image => image.id === imageId) : 0;
    setPreviewZoom(100);
    setPreviewImageIndex(requestedIndex >= 0 ? requestedIndex : 0);
    setPreviewImages(gallery);
  }, []);

  const closePreviewImages = useCallback(() => {
    setPreviewImages([]);
    setPreviewImageIndex(0);
    setPreviewZoom(100);
  }, []);
  const showPreviousPreviewImage = useCallback(() => {
    setPreviewZoom(100);
    setPreviewImageIndex(index => Math.max(0, index - 1));
  }, []);
  const showNextPreviewImage = useCallback(() => {
    setPreviewZoom(100);
    setPreviewImageIndex(index => Math.min(previewImages.length - 1, index + 1));
  }, [previewImages.length]);
  const selectPreviewImage = useCallback((index: number) => {
    setPreviewZoom(100);
    setPreviewImageIndex(index);
  }, []);
  const previewImage = previewImages[previewImageIndex] || null;
  const previewImageCount = previewImages.length;

  const renderMeasurementCapture = useCallback(
    (itemId: string, capture: StepCapture) => {
      if (!MEASUREMENT_CAPTURE_ENABLED) {
        return null;
      }
      const mode = getCaptureUiMode(capture);
      if (mode === 'hidden') {
        return null;
      }

      const captureValue = captureValues[itemId] ?? '';
      const evaluation = evaluateMeasurement(capture, captureValue);
      const placeholder = formatMeasurementPlaceholder(capture);
      const hint = formatMeasurementHint(capture);

      let inputStyle: React.CSSProperties = { ...stepStyles.captureInput };
      if (evaluation.status === 'valid') {
        inputStyle = { ...inputStyle, ...stepStyles.captureInputValid };
      } else if (evaluation.status === 'warning') {
        inputStyle = { ...inputStyle, ...stepStyles.captureInputWarning };
      } else if (evaluation.status === 'invalid') {
        inputStyle = { ...inputStyle, ...stepStyles.captureInputInvalid };
      }
      if (isDeactivated) {
        inputStyle = { ...inputStyle, background: BRAND_SURFACE, cursor: 'not-allowed' };
      }

      const feedback =
        evaluation.status === 'invalid'
          ? { text: evaluation.message, color: BRAND_DANGER_TEXT }
          : evaluation.status === 'warning'
          ? { text: evaluation.message, color: BRAND_ACCENT_TEXT }
          : null;

      return (
        <div style={stepStyles.captureRow}>
          <div style={stepStyles.captureInputGroup}>
            <label style={stepStyles.captureLabel}>Measured value</label>
            <input
              style={inputStyle}
              type="text"
              value={captureValue}
              placeholder={placeholder}
              onChange={event => onCaptureChange(step.id, itemId, event.target.value)}
              disabled={isDeactivated}
            />
          </div>
          {hint && <div style={stepStyles.captureHint}>{hint}</div>}
          {feedback && feedback.text && (
            <div style={{ ...stepStyles.captureFeedback, color: feedback.color }}>{feedback.text}</div>
          )}
        </div>
      );
    },
    [captureValues, isDeactivated, onCaptureChange, step.id]
  );


  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('repairTask:step state', step.id, {
        verificationCompleted: step.metadata.verificationCompleted,
        normalizedVerificationCompleted: step.metadata.normalizedVerificationCompleted,
        repairCompleted: step.metadata.repairCompleted,
        normalizedRepairCompleted: step.metadata.normalizedRepairCompleted,
        status: step.status
      });
    }
  }, [step]);

  useEffect(() => {
    if (editingChecklist !== 'verification') {
      setVerificationDraft(verificationDraftText);
      setVerificationEditError(null);
    }
  }, [editingChecklist, verificationDraftText]);

  useEffect(() => {
    if (editingChecklist !== 'repair') {
      setRepairDraft(repairDraftText);
      setRepairEditError(null);
    }
  }, [editingChecklist, repairDraftText]);

  useEffect(() => {
    if (!canEditChecklist || isDeactivated) {
      setEditingChecklist(null);
    }
  }, [canEditChecklist, isDeactivated]);

  const statusDetails = useMemo(() => computeStepStatusDetails(step), [step]);
  const { verificationItems, repairItems, resolution } = statusDetails;
  const isManualEntry =
    step.metadata.custom === true && verificationItems.length === 0 && repairItems.length === 0;
  const hasVerificationSection =
    verificationItems.length > 0 ||
    Array.isArray(step.metadata.structuredTestingSteps) ||
    Array.isArray(step.metadata.testingSteps);
  const hasRepairSection =
    repairItems.length > 0 ||
    Array.isArray(step.metadata.structuredRepairSteps) ||
    Array.isArray(step.metadata.repairSteps);
  const statusHint = buildStatusHint(statusDetails);
  const verificationCheckedSet = useMemo(
    () => new Set(statusDetails.verificationCompletedIds),
    [statusDetails.verificationCompletedIds]
  );
  const repairCheckedSet = useMemo(
    () => new Set(statusDetails.repairCompletedIds),
    [statusDetails.repairCompletedIds]
  );
  const attachments = Array.isArray(step.metadata.attachments) ? step.metadata.attachments : [];
  const stepReferenceImages = useMemo(
    () =>
      dedupeReferenceImages(
        Array.isArray(step.metadata.referenceImages) ? step.metadata.referenceImages : []
      ),
    [step.metadata.referenceImages]
  );
  const stepReferenceBlocks = useMemo(
    () =>
      Array.isArray(step.metadata.referenceImageBlocks) ? step.metadata.referenceImageBlocks : [],
    [step.metadata.referenceImageBlocks]
  );
  const partsUsed = useMemo(
    () => normalizePartsUsed(step.metadata.partsUsed),
    [step.metadata.partsUsed]
  );
  const partSelectionOptions = useMemo(
    () => buildPartSelectionOptions(step.metadata.requiredParts, analysisRequiredParts, partCatalog),
    [analysisRequiredParts, partCatalog, step.metadata.requiredParts]
  );
  const partSelectionLookup = useMemo(() => {
    const lookup = new Map<string, PartSelectionOption>();
    partSelectionOptions.forEach(option => {
      const partKey = normalizePartLookupToken(option.partNumber);
      if (partKey && !lookup.has(partKey)) {
        lookup.set(partKey, option);
      }
    });
    return lookup;
  }, [partSelectionOptions]);
  const suggestedPartOptions = useMemo(
    () => partSelectionOptions.filter(option => option.source === 'STEP_REQUIRED').slice(0, 6),
    [partSelectionOptions]
  );
  const partDatalistId = useMemo(() => `repair-part-suggestions-${step.id}`, [step.id]);
  const deactivationInfo =
    step.metadata.deactivationInfo && typeof step.metadata.deactivationInfo === 'object'
      ? (step.metadata.deactivationInfo as DeactivationInfo)
      : null;
  const feedbackItemOptions = useMemo(
    () => [
      ...verificationItems.map(item => ({
        id: item.id,
        label: item.label,
        type: 'VERIFICATION' as const
      })),
      ...repairItems.map(item => ({
        id: item.id,
        label: item.label,
        type: 'REPAIR' as const
      }))
    ],
    [verificationItems, repairItems]
  );
  const handleFlagIssue = useCallback(() => {
    onOpenFeedback({
      stepId: step.id,
      stepLabel: step.label,
      causeId: typeof step.metadata.causeId === 'string' ? step.metadata.causeId : null,
      analysisId: typeof step.metadata.analysisId === 'string' ? step.metadata.analysisId : null,
      summary: typeof step.metadata.summary === 'string' ? step.metadata.summary : null,
      availableItems: feedbackItemOptions,
      defaultScope: 'CAUSE'
    });
  }, [
    feedbackItemOptions,
    onOpenFeedback,
    step.id,
    step.label,
    step.metadata.analysisId,
    step.metadata.causeId,
    step.metadata.summary
  ]);
  const humanize = (value?: string | null) => {
    if (!value) return null;
    return value
      .split(/[\s_]+/)
      .filter(Boolean)
      .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
      .join(' ');
  };
  const severityLabel = humanize(deactivationInfo?.severity ?? null);
  const issueTypeLabel = humanize(deactivationInfo?.feedbackType ?? null);
  const flaggedAtLabel =
    deactivationInfo?.flaggedAt
      ? formatDateTime(deactivationInfo.flaggedAt) || deactivationInfo.flaggedAt
      : null;

  const renderReferenceImageTextLinks = useCallback(
    (item: ChecklistItem, section: 'VERIFICATION' | 'REPAIR') => {
      const description = normalizeWhitespace(item.description ?? '');
      if (!description) {
        return { content: item.label, hasLinks: false };
      }

      const block = stepReferenceBlocks.find(candidate => {
        const candidateText = normalizeWhitespace(String(candidate?.text || ''));
        const candidateSection = candidate?.section;
        if (!candidateText || candidateText !== description) return false;
        return !candidateSection || candidateSection === section;
      });

      if (!block || !Array.isArray(block.imageIds) || block.imageIds.length === 0) {
        return { content: item.label, hasLinks: false };
      }

      const blockImageIds = block.imageIds.map(String);
      const imageById = new Map(stepReferenceImages.map(image => [String(image.id), image]));
      const blockImages = dedupeReferenceImages(
        blockImageIds
          .map(imageId => imageById.get(imageId))
          .filter((image): image is ReferenceImageHit => Boolean(image?.id))
      );
      if (blockImages.length === 0) {
        return { content: item.label, hasLinks: false };
      }

      const text = item.label;
      const occupiedRanges: Array<{ start: number; end: number }> = [];
      const renderableAnchors = (Array.isArray(block.anchors) ? block.anchors : [])
        .map((anchor, anchorIndex) => {
          const anchorText = String(anchor?.text || '').trim();
          if (!anchorText) return null;
          const anchorImageIds =
            Array.isArray(anchor?.imageIds) && anchor.imageIds.length > 0
              ? anchor.imageIds.map(String)
              : blockImageIds;
          const gallery = dedupeReferenceImages(
            anchorImageIds
              .map(imageId => imageById.get(imageId))
              .filter((image): image is ReferenceImageHit => Boolean(image?.id))
          );
          if (gallery.length === 0) return null;
          const range = findNonOverlappingPhraseRange(text, anchorText, occupiedRanges);
          if (!range) return null;
          occupiedRanges.push(range);
          return {
            key: `${anchorText}:${anchorIndex}:${range.start}`,
            start: range.start,
            end: range.end,
            gallery
          };
        })
        .filter(
          (
            anchor
          ): anchor is {
            key: string;
            start: number;
            end: number;
            gallery: ReferenceImageHit[];
          } => Boolean(anchor)
        )
        .sort((left, right) => left.start - right.start);

      if (renderableAnchors.length === 0) {
        return { content: text, hasLinks: false };
      }

      const parts: React.ReactNode[] = [];
      let cursor = 0;
      renderableAnchors.forEach(anchor => {
        if (anchor.start > cursor) {
          parts.push(text.slice(cursor, anchor.start));
        }
        parts.push(
          <button
            type="button"
            key={`repair-image-anchor:${item.id}:${anchor.key}`}
            style={stepStyles.inlineImageTextLink}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              openPreviewImages(anchor.gallery);
            }}
          >
            {text.slice(anchor.start, anchor.end)}
          </button>
        );
        cursor = anchor.end;
      });
      if (cursor < text.length) {
        parts.push(text.slice(cursor));
      }

      return {
        content: parts,
        hasLinks: true
      };
    },
    [openPreviewImages, stepReferenceBlocks, stepReferenceImages]
  );

  const getMeasurementEvaluation = useCallback(
    (item: ChecklistItem) => {
      if (!MEASUREMENT_CAPTURE_ENABLED) return null;
      const capture = item.capture;
      if (capture?.type !== 'MEASUREMENT') return null;
      const value = captureValues[item.id] ?? '';
      return evaluateMeasurement(capture, value);
    },
    [captureValues]
  );

  const isMeasurementOutOfSpec = useCallback(
    (item: ChecklistItem) => {
      const evaluation = getMeasurementEvaluation(item);
      return evaluation?.status === 'invalid';
    },
    [getMeasurementEvaluation]
  );

  useEffect(() => {
    if (isDeactivated || !MEASUREMENT_CAPTURE_ENABLED) {
      return;
    }
    verificationItems.forEach(item => {
      if (item.capture?.type === 'MEASUREMENT' && isMeasurementOutOfSpec(item)) {
        if (verificationCheckedSet.has(item.id)) {
          onToggleChecklist(step.id, 'verification', item.id);
        }
      }
    });
    repairItems.forEach(item => {
      if (item.capture?.type === 'MEASUREMENT' && isMeasurementOutOfSpec(item)) {
        if (repairCheckedSet.has(item.id)) {
          onToggleChecklist(step.id, 'repair', item.id);
        }
      }
    });
  }, [
    verificationItems,
    repairItems,
    isMeasurementOutOfSpec,
    verificationCheckedSet,
    repairCheckedSet,
    onToggleChecklist,
    step.id,
    isDeactivated
  ]);

  const handleChecklistToggle = useCallback(
    (type: ChecklistType, item: ChecklistItem) => {
      if (isDeactivated) return;
      const evaluation = getMeasurementEvaluation(item);
      const currentlyChecked = type === 'verification'
        ? verificationCheckedSet.has(item.id)
        : repairCheckedSet.has(item.id);
      if (evaluation?.status === 'invalid') {
        if (currentlyChecked) {
          onToggleChecklist(step.id, type, item.id);
        }
        return;
      }
      onToggleChecklist(step.id, type, item.id);
    },
    [getMeasurementEvaluation, verificationCheckedSet, repairCheckedSet, onToggleChecklist, step.id, isDeactivated]
  );

  const toggleChecklistEditor = useCallback(
    (type: ChecklistType) => {
      if (!canEditChecklist || isDeactivated) return;
      if (type === 'verification') {
        setShowVerification(true);
        setVerificationEditError(null);
        setEditingChecklist(current => {
          if (current === 'verification') {
            setVerificationDraft(verificationDraftText);
            return null;
          }
          setVerificationDraft(verificationDraftText);
          return 'verification';
        });
        return;
      }
      setShowRepair(true);
      setRepairEditError(null);
      setEditingChecklist(current => {
        if (current === 'repair') {
          setRepairDraft(repairDraftText);
          return null;
        }
        setRepairDraft(repairDraftText);
        return 'repair';
      });
    },
    [
      canEditChecklist,
      isDeactivated,
      repairDraftText,
      verificationDraftText
    ]
  );

  const saveChecklistEditor = useCallback(
    (type: ChecklistType) => {
      if (!canEditChecklist || isDeactivated) return;
      const error = onSaveChecklistDraft(
        step.id,
        type,
        type === 'verification' ? verificationDraft : repairDraft
      );
      if (type === 'verification') {
        setVerificationEditError(error);
      } else {
        setRepairEditError(error);
      }
      if (!error) {
        setEditingChecklist(current => (current === type ? null : current));
      }
    },
    [
      canEditChecklist,
      isDeactivated,
      onSaveChecklistDraft,
      repairDraft,
      step.id,
      verificationDraft
    ]
  );
  const [draftPart, setDraftPart] = useState({
    partNumber: '',
    description: '',
    batchNumber: '',
    quantity: ''
  });
  const [selectedPartOptionKey, setSelectedPartOptionKey] = useState<string | null>(null);
  const [scanStatus, setScanStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [scanMessage, setScanMessage] = useState<string | null>(null);

  useEffect(() => {
    if (scanStatus === 'error' && typeof scanMessage === 'string' && scanMessage.toLowerCase().includes('enter quantity')) {
      const qtyText = draftPart.quantity.trim();
      const parsed = Number(qtyText);
      if (qtyText && Number.isFinite(parsed) && parsed > 0) {
        setScanStatus('idle');
        setScanMessage(null);
      }
    }
  }, [draftPart.quantity, scanMessage, scanStatus]);

  const resetDraftPart = useCallback(() => {
    setDraftPart({
      partNumber: '',
      description: '',
      batchNumber: '',
      quantity: ''
    });
    setSelectedPartOptionKey(null);
  }, []);

  const handleDraftChange = useCallback(
    (field: 'partNumber' | 'description' | 'batchNumber') =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (field === 'partNumber') {
          setDraftPart(prev => {
            const trimmed = value.trim();
            const nextKey = normalizePartLookupToken(trimmed);
            const exactMatch = nextKey ? partSelectionLookup.get(nextKey) : null;
            const previousSelected =
              selectedPartOptionKey ? partSelectionLookup.get(selectedPartOptionKey) : null;
            const shouldClearDescription =
              !exactMatch &&
              previousSelected &&
              normalizeWhitespace(prev.description) === normalizeWhitespace(previousSelected.description);
            return {
              ...prev,
              partNumber: value,
              description:
                exactMatch && exactMatch.description
                  ? exactMatch.description
                  : shouldClearDescription
                  ? ''
                  : prev.description
            };
          });
          setSelectedPartOptionKey(() => {
            const trimmed = value.trim();
            const nextKey = normalizePartLookupToken(trimmed);
            return nextKey && partSelectionLookup.has(nextKey) ? nextKey : null;
          });
          return;
        }

        setDraftPart(prev => ({
          ...prev,
          [field]: value
        }));
        if (field === 'description') {
          setSelectedPartOptionKey(null);
        }
      },
    [partSelectionLookup, selectedPartOptionKey]
  );

  const handleDraftQuantityChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (!/^\d*\.?\d*$/.test(value)) return;
    setDraftPart(prev => ({
      ...prev,
      quantity: value
    }));
  }, []);

  const canAddPart = useMemo(() => {
    return Boolean(draftPart.partNumber.trim() || draftPart.description.trim());
  }, [draftPart.description, draftPart.partNumber]);

  const handleAddPart = useCallback(() => {
    if (isDeactivated) return;
    if (!canAddPart) {
      setScanStatus('error');
      setScanMessage('Enter a quantity before adding the part.');
      return;
    }
    const qtyText = draftPart.quantity.trim();
    const quantityValue = Number(qtyText);
    if (!qtyText || !Number.isFinite(quantityValue) || quantityValue <= 0) {
      setScanStatus('error');
      setScanMessage('Enter a quantity before adding the part.');
      return;
    }
    const newPart: PartUsage = {
      id: createPartUsageId(),
      partNumber: toSafeString(draftPart.partNumber),
      description: toSafeString(draftPart.description),
      batchNumber: toSafeString(draftPart.batchNumber),
      quantity: quantityValue
    };
    onAddPart(step.id, newPart);
    resetDraftPart();
    setScanStatus('idle');
    setScanMessage(null);
  }, [
    canAddPart,
    draftPart.batchNumber,
    draftPart.description,
    draftPart.partNumber,
    draftPart.quantity,
    isDeactivated,
    onAddPart,
    resetDraftPart,
    step.id
  ]);

  const handleSelectPartOption = useCallback((option: PartSelectionOption) => {
    setDraftPart(prev => ({
      ...prev,
      partNumber: option.partNumber,
      description: option.description || prev.description
    }));
    setSelectedPartOptionKey(option.key);
    setScanStatus('idle');
    setScanMessage(null);
  }, []);

  const handleRemovePart = useCallback(
    (partId: string) => {
      if (isDeactivated) return;
      onRemovePart(step.id, partId);
    },
    [isDeactivated, onRemovePart, step.id]
  );

  const probabilityLabel =
    step.metadata.probability && step.metadata.probability !== 'HIGH'
      ? step.metadata.probability
      : step.metadata.probability === 'HIGH'
      ? 'High likelihood'
      : null;

  const handleScanClick = useCallback(() => {
    if (isDeactivated || scanStatus === 'pending') return;
    setScanMessage(null);
    fileInputRef.current?.click();
  }, [isDeactivated, scanStatus]);

  const handleFileInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isDeactivated) return;
      const file = event.target.files?.[0];
      if (!file) return;
      setScanStatus('pending');
      setScanMessage(null);
      try {
        const response = await repairApi.scanPartLabel(file);
        if (!response || response.success === false) {
          throw new Error('Failed to scan label.');
        }
        if (!response.found || !response.part) {
          setScanStatus('error');
          setScanMessage('No part details detected. Enter manually or try another photo.');
        } else {
          const { part } = response;
          const matchedKey = normalizePartLookupToken(part.partNumber || '');
          const matchedOption = matchedKey ? partSelectionLookup.get(matchedKey) : null;
          setDraftPart({
            partNumber: part.partNumber || '',
            description: matchedOption?.description || part.description || '',
            batchNumber: part.batchNumber || '',
            quantity: ''
          });
          setSelectedPartOptionKey(matchedOption?.key || null);
          const details: string[] = [];
          if (part.manufacturedDate) {
            details.push(`Manufactured ${part.manufacturedDate}`);
          }
          if (part.notes) {
            details.push(part.notes);
          }
          if (!details.some(text => text.toLowerCase().includes('quantity'))) {
            details.push('Enter quantity before adding.');
          }
          setScanStatus('success');
          setScanMessage(details.join(' | ') || 'Fields populated from label.');
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to scan label.';
        setScanStatus('error');
        setScanMessage(message);
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [isDeactivated, partSelectionLookup]
  );

  const handleAttachmentButtonClick = useCallback(() => {
    if (isDeactivated || attachmentUploading) return;
    setAttachmentError(null);
    attachmentInputRef.current?.click();
  }, [attachmentUploading, isDeactivated]);

  const handleAttachmentInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isDeactivated) return;
      const fileList = event.target.files;
      if (!fileList || fileList.length === 0) return;
      if (attachments.length >= MAX_ATTACHMENTS_PER_STEP) {
        setAttachmentError(`You can store up to ${MAX_ATTACHMENTS_PER_STEP} photos per cause.`);
        event.target.value = '';
        return;
      }
      const availableSlots = Math.max(0, MAX_ATTACHMENTS_PER_STEP - attachments.length);
      const selectedFiles = Array.from(fileList).slice(0, availableSlots);
      if (selectedFiles.length === 0) {
        setAttachmentError(`You can store up to ${MAX_ATTACHMENTS_PER_STEP} photos per cause.`);
        event.target.value = '';
        return;
      }
      setAttachmentUploading(true);
      const messages: string[] = [];
      try {
        for (const file of selectedFiles) {
          if (!file.type || !file.type.startsWith('image/')) {
            messages.push('Only image files are supported.');
            continue;
          }
          if (file.size > MAX_ATTACHMENT_BYTES) {
            messages.push(`${file.name || 'Image'} exceeds the 6MB limit.`);
            continue;
          }
          try {
            const dataUrl = await readFileAsDataUrl(file);
            onAddAttachment(step.id, {
              id: createAttachmentId(),
              name: file.name || 'Photo',
              mimeType: file.type || 'image/jpeg',
              size: file.size,
              dataUrl,
              addedAt: new Date().toISOString()
            });
          } catch (error) {
            console.error('repairTask:attachment upload failed', error);
            messages.push(`Failed to process ${file.name || 'image'}.`);
          }
        }
      } finally {
        setAttachmentUploading(false);
        if (event.target) {
          event.target.value = '';
        }
      }
      setAttachmentError(messages.length > 0 ? messages.join(' ') : null);
    },
    [attachments.length, isDeactivated, onAddAttachment, step.id]
  );

  const handleRemoveAttachment = useCallback(
    (attachmentId: string) => {
      if (isDeactivated) return;
      onRemoveAttachment(step.id, attachmentId);
    },
    [isDeactivated, onRemoveAttachment, step.id]
  );

  const isItemChecked = useCallback(
    (type: ChecklistType, item: ChecklistItem) => {
      const base =
        type === 'verification'
          ? verificationCheckedSet.has(item.id)
          : repairCheckedSet.has(item.id);
      if (item.capture?.type === 'MEASUREMENT') {
        const evaluation = getMeasurementEvaluation(item);
        return base && evaluation?.status !== 'invalid';
      }
      return base;
    },
    [verificationCheckedSet, repairCheckedSet, getMeasurementEvaluation]
  );

  return (
    <div style={{ ...stepStyles.card, ...STEP_CARD_VARIANTS[resolution] }}>
      {!isManualEntry && (
      <div style={stepStyles.header}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', flex: 1 }}>
          <div style={stepStyles.title}>
            {step.sequence}. {step.label}
          </div>
          {step.metadata.summary && (
            <div style={stepStyles.summary}>{step.metadata.summary}</div>
          )}
          <div style={stepStyles.statusRow}>
            <div style={stepStyles.statusRowLeft}>
              <span style={{ ...stepStyles.statusPill, ...STEP_PILL_VARIANTS[resolution] }}>
                {STEP_STATE_LABELS[resolution]}
              </span>
              <span style={stepStyles.statusHint}>{statusHint}</span>
            </div>
          </div>
        </div>
        <div style={stepStyles.headerActions}>
          <div style={stepStyles.headerActionsTop}>
          <button
            type="button"
            style={{
              ...pageStyles.buttonPrimary,
              padding: '0.5rem 1rem',
              opacity: canSaveStep ? 1 : 0.65,
              cursor: canSaveStep ? 'pointer' : 'not-allowed'
            }}
            onClick={() => onSaveStep(step.id)}
            disabled={!canSaveStep}
          >
            {stepSaving ? 'Saving step...' : stepDirty ? 'Save step' : 'Step saved'}
          </button>
          <button
            type="button"
            style={stepStyles.flagButton}
            onClick={handleFlagIssue}
          >
            Flag issue
          </button>
          <button
            style={stepStyles.collapseToggle}
            type="button"
            onClick={() => setCollapsed(prev => !prev)}
          >
            {collapsed ? 'Expand' : 'Collapse'}
          </button>
          </div>
          {probabilityLabel && (
            <div style={stepStyles.headerActionsMeta}>
              <span style={{ ...stepStyles.chip, ...stepStyles.chipRight }}>{probabilityLabel}</span>
            </div>
          )}
        </div>
      </div>
      )}
      {isDeactivated && (
        <div style={stepStyles.deactivatedBanner}>
          <div style={stepStyles.deactivatedInfo}>
            <span>Flagged for documentation review.</span>
            {deactivationInfo?.title && (
              <span style={{ fontWeight: 600 }}>{deactivationInfo.title}</span>
            )}
            {(issueTypeLabel || severityLabel) && (
              <span style={stepStyles.flaggedDetail}>
                {issueTypeLabel ? `Type: ${issueTypeLabel}` : ''}
                {issueTypeLabel && severityLabel ? ' | ' : ''}
                {severityLabel ? `Severity: ${severityLabel}` : ''}
              </span>
            )}
            {deactivationInfo?.itemLabel && (
              <span style={stepStyles.flaggedDetail}>
                Affected step: {deactivationInfo.itemLabel}
              </span>
            )}
            {flaggedAtLabel && (
              <span style={stepStyles.flaggedDetail}>Flagged {flaggedAtLabel}</span>
            )}
          </div>
          <button
            type="button"
            style={stepStyles.reactivateButton}
            onClick={() => onReactivate(step.id)}
          >
            Reactivate
          </button>
        </div>
      )}

      {!isManualEntry && !collapsed && hasVerificationSection && (
        <div style={stepStyles.checklistSection}>
          <div style={stepStyles.checklistHeader}>
            <strong>Verification steps</strong>
            <div style={stepStyles.checklistHeaderActions}>
              {canEditChecklist && (
                <button
                  style={stepStyles.checklistToggle}
                  type="button"
                  onClick={() => toggleChecklistEditor('verification')}
                >
                  {editingChecklist === 'verification' ? 'Cancel' : 'Edit'}
                </button>
              )}
              <button
                style={stepStyles.checklistToggle}
                type="button"
                onClick={() => setShowVerification(prev => !prev)}
              >
                {showVerification ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          {showVerification && (
            <div style={stepStyles.checklistBody}>
              {editingChecklist === 'verification' ? (
                <div style={stepStyles.checklistEditor}>
                  <div style={stepStyles.checklistHint}>
                    One step per line. Delete lines to remove rows. Keep numbering if you want the edited repair record to stay easy to compare against TSFAD.
                  </div>
                  {verificationEditError && (
                    <div style={stepStyles.checklistEditorError}>{verificationEditError}</div>
                  )}
                  <textarea
                    value={verificationDraft}
                    onChange={event => setVerificationDraft(event.target.value)}
                    style={pageStyles.textarea}
                    rows={Math.max(6, verificationDraft.split(/\r?\n/).length + 1)}
                  />
                  <div style={stepStyles.checklistEditorActions}>
                    <button
                      type="button"
                      style={pageStyles.buttonPrimary}
                      onClick={() => saveChecklistEditor('verification')}
                    >
                      Save verification steps
                    </button>
                    <button
                      type="button"
                      style={pageStyles.buttonSecondary}
                      onClick={() => {
                        setVerificationDraft(verificationDraftText);
                        setVerificationEditError(null);
                        setEditingChecklist(current =>
                          current === 'verification' ? null : current
                        );
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : verificationItems.length === 0 ? (
                <div style={pageStyles.muted}>No verification steps recorded for this repair cause.</div>
              ) : (
                <>
                  <div style={stepStyles.checklistHint}>
                    If a verification step or repair step was already completed under another cause and conditions are unchanged,
                    mark it complete here and note the cause reference in step notes.
                  </div>
                  {verificationItems.map(item => {
                    const capture = item.capture;
                    const isMeasurement = capture?.type === 'MEASUREMENT';
                    const linkedText = renderReferenceImageTextLinks(item, 'VERIFICATION');
                    return (
                      <label
                        key={item.id}
                        style={{
                          ...stepStyles.checklistItem,
                          paddingLeft: `${item.depth * 1.2}rem`
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isItemChecked('verification', item)}
                          onChange={() => handleChecklistToggle('verification', item)}
                          disabled={isDeactivated}
                          style={{ marginTop: '0.3rem' }}
                        />
                        <div style={stepStyles.checklistContent}>
                          <span style={stepStyles.checklistLabel}>{linkedText.content}</span>
                          {isMeasurement && capture && renderMeasurementCapture(item.id, capture)}
                        </div>
                      </label>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {!isManualEntry && !collapsed && hasRepairSection && (
        <div style={stepStyles.checklistSection}>
          <div style={stepStyles.checklistHeader}>
            <strong>Repair steps</strong>
            <div style={stepStyles.checklistHeaderActions}>
              {canEditChecklist && (
                <button
                  style={stepStyles.checklistToggle}
                  type="button"
                  onClick={() => toggleChecklistEditor('repair')}
                >
                  {editingChecklist === 'repair' ? 'Cancel' : 'Edit'}
                </button>
              )}
              <button
                style={stepStyles.checklistToggle}
                type="button"
                onClick={() => setShowRepair(prev => !prev)}
              >
                {showRepair ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          {showRepair && (
            <div style={stepStyles.checklistBody}>
              {editingChecklist === 'repair' ? (
                <div style={stepStyles.checklistEditor}>
                  <div style={stepStyles.checklistHint}>
                    One step per line. Delete lines to remove rows. Keep numbering if you want the edited repair record to stay easy to compare against TSFAD.
                  </div>
                  {repairEditError && (
                    <div style={stepStyles.checklistEditorError}>{repairEditError}</div>
                  )}
                  <textarea
                    value={repairDraft}
                    onChange={event => setRepairDraft(event.target.value)}
                    style={pageStyles.textarea}
                    rows={Math.max(6, repairDraft.split(/\r?\n/).length + 1)}
                  />
                  <div style={stepStyles.checklistEditorActions}>
                    <button
                      type="button"
                      style={pageStyles.buttonPrimary}
                      onClick={() => saveChecklistEditor('repair')}
                    >
                      Save repair steps
                    </button>
                    <button
                      type="button"
                      style={pageStyles.buttonSecondary}
                      onClick={() => {
                        setRepairDraft(repairDraftText);
                        setRepairEditError(null);
                        setEditingChecklist(current => (current === 'repair' ? null : current));
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : repairItems.length === 0 ? (
                <div style={pageStyles.muted}>No repair steps recorded for this repair cause.</div>
              ) : (
                <>
                  {repairItems.map(item => {
                    const capture = item.capture;
                    const isMeasurement = capture?.type === 'MEASUREMENT';
                    const linkedText = renderReferenceImageTextLinks(item, 'REPAIR');
                    return (
                      <label
                        key={item.id}
                        style={{
                          ...stepStyles.checklistItem,
                          paddingLeft: `${item.depth * 1.2}rem`
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isItemChecked('repair', item)}
                          onChange={() => handleChecklistToggle('repair', item)}
                          disabled={isDeactivated}
                          style={{ marginTop: '0.3rem' }}
                        />
                        <div style={stepStyles.checklistContent}>
                          <span style={stepStyles.checklistLabel}>{linkedText.content}</span>
                          {isMeasurement && capture && renderMeasurementCapture(item.id, capture)}
                        </div>
                      </label>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {(!collapsed || isManualEntry) && (
        <>
          <div style={stepStyles.partsSection}>
            <div style={stepStyles.partsSectionHeader}>
              <strong>Parts replaced</strong>
            </div>
            {suggestedPartOptions.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                <div style={stepStyles.partDraftHint}>Likely parts for this repair cause</div>
                <div style={stepStyles.partSuggestionList}>
                  {suggestedPartOptions.map(option => (
                    <button
                      key={option.key}
                      type="button"
                      style={{
                        ...stepStyles.partSuggestionButton,
                        ...(isDeactivated ? { opacity: 0.55, cursor: 'not-allowed' } : {})
                      }}
                      onClick={() => handleSelectPartOption(option)}
                      disabled={isDeactivated}
                      title={`${option.description || option.partNumber} | ${formatPartOptionMeta(option)}`}
                    >
                      {option.partNumber}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {partsUsed.length === 0 ? (
              <span style={pageStyles.muted}>No parts recorded.</span>
            ) : (
              <div style={stepStyles.partsTable}>
                <div style={{ ...stepStyles.partsRow, ...stepStyles.partsHeaderRow }}>
                  <span>Part number</span>
                  <span>Description</span>
                  <span>Batch/lot</span>
                  <span>Quantity</span>
                  <span />
                </div>
                {partsUsed.map(part => (
                  <div key={part.id} style={stepStyles.partsRow}>
                    <span style={stepStyles.partCellValue}>{part.partNumber || '"'}</span>
                    <span style={stepStyles.partCellValue}>{part.description || '"'}</span>
                    <span style={stepStyles.partCellValue}>{part.batchNumber || '"'}</span>
                    <span style={stepStyles.partCellValue}>
                      {part.quantity === null || Number.isNaN(part.quantity)
                        ? '"'
                        : String(part.quantity)}
                    </span>
                    <div style={stepStyles.partActions}>
                      <button
                        type="button"
                        style={{
                          ...stepStyles.partRemoveButton,
                          ...(isDeactivated ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                        }}
                        onClick={() => handleRemovePart(part.id)}
                        aria-label="Remove part entry"
                        disabled={isDeactivated}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={stepStyles.partDraftLabel}>Add new part</div>
            <div style={stepStyles.partDraftHint}>
              Start with the likely TSFAD parts, keep typing to narrow the model part list, or enter a
              unique part number manually. Known part numbers will auto-fill the description.
            </div>
            <div style={stepStyles.partsRow}>
              <input
                style={{
                  ...stepStyles.partCellInput,
                  ...(isDeactivated ? { background: BRAND_SURFACE, cursor: 'not-allowed' } : {})
                }}
                placeholder="Part number"
                list={partDatalistId}
                value={draftPart.partNumber}
                onChange={handleDraftChange('partNumber')}
                disabled={isDeactivated}
              />
              <datalist id={partDatalistId}>
                {partSelectionOptions.map(option => (
                  <option key={option.key} value={option.partNumber}>
                    {option.description
                      ? `${option.description} | ${formatPartOptionMeta(option)}`
                      : formatPartOptionMeta(option)}
                  </option>
                ))}
              </datalist>
              <input
                style={{
                  ...stepStyles.partCellInput,
                  ...(isDeactivated ? { background: BRAND_SURFACE, cursor: 'not-allowed' } : {})
                }}
                placeholder="Part description"
                value={draftPart.description}
                onChange={handleDraftChange('description')}
                disabled={isDeactivated}
              />
              <input
                style={{
                  ...stepStyles.partCellInput,
                  ...(isDeactivated ? { background: BRAND_SURFACE, cursor: 'not-allowed' } : {})
                }}
                placeholder="Batch or lot number"
                value={draftPart.batchNumber}
                onChange={handleDraftChange('batchNumber')}
                disabled={isDeactivated}
              />
              <input
                style={{
                  ...stepStyles.partCellInput,
                  maxWidth: '6rem',
                  ...(isDeactivated ? { background: BRAND_SURFACE, cursor: 'not-allowed' } : {})
                }}
                type="text"
                inputMode="numeric"
                placeholder="Qty"
                value={draftPart.quantity}
                onChange={handleDraftQuantityChange}
                disabled={isDeactivated}
              />
              <div style={stepStyles.partActions}>
                <button
                  type="button"
                  style={{
                    ...stepStyles.partAddButton,
                    opacity: !canAddPart || isDeactivated ? 0.6 : 1,
                    cursor: !canAddPart || isDeactivated ? 'not-allowed' : 'pointer'
                  }}
                  onClick={handleAddPart}
                  disabled={!canAddPart || isDeactivated}
                >
                  Add part
                </button>
                <button
                  type="button"
                  style={{
                    ...stepStyles.partScanButton,
                    opacity: scanStatus === 'pending' || isDeactivated ? 0.6 : 1,
                    cursor: scanStatus === 'pending' || isDeactivated ? 'not-allowed' : 'pointer'
                  }}
                  onClick={handleScanClick}
                  disabled={scanStatus === 'pending' || isDeactivated}
                >
                  {scanStatus === 'pending' ? 'Scanning...' : 'Scan label'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileInputChange}
                />
              </div>
            </div>
            {scanMessage && (
              <div
                style={{
                  ...stepStyles.scanMessage,
                  ...(scanStatus === 'error'
                    ? stepStyles.scanMessageError
                    : scanStatus === 'success'
                    ? stepStyles.scanMessageSuccess
                    : {})
                }}
              >
                {scanMessage}
              </div>
            )}
          </div>

          <div style={{ borderTop: `1px dashed ${BRAND_BORDER}` }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={stepStyles.notesHeader}>
              <strong>Technician notes</strong>
              <div style={stepStyles.attachmentToolbar}>
                <button
                  type="button"
                  style={{
                    ...stepStyles.attachmentButton,
                    opacity: isDeactivated || attachmentUploading ? 0.5 : 1,
                    cursor: isDeactivated || attachmentUploading ? 'not-allowed' : 'pointer'
                  }}
                  onClick={handleAttachmentButtonClick}
                  disabled={isDeactivated || attachmentUploading}
                >
                  {attachmentUploading ? 'Processing...' : 'Add photo'}
                </button>
                <span style={stepStyles.attachmentHint}>
                  {isManualEntry
                    ? 'Photos are stored with this repair entry and appear in the final report.'
                    : 'Photos are stored with this cause and appear in the final report.'}
                </span>
              </div>
            </div>
            <textarea
              style={{ ...pageStyles.textarea, background: '#ffffff' }}
              placeholder={
                isManualEntry ? 'Add verification and repair notes' : 'Add any observations for this cause'
              }
              value={step.notes}
              onChange={event => onNotesChange(step.id, event.target.value)}
            />
            {attachmentError && <div style={stepStyles.attachmentError}>{attachmentError}</div>}
            {attachments.length === 0 ? (
              <div style={stepStyles.attachmentEmpty}>No photos attached yet.</div>
            ) : (
              <div style={stepStyles.attachmentList}>
                {attachments.map(attachment => (
                  <div key={attachment.id} style={stepStyles.attachmentCard}>
                    <a href={attachment.dataUrl} target="_blank" rel="noreferrer">
                      <img
                        src={attachment.dataUrl}
                        alt={attachment.name || 'Technician photo'}
                        style={stepStyles.attachmentThumb}
                      />
                    </a>
                    <div style={stepStyles.attachmentMeta}>
                      <div style={stepStyles.attachmentName}>
                        {attachment.name || 'Photo'}
                      </div>
                      {attachment.size > 0 && (
                        <div style={stepStyles.attachmentSize}>
                          {formatFileSize(attachment.size)}
                        </div>
                      )}
                    </div>
                    {!isDeactivated && (
                      <button
                        type="button"
                        style={stepStyles.attachmentRemove}
                        onClick={() => handleRemoveAttachment(attachment.id)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <input
              ref={attachmentInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleAttachmentInputChange}
            />
          </div>
        </>
      )}

      {previewImage && (
        <div style={stepStyles.previewOverlay} onClick={closePreviewImages}>
          <div style={stepStyles.previewModal} onClick={event => event.stopPropagation()}>
            <div style={stepStyles.previewHeader}>
              <div style={stepStyles.previewTitle}>
                <strong>{previewImage.label || 'Reference image'}</strong>
                <span style={stepStyles.previewCounter}>
                  {previewImageIndex + 1} / {previewImageCount}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  style={stepStyles.previewControlButton}
                  onClick={() =>
                    onFlagReferenceImage({
                      stepId: step.id,
                      stepLabel: step.label,
                      causeId: typeof step.metadata.causeId === 'string' ? step.metadata.causeId : null,
                      analysisId:
                        typeof step.metadata.analysisId === 'string' ? step.metadata.analysisId : null,
                      summary: typeof step.metadata.summary === 'string' ? step.metadata.summary : null,
                      image: previewImage
                    })
                  }
                >
                  Flag Image
                </button>
                <button
                  type="button"
                  style={stepStyles.previewCloseButton}
                  onClick={closePreviewImages}
                >
                  Close
                </button>
              </div>
            </div>
            <div style={stepStyles.previewControls}>
              {previewImageCount > 1 && (
                <>
                  <button
                    type="button"
                    style={stepStyles.previewControlButton}
                    onClick={showPreviousPreviewImage}
                    disabled={previewImageIndex <= 0}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    style={stepStyles.previewControlButton}
                    onClick={showNextPreviewImage}
                    disabled={previewImageIndex >= previewImageCount - 1}
                  >
                    Next
                  </button>
                </>
              )}
              <button
                type="button"
                style={stepStyles.previewControlButton}
                onClick={() => setPreviewZoom(value => Math.max(100, value - 25))}
                aria-label="Zoom out"
              >
                -
              </button>
              <input
                type="range"
                min={100}
                max={300}
                step={25}
                value={previewZoom}
                onChange={event => setPreviewZoom(Number(event.target.value))}
                style={stepStyles.previewZoomSlider}
                aria-label="Image zoom"
              />
              <button
                type="button"
                style={stepStyles.previewControlButton}
                onClick={() => setPreviewZoom(value => Math.min(300, value + 25))}
                aria-label="Zoom in"
              >
                +
              </button>
              <button
                type="button"
                style={stepStyles.previewControlButton}
                onClick={() => setPreviewZoom(100)}
              >
                100%
              </button>
              <span style={stepStyles.previewZoomValue}>{previewZoom}%</span>
            </div>
            {previewImageCount > 1 && (
              <div style={stepStyles.previewGalleryStrip}>
                {previewImages.map((image, index) => (
                  <button
                    type="button"
                    key={image.id || `${index}`}
                    style={{
                      ...stepStyles.previewGalleryThumbButton,
                      ...(index === previewImageIndex
                        ? stepStyles.previewGalleryThumbButtonActive
                        : {})
                    }}
                    onClick={() => selectPreviewImage(index)}
                    aria-label={`Open reference image ${index + 1}`}
                  >
                    <AuthenticatedReferenceImage
                      src={image.fileUrl}
                      alt={image.label || 'reference image'}
                      style={stepStyles.previewGalleryThumb}
                      placeholderStyle={stepStyles.previewGalleryThumbPlaceholder}
                    />
                  </button>
                ))}
              </div>
            )}
            <div style={stepStyles.previewImageStage}>
              <div
                style={{
                  ...stepStyles.previewImageCanvas,
                  width: `${previewZoom}%`,
                  height: `${previewZoom}%`,
                  alignItems: previewZoom > 100 ? 'flex-start' : 'center',
                  justifyContent: previewZoom > 100 ? 'flex-start' : 'center'
                }}
              >
                <AuthenticatedReferenceImage
                  src={previewImage.fileUrl}
                  alt="Reference image"
                  style={stepStyles.previewImage}
                  placeholderStyle={stepStyles.previewPlaceholder}
                  placeholderText="No preview available"
                />
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const RepairTask: React.FC = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();

  const [workspace, setWorkspace] = useState<WorkspacePayload | null>(null);
  const [steps, setSteps] = useState<RepairStep[]>([]);
  const [overallStatus, setOverallStatus] = useState<string>('IN_PROGRESS');
  const [totalTimeHours, setTotalTimeHours] = useState<string>('');
  const [totalTimeBaselineMinutes, setTotalTimeBaselineMinutes] = useState<number | null>(null);
  const [totalTimeManualOverride, setTotalTimeManualOverride] = useState(false);
  const [timeTrackingState, setTimeTrackingState] = useState<RepairTimeTrackingState>(
    EMPTY_REPAIR_TIME_TRACKING_STATE
  );
  const [timeTrackingAnchor, setTimeTrackingAnchor] =
    useState<LocalRepairTimeTrackingAnchor | null>(null);
  const [timeTrackingTick, setTimeTrackingTick] = useState<number>(() => Date.now());
  const [runNotes, setRunNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [dirtyStepIds, setDirtyStepIds] = useState<string[]>([]);
  const [topLevelDirty, setTopLevelDirty] = useState(false);
  const [functionalTestState, setFunctionalTestState] = useState<FunctionalTestState>(
    EMPTY_FUNCTIONAL_TEST_STATE
  );
  const [functionalTestDirty, setFunctionalTestDirty] = useState(false);
  const [functionalTestCollapsed, setFunctionalTestCollapsed] = useState(false);
  const [repairStepsCollapsed, setRepairStepsCollapsed] = useState(false);
  const [functionalTestReferenceCache, setFunctionalTestReferenceCache] = useState<
    Partial<Record<FunctionalTestType, FunctionalTestReference | null>>
  >({});
  const [functionalTestReferenceLoading, setFunctionalTestReferenceLoading] =
    useState<FunctionalTestType | null>(null);
  const [savingStepId, setSavingStepId] = useState<string | null>(null);
  const [statusReason, setStatusReason] = useState('');
  const [statusDialog, setStatusDialog] = useState<{
    mode: 'COMPLETE' | 'ON_HOLD' | 'REOPEN';
    reason: string;
    requireReason: boolean;
  } | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [reportSummary, setReportSummary] = useState<string | null>(null);
  const [reportGeneratedAt, setReportGeneratedAt] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportIsCustom, setReportIsCustom] = useState(false);
  const [reportEditing, setReportEditing] = useState(false);
  const [reportDraft, setReportDraft] = useState('');
  const [reportSaving, setReportSaving] = useState(false);
  const [reportEditError, setReportEditError] = useState<string | null>(null);
  const [serviceReportModalOpen, setServiceReportModalOpen] = useState(false);
  const [serviceReportDraft, setServiceReportDraft] = useState<ServiceReportDocument | null>(null);
  const [serviceReportLoading, setServiceReportLoading] = useState(false);
  const [serviceReportSaving, setServiceReportSaving] = useState(false);
  const [serviceReportSending, setServiceReportSending] = useState(false);
  const [serviceReportError, setServiceReportError] = useState<string | null>(null);
  const [serviceReportMode, setServiceReportMode] = useState<'create' | 'view'>('create');
  const [feedbackModal, setFeedbackModal] = useState<FeedbackModalState | null>(null);
  const [feedbackForm, setFeedbackForm] = useState<DocumentationFeedbackForm>(() =>
    createDocumentationFeedbackDefaults()
  );
  const [feedbackScope, setFeedbackScope] = useState<FeedbackScope>('CAUSE');
  const [feedbackItemId, setFeedbackItemId] = useState<string | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [imageFlagTarget, setImageFlagTarget] = useState<ImageFeedbackTargetState | null>(null);
  const [imageFlagNotes, setImageFlagNotes] = useState('');
  const [imageFlagSubmitting, setImageFlagSubmitting] = useState(false);

  useEffect(() => {
    setHasUnsavedChanges(topLevelDirty || functionalTestDirty || dirtyStepIds.length > 0);
  }, [dirtyStepIds, functionalTestDirty, topLevelDirty]);
  const [manualStepSaving, setManualStepSaving] = useState(false);
  const [deviceExpertOpen, setDeviceExpertOpen] = useState(true);
  const [deviceExpertStepId, setDeviceExpertStepId] = useState('');
  const [deviceExpertContextKey, setDeviceExpertContextKey] = useState('');
  const [deviceExpertQuestion, setDeviceExpertQuestion] = useState('');
  const [deviceExpertMessages, setDeviceExpertMessages] = useState<DeviceExpertMessage[]>([]);
  const [deviceExpertLoading, setDeviceExpertLoading] = useState(false);
  const [deviceExpertError, setDeviceExpertError] = useState<string | null>(null);
  const [deviceExpertConversationId, setDeviceExpertConversationId] = useState<string | null>(null);
  const taskStatus = useMemo(
    () =>
      String(workspace?.task?.status || overallStatus || 'IN_PROGRESS')
        .trim()
        .toUpperCase(),
    [workspace?.task?.status, overallStatus]
  );
  const workspaceReadOnly = workspace?.access?.canWrite === false;
  const savedServiceReportStatus = String(workspace?.serviceReport?.status || '').trim().toUpperCase();
  const savedServiceReportSent = savedServiceReportStatus === 'SENT';
  const canRebuildServiceReport =
    !workspaceReadOnly &&
    taskStatus === 'COMPLETED' &&
    Boolean(reportSummary?.trim()) &&
    !savedServiceReportSent;
  const assignedLabel = workspace?.access?.assignedUserName || workspace?.access?.assignedUserId || null;
  const taskLocked = taskStatus === 'COMPLETED';
  const currentTrackedMinutes = useMemo(() => {
    const baseMinutes = Math.max(0, timeTrackingState.accumulatedMinutes);
    if (timeTrackingState.phase !== 'RUNNING' || !timeTrackingAnchor) {
      return baseMinutes;
    }
    const startedAtMs = Date.parse(timeTrackingAnchor.startedAt);
    if (!Number.isFinite(startedAtMs)) {
      return baseMinutes;
    }
    const anchorBaseMinutes = Math.max(baseMinutes, timeTrackingAnchor.baseMinutes);
    const elapsedMinutes = Math.max(0, timeTrackingTick - startedAtMs) / 60000;
    return Math.round((anchorBaseMinutes + elapsedMinutes) * 100) / 100;
  }, [
    timeTrackingAnchor,
    timeTrackingState.accumulatedMinutes,
    timeTrackingState.phase,
    timeTrackingTick
  ]);
  const suggestedTotalTimeMinutes = useMemo(() => {
    const roundedTrackedMinutes = roundMinutesToQuarterHour(currentTrackedMinutes);
    return Math.max(totalTimeBaselineMinutes ?? 0, roundedTrackedMinutes);
  }, [currentTrackedMinutes, totalTimeBaselineMinutes]);
  const suggestedTotalTimeHours = useMemo(
    () => formatHoursFromMinutes(suggestedTotalTimeMinutes),
    [suggestedTotalTimeMinutes]
  );
  const totalTimeClockNote = useMemo(() => {
    if (workspaceReadOnly) {
      return 'Clock inactive in read-only view. Total time remains manually entered.';
    }
    const elapsedLabel = formatElapsedMinutes(currentTrackedMinutes);
    switch (timeTrackingState.phase) {
      case 'RUNNING':
        return `Clock running | ${elapsedLabel} elapsed | rounded to nearest quarter hour.`;
      case 'PAUSED':
        return `Clock paused | ${elapsedLabel} captured so far.`;
      case 'STOPPED':
        return `Clock stopped | ${elapsedLabel} captured on this repair.`;
      default:
        return `Clock idle | ${elapsedLabel} captured so far.`;
    }
  }, [currentTrackedMinutes, timeTrackingState.phase, workspaceReadOnly]);
  const totalTimeInputNote = useMemo(
    () =>
      totalTimeManualOverride
        ? 'Manual override active. Save to replace the clock suggestion.'
        : totalTimeClockNote,
    [totalTimeClockNote, totalTimeManualOverride]
  );
  const functionalTestChecklistIds = useMemo(
    () => collectFunctionalTestChecklistIds(functionalTestState.reference),
    [functionalTestState.reference]
  );
  const functionalTestProgress = useMemo(
    () => ({
      completed: functionalTestState.completedItemIds.length,
      total: functionalTestChecklistIds.length
    }),
    [functionalTestChecklistIds.length, functionalTestState.completedItemIds.length]
  );
  const functionalTestSummaryLabel = useMemo(() => {
    if (!functionalTestState.selectedType || !functionalTestState.reference) {
      return 'No final functional test carried out';
    }
    const shortLabel = FUNCTIONAL_TEST_TYPE_COPY[functionalTestState.selectedType].shortLabel;
    if (functionalTestState.status === 'COMPLETE') {
      return `${shortLabel} completed`;
    }
    if (functionalTestState.status === 'IN_PROGRESS') {
      return `${shortLabel} in progress`;
    }
    return `${shortLabel} selected`;
  }, [functionalTestState.reference, functionalTestState.selectedType, functionalTestState.status]);
  const functionalTestSummaryNote = useMemo(() => {
    if (!functionalTestState.selectedType || !functionalTestState.reference) {
      return 'Optional post-repair verification only. No final test has been carried out.';
    }
    const revisionDetails = functionalTestState.reference.revisionId
      ? `Revision ${functionalTestState.reference.revisionId}`
      : 'Reference revision not set';
    return `${revisionDetails} | ${functionalTestProgress.completed} of ${functionalTestProgress.total} items checked`;
  }, [functionalTestProgress.completed, functionalTestProgress.total, functionalTestState.reference, functionalTestState.selectedType]);

  const progress = useMemo(() => {
    const activeSteps = steps.filter(step => !step.metadata.deactivated);
    const total = activeSteps.length;
    const completed = activeSteps.filter(step => step.status === 'COMPLETE').length;
    return { total, completed };
  }, [steps]);
  const hasGuidedSteps = useMemo(
    () =>
      steps.some(step => {
        const details = computeStepStatusDetails(step);
        return details.verificationTotal > 0 || details.repairTotal > 0;
      }),
    [steps]
  );
  const deviceExpertModelId = workspace?.analysis?.modelId || null;
  const deviceExpertParentOptions = useMemo<DeviceExpertParentOption[]>(
    () =>
      steps
        .map(step => {
          const details = computeStepStatusDetails(step);
          return {
            id: step.id,
            sequence: step.sequence,
            label: step.label,
            verificationItems: details.verificationItems,
            repairItems: details.repairItems
          };
        })
        .filter(option => option.verificationItems.length > 0 || option.repairItems.length > 0),
    [steps]
  );
  const selectedDeviceExpertParent = useMemo(
    () => deviceExpertParentOptions.find(option => option.id === deviceExpertStepId) || null,
    [deviceExpertParentOptions, deviceExpertStepId]
  );
  const deviceExpertContextOptions = useMemo<DeviceExpertContextOption[]>(
    () =>
      selectedDeviceExpertParent
        ? [
            ...buildDeviceExpertContextOptions(
              selectedDeviceExpertParent.verificationItems,
              selectedDeviceExpertParent.id,
              'VERIFICATION'
            ),
            ...buildDeviceExpertContextOptions(
              selectedDeviceExpertParent.repairItems,
              selectedDeviceExpertParent.id,
              'REPAIR'
            )
          ]
        : [],
    [selectedDeviceExpertParent]
  );
  const selectedDeviceExpertContext = useMemo(
    () => deviceExpertContextOptions.find(option => option.key === deviceExpertContextKey) || null,
    [deviceExpertContextKey, deviceExpertContextOptions]
  );
  const statusLabel = useMemo(
    () => STATUS_LABELS[taskStatus] || taskStatus.replace(/_/g, ' '),
    [taskStatus]
  );
  const statusChipStyle = useMemo(
    () => ({
      ...pageStyles.statusChip,
      ...(STATUS_CHIP_VARIANTS[taskStatus] || {})
    }),
    [taskStatus]
  );
  const dialogCopy = useMemo(() => {
    if (!statusDialog) return null;
    switch (statusDialog.mode) {
      case 'COMPLETE':
        return {
          title: 'Complete repair',
          description: statusDialog.requireReason
            ? 'Explain why you are closing the repair before every probable cause is marked complete.'
            : 'Confirm that the repair has been completed.'
        };
      case 'ON_HOLD':
        return {
          title: 'Place repair on hold',
          description:
            'Leave a quick note so the next technician understands why work is paused.'
        };
      case 'REOPEN':
        return {
          title: 'Re-open repair',
          description:
            'Add an optional note describing why the repair is being reopened.'
        };
      default:
        return null;
    }
  }, [statusDialog]);

  const applyWorkspace = useCallback(
    (
      payload: WorkspacePayload,
      options: { savedAt?: string | null; statusReason?: string } = {}
    ) => {
      setWorkspace(payload);

      const mappedSteps = (payload.steps || []).map(step => {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('repairTask:load step', step.id, {
            verificationCompleted: (step.metadata as StepMetadata | undefined)?.verificationCompleted,
            repairCompleted: (step.metadata as StepMetadata | undefined)?.repairCompleted
          });
        }
        return normalizeRepairStepForState(step);
      });

      setSteps(mappedSteps);

      setOverallStatus(payload.task?.status || 'IN_PROGRESS');

      const runMetadata = (payload.run?.metadata ?? {}) as Record<string, unknown>;
      const timeFromRun = normalizeTimeMinutes(runMetadata.totalTimeMinutes);
      const normalizedTimeTracking = normalizeRepairTimeTrackingState(
        runMetadata.timeTracking,
        payload.task?.status || 'IN_PROGRESS',
        timeFromRun
      );
      const displayMinutes =
        timeFromRun ?? roundMinutesToQuarterHour(normalizedTimeTracking.accumulatedMinutes);
      setTimeTrackingState(normalizedTimeTracking);
      setTotalTimeBaselineMinutes(displayMinutes);
      setTotalTimeManualOverride(false);
      setTotalTimeHours(formatHoursFromMinutes(displayMinutes));
      const rawReason =
        typeof runMetadata.statusReason === 'string' ? runMetadata.statusReason : undefined;
      const resolvedReason =
        rawReason !== undefined ? rawReason : options.statusReason ?? '';
      setStatusReason(resolvedReason);
      const normalizedFunctionalTest = normalizeFunctionalTestState(runMetadata.finalFunctionalTest);
      setFunctionalTestState(normalizedFunctionalTest);
      setFunctionalTestDirty(false);
      setFunctionalTestCollapsed(false);
      setRepairStepsCollapsed(Boolean(normalizedFunctionalTest.selectedType));
      setFunctionalTestReferenceCache(
        normalizedFunctionalTest.selectedType && normalizedFunctionalTest.reference
          ? {
              [normalizedFunctionalTest.selectedType]: normalizedFunctionalTest.reference
            }
          : {}
      );
      const rawSummary =
        typeof runMetadata.reportSummary === 'string' ? runMetadata.reportSummary.trim() : '';
      setReportSummary(rawSummary || null);
      const rawSummaryTimestamp =
        typeof runMetadata.reportGeneratedAt === 'string' ? runMetadata.reportGeneratedAt : null;
      setReportGeneratedAt(rawSummaryTimestamp);
      const isCustomFlag =
        typeof runMetadata.reportIsCustom === 'boolean' ? runMetadata.reportIsCustom : false;
      setReportIsCustom(isCustomFlag);
      setReportEditing(false);
      setReportDraft('');
      setReportEditError(null);
      setReportSaving(false);
      setReportError(null);
      setReportLoading(false);
      setServiceReportModalOpen(false);
      setServiceReportDraft(null);
      setServiceReportLoading(false);
      setServiceReportSaving(false);
      setServiceReportSending(false);
      setServiceReportError(null);
      setServiceReportMode('create');
      setFeedbackModal(null);
      setFeedbackForm(createDocumentationFeedbackDefaults());
      setFeedbackScope('CAUSE');
      setFeedbackItemId(null);
      setFeedbackSubmitting(false);
      setFeedbackError(null);
      setImageFlagTarget(null);
      setImageFlagNotes('');
      setImageFlagSubmitting(false);
      setDeviceExpertError(null);

      setRunNotes(payload.run?.notes || '');

      const taskUpdatedAt = (payload.task as Record<string, unknown> | null | undefined)?.updatedAt;
      const runUpdatedAt = (payload.run as Record<string, unknown> | null | undefined)?.updatedAt;
      const savedTimestamp =
        options.savedAt ??
        (typeof runMetadata.lastUpdatedAt === 'string'
          ? runMetadata.lastUpdatedAt
          : typeof taskUpdatedAt === 'string'
          ? taskUpdatedAt
          : typeof runUpdatedAt === 'string'
          ? runUpdatedAt
          : null);
      setLastSavedAt(savedTimestamp);

      setDirtyStepIds([]);
      setTopLevelDirty(false);
      setHasUnsavedChanges(false);
    },
    []
  );

  const loadWorkspace = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await repairApi.get(caseId);
      const { savedAt, ...workspacePayload } = response as any;
      applyWorkspace(workspacePayload as WorkspacePayload, {
        savedAt: typeof savedAt === 'string' ? savedAt : null
      });
    } catch (err) {
      console.error('Failed to load repair workspace', err);
      setError(err instanceof Error ? err.message : 'Failed to load repair workspace');
    } finally {
      setLoading(false);
    }
  }, [applyWorkspace, caseId]);

  useEffect(() => {
    if (totalTimeManualOverride) return;
    setTotalTimeHours(suggestedTotalTimeHours);
  }, [suggestedTotalTimeHours, totalTimeManualOverride]);

  useEffect(() => {
    const runId = workspace?.run?.id || '';
    if (!runId || !caseId) {
      setTimeTrackingAnchor(null);
      return;
    }

    if (workspaceReadOnly) {
      setTimeTrackingAnchor(null);
      return;
    }

    if (timeTrackingState.phase !== 'RUNNING') {
      clearRepairTimeTrackingAnchor(runId);
      setTimeTrackingAnchor(null);
      return;
    }

    const persistedAnchor = readRepairTimeTrackingAnchor(runId);
    const baseMinutes = Math.round(Math.max(0, timeTrackingState.accumulatedMinutes) * 100) / 100;
    const canReuseAnchor =
      persistedAnchor &&
      persistedAnchor.runId === runId &&
      persistedAnchor.caseId === caseId &&
      Number.isFinite(Date.parse(persistedAnchor.startedAt)) &&
      Math.abs(persistedAnchor.baseMinutes - baseMinutes) < 0.01;
    const nextAnchor = canReuseAnchor
      ? persistedAnchor
      : {
          runId,
          caseId,
          startedAt: new Date().toISOString(),
          baseMinutes
        };
    writeRepairTimeTrackingAnchor(runId, nextAnchor);
    setTimeTrackingAnchor(nextAnchor);
    setTimeTrackingTick(Date.now());
  }, [
    caseId,
    timeTrackingState.accumulatedMinutes,
    timeTrackingState.phase,
    workspace?.run?.id,
    workspaceReadOnly
  ]);

  useEffect(() => {
    if (timeTrackingState.phase !== 'RUNNING' || !timeTrackingAnchor) {
      return undefined;
    }
    setTimeTrackingTick(Date.now());
    const intervalId = window.setInterval(() => {
      setTimeTrackingTick(Date.now());
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [timeTrackingAnchor, timeTrackingState.phase]);

  useEffect(() => {
    if (!selectedDeviceExpertParent && deviceExpertStepId) {
      setDeviceExpertStepId('');
    }
  }, [deviceExpertStepId, selectedDeviceExpertParent]);

  useEffect(() => {
    if (
      deviceExpertContextKey &&
      !deviceExpertContextOptions.some(option => option.key === deviceExpertContextKey)
    ) {
      setDeviceExpertContextKey('');
    }
  }, [deviceExpertContextKey, deviceExpertContextOptions]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const buildTimeTrackingSaveState = useCallback(
    (resolvedStatus: string) => {
      const manualMinutes = parseHoursInputToMinutes(totalTimeHours);
      const resolvedTotalTimeMinutes = manualMinutes ?? suggestedTotalTimeMinutes;
      const trackedMinutes = manualMinutes ?? currentTrackedMinutes;
      return {
        totalTimeMinutes: resolvedTotalTimeMinutes,
        timeTracking: serializeRepairTimeTrackingState({
          accumulatedMinutes: trackedMinutes,
          phase: normalizeTimeTrackingPhase(resolvedStatus, resolvedStatus),
          updatedAt: new Date().toISOString()
        })
      };
    },
    [currentTrackedMinutes, suggestedTotalTimeMinutes, totalTimeHours]
  );

  const buildSavePayload = useCallback(
    (overrides?: { status?: string; statusReason?: string }) => {
      if (!workspace?.task?.id || !workspace?.run?.id) {
        return null;
      }
      const serializeStepForSave = (step: RepairStep) => ({
        id: step.id,
        status: step.status,
        notes: step.notes,
        metadata: {
          structuredTestingSteps: Array.isArray(step.metadata.structuredTestingSteps)
            ? step.metadata.structuredTestingSteps
            : [],
          testingSteps: Array.isArray(step.metadata.testingSteps)
            ? step.metadata.testingSteps
            : [],
          structuredRepairSteps: Array.isArray(step.metadata.structuredRepairSteps)
            ? step.metadata.structuredRepairSteps
            : [],
          repairSteps: Array.isArray(step.metadata.repairSteps)
            ? step.metadata.repairSteps
            : [],
          verificationCompleted: step.metadata.verificationCompleted ?? [],
          normalizedVerificationCompleted:
            step.metadata.normalizedVerificationCompleted ?? [],
          repairCompleted: step.metadata.repairCompleted ?? [],
          normalizedRepairCompleted:
            step.metadata.normalizedRepairCompleted ?? [],
          partsUsed: normalizePartsUsed(step.metadata.partsUsed),
          captureValues: Object.entries(step.metadata.captureValues ?? {}).reduce(
            (acc, [key, value]) => {
              if (typeof key !== 'string' || !key) return acc;
              const text =
                typeof value === 'string'
                  ? value.trim()
                  : value === null || value === undefined
                  ? ''
                  : String(value).trim();
              if (text) {
                acc[key] = text;
              }
              return acc;
            },
            {} as Record<string, string>
          ),
          attachments: (step.metadata.attachments ?? []).map(attachment => ({
            id: attachment.id,
            name: attachment.name,
            mimeType: attachment.mimeType,
            size: attachment.size,
            dataUrl: attachment.dataUrl,
            addedAt: attachment.addedAt
          })),
          deactivated: step.metadata.deactivated === true,
          deactivationInfo:
            step.metadata.deactivationInfo !== undefined
              ? step.metadata.deactivationInfo
              : null
        }
      });
      const resolvedStatus = overrides?.status ?? taskStatus;
      const resolvedStatusReason = overrides?.statusReason ?? statusReason;
      const serializedFunctionalTest = serializeFunctionalTestState(functionalTestState);
      const resolvedTimeTracking = buildTimeTrackingSaveState(resolvedStatus);
      const dirtyStepSet = new Set(dirtyStepIds);
      return {
        taskId: workspace.task.id,
        runId: workspace.run.id,
        taskStatus: resolvedStatus,
        runNotes,
        runMetadata: {
          totalTimeMinutes: resolvedTimeTracking.totalTimeMinutes,
          timeTracking: resolvedTimeTracking.timeTracking,
          lastUpdatedAt: new Date().toISOString(),
          ...(resolvedStatusReason ? { statusReason: resolvedStatusReason } : {}),
          finalFunctionalTest: serializedFunctionalTest
        },
        steps: steps
          .filter(step => dirtyStepSet.has(step.id))
          .map(serializeStepForSave)
      };
    },
    [
      buildTimeTrackingSaveState,
      dirtyStepIds,
      functionalTestState,
      runNotes,
      statusReason,
      steps,
      taskStatus,
      workspace
    ]
  );

  const buildSingleStepSavePayload = useCallback(
    (stepId: string) => {
      if (!workspace?.task?.id || !workspace?.run?.id) {
        return null;
      }
      const step = steps.find(entry => entry.id === stepId);
      if (!step) return null;
      const serializedFunctionalTest = serializeFunctionalTestState(functionalTestState);
      return {
        taskId: workspace.task.id,
        runId: workspace.run.id,
        runMetadata: {
          finalFunctionalTest: serializedFunctionalTest
        },
        steps: [
          {
            id: step.id,
            status: step.status,
            notes: step.notes,
            metadata: {
              structuredTestingSteps: Array.isArray(step.metadata.structuredTestingSteps)
                ? step.metadata.structuredTestingSteps
                : [],
              testingSteps: Array.isArray(step.metadata.testingSteps)
                ? step.metadata.testingSteps
                : [],
              structuredRepairSteps: Array.isArray(step.metadata.structuredRepairSteps)
                ? step.metadata.structuredRepairSteps
                : [],
              repairSteps: Array.isArray(step.metadata.repairSteps)
                ? step.metadata.repairSteps
                : [],
              verificationCompleted: step.metadata.verificationCompleted ?? [],
              normalizedVerificationCompleted:
                step.metadata.normalizedVerificationCompleted ?? [],
              repairCompleted: step.metadata.repairCompleted ?? [],
              normalizedRepairCompleted:
                step.metadata.normalizedRepairCompleted ?? [],
              partsUsed: normalizePartsUsed(step.metadata.partsUsed),
              captureValues: Object.entries(step.metadata.captureValues ?? {}).reduce(
                (acc, [key, value]) => {
                  if (typeof key !== 'string' || !key) return acc;
                  const text =
                    typeof value === 'string'
                      ? value.trim()
                      : value === null || value === undefined
                      ? ''
                      : String(value).trim();
                  if (text) {
                    acc[key] = text;
                  }
                  return acc;
                },
                {} as Record<string, string>
              ),
              attachments: (step.metadata.attachments ?? []).map(attachment => ({
                id: attachment.id,
                name: attachment.name,
                mimeType: attachment.mimeType,
                size: attachment.size,
                dataUrl: attachment.dataUrl,
                addedAt: attachment.addedAt
              })),
              deactivated: step.metadata.deactivated === true,
              deactivationInfo:
                step.metadata.deactivationInfo !== undefined
                  ? step.metadata.deactivationInfo
                  : null
            }
          }
        ]
      };
    },
    [functionalTestState, steps, workspace]
  );

  const handleSave = useCallback(
    async (overrides?: { status?: string; statusReason?: string; suppressReport?: boolean }) => {
      if (!caseId) return false;
      if (workspaceReadOnly) return false;
      if (taskLocked && overrides?.status !== 'IN_PROGRESS') {
        setError('Repair task is completed and locked. Re-open the repair before making any changes.');
        return false;
      }
      const payload = buildSavePayload(overrides);
      if (!payload) return false;

      const reasonForSave = overrides?.statusReason ?? statusReason;

      if (process.env.NODE_ENV !== 'production') {
        console.debug(
          'repairTask:save payload',
          payload.steps.map(stepPayload => ({
            id: stepPayload.id,
            verificationCompleted: stepPayload.metadata.verificationCompleted,
            repairCompleted: stepPayload.metadata.repairCompleted
          }))
        );
      }

      setSaving(true);
      setError(null);
      try {
        const apiPayload =
          overrides?.suppressReport === true ? { ...payload, suppressReport: true } : payload;
        const response = await repairApi.save(caseId, apiPayload as any);
        const { savedAt, ...workspacePayload } = response as any;
        const resolvedSavedAt =
          typeof savedAt === 'string' ? savedAt : new Date().toISOString();
        applyWorkspace(workspacePayload as WorkspacePayload, {
          savedAt: resolvedSavedAt,
          statusReason: reasonForSave
        });
        return true;
      } catch (err) {
        console.error('Failed to save repair task', err);
        setError(err instanceof Error ? err.message : 'Failed to save repair task');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [applyWorkspace, buildSavePayload, caseId, statusReason, taskLocked, workspaceReadOnly]
  );

  const handleSaveStep = useCallback(
    async (stepId: string) => {
      if (!caseId) return;
      if (workspaceReadOnly) return;
      if (taskLocked) {
        setError('Repair task is completed and locked. Re-open the repair before making any changes.');
        return;
      }
      const payload = buildSingleStepSavePayload(stepId);
      if (!payload || saving || savingStepId) return;

      setSavingStepId(stepId);
      setError(null);
      try {
        const response = await repairApi.save(caseId, payload as any);
        const { savedAt, ...workspacePayload } = response as any;
        const refreshedWorkspace = workspacePayload as WorkspacePayload;
        const refreshedStep = (refreshedWorkspace.steps || []).find(step => step.id === stepId);

        if (refreshedStep) {
          const normalizedStep = normalizeRepairStepForState(refreshedStep);
          setSteps(prev =>
            prev.map(step => (step.id === stepId ? normalizedStep : step))
          );
        }

        setWorkspace(prev =>
          prev
            ? {
                ...prev,
                ...refreshedWorkspace,
                steps: prev.steps
              }
            : refreshedWorkspace
        );

        const resolvedSavedAt =
          typeof savedAt === 'string' ? savedAt : new Date().toISOString();
        setLastSavedAt(resolvedSavedAt);
        setDirtyStepIds(prev => prev.filter(id => id !== stepId));
      } catch (err) {
        console.error('Failed to save repair step', err);
        setError(err instanceof Error ? err.message : 'Failed to save repair step');
      } finally {
        setSavingStepId(null);
      }
    },
    [buildSingleStepSavePayload, caseId, saving, savingStepId, taskLocked, workspaceReadOnly]
  );

  const applyStatusChange = useCallback(
    async (status: 'COMPLETED' | 'ON_HOLD' | 'IN_PROGRESS', reason: string) => {
      const trimmed = reason.trim();
      await handleSave({ status, statusReason: trimmed });
    },
    [handleSave]
  );

  const handleMarkComplete = useCallback(() => {
    if (saving || workspaceReadOnly) return;
    const requiresReason = progress.total > 0 && progress.completed < progress.total;
    if (requiresReason) {
      setDialogError(null);
      setStatusDialog({
        mode: 'COMPLETE',
        reason: statusReason || '',
        requireReason: true
      });
      return;
    }
    void applyStatusChange('COMPLETED', '');
  }, [applyStatusChange, progress, saving, statusReason, workspaceReadOnly]);

  const handlePlaceOnHold = useCallback(() => {
    if (saving || workspaceReadOnly) return;
    setDialogError(null);
    setStatusDialog({
      mode: 'ON_HOLD',
      reason: statusReason || '',
      requireReason: true
    });
  }, [saving, statusReason, workspaceReadOnly]);

  const handleReopen = useCallback(() => {
    if (saving || workspaceReadOnly) return;
    setDialogError(null);
    setStatusDialog({
      mode: 'REOPEN',
      reason: '',
      requireReason: false
    });
  }, [saving, workspaceReadOnly]);

  const closeStatusDialog = useCallback(() => {
    setStatusDialog(null);
    setDialogError(null);
  }, []);

  const submitStatusDialog = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (!statusDialog) return;
    const trimmed = statusDialog.reason.trim();
    if (statusDialog.requireReason && !trimmed) {
      setDialogError('Please add a short note before continuing.');
      return;
    }
    const targetStatus =
      statusDialog.mode === 'COMPLETE'
        ? 'COMPLETED'
        : statusDialog.mode === 'ON_HOLD'
        ? 'ON_HOLD'
        : 'IN_PROGRESS';
    closeStatusDialog();
    await applyStatusChange(targetStatus, trimmed);
  }, [applyStatusChange, closeStatusDialog, statusDialog, workspaceReadOnly]);

  const handleGenerateReport = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (!caseId || reportLoading) return;
    if (taskStatus !== 'COMPLETED') {
      setReportError('Complete the repair before generating a customer summary.');
      return;
    }
    try {
      setReportError(null);
      setServiceReportError(null);
      setReportEditError(null);
      setReportEditing(false);
      setReportDraft('');
      setReportLoading(true);
      const response = await repairApi.generateReport(caseId);
      const summaryText =
        typeof response?.summary === 'string' ? response.summary.trim() : '';
      setReportSummary(summaryText || null);
      const generatedTimestamp =
        typeof response?.generatedAt === 'string' ? response.generatedAt : new Date().toISOString();
      setReportGeneratedAt(generatedTimestamp);
      const customFlag =
        typeof response?.isCustom === 'boolean' ? response.isCustom : false;
      setReportIsCustom(customFlag);
      if (typeof response?.statusReason === 'string') {
        setStatusReason(response.statusReason);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
          ? error
          : 'Failed to generate summary.';
      setReportError(message);
    } finally {
      setReportLoading(false);
    }
  }, [
    caseId,
    taskStatus,
    reportLoading,
    workspaceReadOnly
  ]);

  const closeServiceReportModal = useCallback(() => {
    if (serviceReportSaving || serviceReportSending) return;
    setServiceReportModalOpen(false);
    setServiceReportDraft(null);
    setServiceReportError(null);
    setServiceReportMode('create');
  }, [serviceReportSaving, serviceReportSending]);

  const handleViewServiceReport = useCallback(() => {
    const existingDocument = workspace?.serviceReport?.document || null;
    if (!existingDocument) {
      setServiceReportError('No saved service report is available yet.');
      return;
    }
    setServiceReportError(null);
    setServiceReportDraft(existingDocument);
    setServiceReportMode('view');
    setServiceReportModalOpen(true);
  }, [workspace]);

  const handleCreateServiceReport = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (!caseId || serviceReportLoading || serviceReportSaving || serviceReportSending) return;
    if (taskStatus !== 'COMPLETED') {
      setServiceReportError('Complete the repair before creating the service report.');
      return;
    }
    if (savedServiceReportSent) {
      setServiceReportError('This service report has already been sent and cannot be regenerated.');
      return;
    }
    if (!reportSummary || !reportSummary.trim()) {
      setServiceReportError('Generate the customer summary before creating the service report.');
      return;
    }

    try {
      setServiceReportLoading(true);
      setServiceReportError(null);
      const response = await repairApi.createServiceReportDraft(caseId);
      const draftDocument =
        response?.document && typeof response.document === 'object'
          ? (response.document as ServiceReportDocument)
          : null;
      if (!draftDocument) {
        throw new Error('Failed to build the service report preview.');
      }

      const summaryText =
        typeof response?.reportSummary === 'string' ? response.reportSummary.trim() : '';
      if (summaryText) {
        setReportSummary(summaryText || null);
      }
      setWorkspace(prev =>
        prev
          ? {
              ...prev,
              serviceReport:
                response?.existingReport && typeof response.existingReport === 'object'
                  ? (response.existingReport as ServiceReportRecord)
                  : prev.serviceReport,
              serviceReportTemplate:
                response?.template && typeof response.template === 'object'
                  ? (response.template as ReportTemplate)
                  : prev.serviceReportTemplate
            }
          : prev
      );
      setServiceReportDraft(draftDocument);
      setServiceReportMode('create');
      setServiceReportModalOpen(true);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
          ? error
          : 'Failed to create the service report preview.';
      setServiceReportError(message);
    } finally {
      setServiceReportLoading(false);
    }
  }, [
    caseId,
    reportSummary,
    savedServiceReportSent,
    serviceReportLoading,
    serviceReportSaving,
    serviceReportSending,
    taskStatus,
    workspaceReadOnly
  ]);

  const handleSaveServiceReport = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (!caseId || !serviceReportDraft || serviceReportSaving || serviceReportSending) return;

    try {
      setServiceReportSaving(true);
      setServiceReportError(null);
      const response = await repairApi.saveServiceReport(
        caseId,
        serviceReportDraft as unknown as Record<string, unknown>
      );
      const savedReport =
        response?.serviceReport && typeof response.serviceReport === 'object'
          ? (response.serviceReport as ServiceReportRecord)
          : null;
      if (!savedReport) {
        throw new Error('The service report could not be saved.');
      }
      setWorkspace(prev => (prev ? { ...prev, serviceReport: savedReport } : prev));
      setLastSavedAt(savedReport.savedAt || new Date().toISOString());
      setServiceReportDraft(savedReport.document || serviceReportDraft);
      setServiceReportMode('view');
      setServiceReportModalOpen(true);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
          ? error
          : 'Failed to save the service report.';
      setServiceReportError(message);
    } finally {
      setServiceReportSaving(false);
    }
  }, [
    caseId,
    serviceReportDraft,
    serviceReportSaving,
    serviceReportSending,
    workspaceReadOnly
  ]);

  const handleSendServiceReport = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (!caseId || serviceReportSaving || serviceReportSending) return;
    if (!workspace?.serviceReport?.id) {
      setServiceReportError('Save the service report before sending it to the customer.');
      return;
    }

    try {
      setServiceReportSending(true);
      setServiceReportError(null);
      const response = await repairApi.sendServiceReport(caseId, workspace.serviceReport.id);
      const sentReport =
        response?.serviceReport && typeof response.serviceReport === 'object'
          ? (response.serviceReport as ServiceReportRecord)
          : null;
      if (!sentReport) {
        throw new Error('The service report could not be sent.');
      }
      setWorkspace(prev => (prev ? { ...prev, serviceReport: sentReport } : prev));
      setServiceReportDraft(sentReport.document || serviceReportDraft);
      setServiceReportMode('view');
      await promptCaseCloseAfterServiceReport(caseId, {
        triggerTaskId: workspace?.task?.id || null
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
          ? error
          : 'Failed to send the service report.';
      setServiceReportError(message);
    } finally {
      setServiceReportSending(false);
    }
  }, [
    caseId,
    serviceReportDraft,
    serviceReportSaving,
    serviceReportSending,
    workspace,
    workspaceReadOnly
  ]);

  const askDeviceExpert = useCallback(async () => {
    if (!deviceExpertModelId) {
      setDeviceExpertError('Device model context is unavailable for this repair task.');
      return;
    }

    const customQuestion = deviceExpertQuestion.trim();
    const quickQuestion = selectedDeviceExpertContext
      ? `How do I carry out ${
          selectedDeviceExpertContext.section.toLowerCase()
        } step ${
          selectedDeviceExpertContext.number || selectedDeviceExpertContext.description
        } safely and efficiently?`
      : '';
    const technicianQuestion = customQuestion || quickQuestion;

    if (!technicianQuestion) {
      setDeviceExpertError('Select an internal step or enter a question.');
      return;
    }

    try {
      setDeviceExpertLoading(true);
      setDeviceExpertError(null);
      const base = await getBaseUrl();
      const token = getAuthToken();
      const condensedHistory = deviceExpertMessages.slice(-6).map(message => ({
        role: message.role,
        content: message.content
      }));
      const stepContext = selectedDeviceExpertContext
        ? `${selectedDeviceExpertContext.section} step ${
            selectedDeviceExpertContext.number || selectedDeviceExpertContext.label
          }: ${selectedDeviceExpertContext.description}`
        : null;
      const formattedQuestion = stepContext
        ? [`Focus step: ${stepContext}`, `Technician question: ${technicianQuestion}`].join('\n')
        : technicianQuestion;

      const response = await fetch(`${base}/api/knowledge-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          modelId: deviceExpertModelId,
          question: formattedQuestion,
          history: condensedHistory,
          conversationId: deviceExpertConversationId,
          includeFundamentals: false,
          includeImages: false,
          responseLanguage: 'EN'
        })
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to get Device Expert guidance.');
      }

      const now = new Date().toISOString();
      const assistantTimestamp = payload?.timestamp || now;
      setDeviceExpertMessages(prev => [
        ...prev,
        {
          id: `user-${now}`,
          role: 'user',
          content: technicianQuestion,
          timestamp: now
        },
        {
          id: `assistant-${assistantTimestamp}`,
          role: 'assistant',
          content: String(payload?.answer || '').trim(),
          timestamp: assistantTimestamp
        }
      ]);
      setDeviceExpertConversationId(payload?.conversationId || deviceExpertConversationId || null);
      setDeviceExpertQuestion('');
    } catch (err) {
      console.error('Repair task Device Expert failed', err);
      setDeviceExpertError(
        err instanceof Error ? err.message : 'Failed to get Device Expert guidance.'
      );
    } finally {
      setDeviceExpertLoading(false);
    }
  }, [
    deviceExpertConversationId,
    deviceExpertMessages,
    deviceExpertModelId,
    deviceExpertQuestion,
    selectedDeviceExpertContext
  ]);

  const handleOpenFeedback = useCallback((payload: FeedbackLaunchPayload) => {
    setFeedbackForm(createDocumentationFeedbackDefaults());
    setFeedbackScope(payload.defaultScope);
    let nextItemId: string | null = null;
    if (payload.defaultScope === 'VERIFICATION' || payload.defaultScope === 'REPAIR') {
      const preferred =
        payload.defaultItemId &&
        payload.availableItems.find(
          option => option.type === payload.defaultScope && option.id === payload.defaultItemId
        );
      if (preferred) {
        nextItemId = preferred.id;
      } else {
        const first = payload.availableItems.find(option => option.type === payload.defaultScope);
        nextItemId = first ? first.id : null;
      }
    }
    setFeedbackItemId(nextItemId);
    setFeedbackModal({
      stepId: payload.stepId,
      stepLabel: payload.stepLabel,
      causeId: payload.causeId ?? null,
      analysisId: payload.analysisId ?? null,
      summary: payload.summary ?? null,
      availableItems: payload.availableItems
    });
    setFeedbackError(null);
  }, []);

  const closeFeedbackModal = useCallback(() => {
    setFeedbackModal(null);
    setFeedbackForm(createDocumentationFeedbackDefaults());
    setFeedbackScope('CAUSE');
    setFeedbackItemId(null);
    setFeedbackSubmitting(false);
    setFeedbackError(null);
  }, []);

  const openImageFlagModal = useCallback((payload: ImageFeedbackTargetState) => {
    setImageFlagTarget(payload);
    setImageFlagNotes('');
    setError(null);
  }, []);

  const closeImageFlagModal = useCallback(() => {
    if (imageFlagSubmitting) return;
    setImageFlagTarget(null);
    setImageFlagNotes('');
  }, [imageFlagSubmitting]);

  const updateStep = useCallback(
    (
      stepId: string,
      updater: (current: RepairStep) => Partial<RepairStep> & { metadata?: StepMetadata }
    ) => {
      if (taskLocked || workspaceReadOnly) return;
      setSteps(prev => {
        return prev.map(step => {
          if (step.id !== stepId) return step;
          const updates = updater(step);
          return {
            ...step,
            ...updates,
            metadata: { ...step.metadata, ...updates.metadata }
          };
        });
      });
      setDirtyStepIds(prev => (prev.includes(stepId) ? prev : [...prev, stepId]));
    },
    [taskLocked, workspaceReadOnly]
  );

  const handleDeactivateStep = useCallback(
    (stepId: string, info: DeactivationInfo) => {
      updateStep(stepId, step => {
        const override: StepMetadata = {
          ...step.metadata,
          deactivated: true,
          deactivationInfo: {
            flaggedAt: info.flaggedAt || new Date().toISOString(),
            feedbackId: info.feedbackId ?? null,
            feedbackType: info.feedbackType ?? null,
            severity: info.severity ?? null,
            title: info.title ?? null,
            description: info.description ?? null,
            impact: info.impact ?? null,
            scope: info.scope,
            itemId: info.itemId ?? null,
            itemLabel: info.itemLabel ?? null,
            technicianId: info.technicianId ?? 'current-tech-id',
            technicianName: info.technicianName ?? 'Tech Name'
          }
        };
        const details = computeStepStatusDetails(step, override);
        return {
          status: 'PENDING',
          metadata: {
            ...override,
            normalizedVerificationCompleted: details.verificationCompletedIds,
            normalizedRepairCompleted: details.repairCompletedIds
          }
        };
      });
    },
    [updateStep]
  );

  const handleReactivateStep = useCallback(
    (stepId: string) => {
      updateStep(stepId, step => {
        const override: StepMetadata = {
          deactivated: false,
          deactivationInfo: null
        };
        const details = computeStepStatusDetails(step, override);
        return {
          metadata: override,
          status: details.status
        };
      });
    },
    [updateStep]
  );

  const handleSubmitFeedback = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (!feedbackModal) return;
    const trimmedTitle = feedbackForm.title.trim();
    const trimmedDescription = feedbackForm.description.trim();
    if (!trimmedTitle || !trimmedDescription) {
      setFeedbackError('Provide a title and detailed description.');
      return;
    }
    const modelId = workspace?.analysis?.modelId;
    if (!modelId) {
      setFeedbackError('TSFAD model not available. Refresh the analysis and try again.');
      return;
    }

    const scope = feedbackScope;
    let resolvedItemId: string | null = null;
    let resolvedItemLabel: string | null = null;
    if (scope !== 'CAUSE') {
      if (!feedbackItemId) {
        setFeedbackError('Select the specific step to flag.');
        return;
      }
      const match = feedbackModal.availableItems.find(
        item => item.type === scope && item.id === feedbackItemId
      );
      if (!match) {
        setFeedbackError('Select the specific step to flag.');
        return;
      }
      resolvedItemId = match.id;
      resolvedItemLabel = match.label;
    }

    try {
      setFeedbackSubmitting(true);
      setFeedbackError(null);
      const flaggedAt = new Date().toISOString();
      const response = await tsfadApi.submitDocumentationFeedback({
        modelId,
        caseId: workspace?.caseSummary?.id ?? null,
        feedbackType: feedbackForm.feedbackType,
        severity: feedbackForm.severity,
        title: trimmedTitle,
        description: trimmedDescription,
        impact: feedbackForm.impact.trim() || null,
        technicianId: 'current-tech-id',
        technicianName: 'Tech Name',
        metadata: {
          source: 'repair-task',
          repairStepId: feedbackModal.stepId,
          repairStepLabel: feedbackModal.stepLabel,
          causeId: feedbackModal.causeId ?? null,
          analysisId: feedbackModal.analysisId ?? null,
          scope,
          itemId: resolvedItemId,
          itemLabel: resolvedItemLabel,
          stepSummary: feedbackModal.summary ?? null
        }
      });
      const feedbackId =
        response && typeof response.feedbackId === 'string' ? response.feedbackId : null;
      handleDeactivateStep(feedbackModal.stepId, {
        flaggedAt,
        feedbackId,
        feedbackType: feedbackForm.feedbackType,
        severity: feedbackForm.severity,
        title: trimmedTitle,
        description: trimmedDescription,
        impact: feedbackForm.impact.trim() || null,
        scope,
        itemId: resolvedItemId,
        itemLabel: resolvedItemLabel,
        technicianId: 'current-tech-id',
        technicianName: 'Tech Name'
      });
      closeFeedbackModal();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
          ? error
          : 'Failed to submit documentation feedback.';
      setFeedbackError(message);
    } finally {
      setFeedbackSubmitting(false);
    }
  }, [
    closeFeedbackModal,
    feedbackForm,
    feedbackItemId,
    feedbackModal,
    feedbackScope,
    handleDeactivateStep,
    workspace,
    workspaceReadOnly
  ]);

  const handleSubmitImageFlag = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (!imageFlagTarget) return;
    const modelId = workspace?.analysis?.modelId;
    if (!modelId) {
      setError('TSFAD model not available. Refresh the analysis and try again.');
      return;
    }

    try {
      setImageFlagSubmitting(true);
      setError(null);
      const trimmedNotes = imageFlagNotes.trim();
      const descriptionParts = [
        `Repair Step: ${imageFlagTarget.stepLabel}`,
        `Image ID: ${imageFlagTarget.image.id || 'N/A'}`,
        `Image Title: ${imageFlagTarget.image.label || 'Untitled reference image'}`
      ];
      if (imageFlagTarget.summary) {
        descriptionParts.push(`Cause Summary:\n${imageFlagTarget.summary}`);
      }
      if (trimmedNotes) {
        descriptionParts.push(`Technician Notes:\n${trimmedNotes}`);
      }

      await tsfadApi.submitDocumentationFeedback({
        modelId,
        caseId: workspace?.caseSummary?.id ?? null,
        feedbackType: 'WRONG_REFERENCE_IMAGE',
        severity: 'MEDIUM',
        title: `Repair task image review - ${(
          imageFlagTarget.image.label || imageFlagTarget.stepLabel
        ).slice(0, 80)}`,
        description: descriptionParts.join('\n\n'),
        impact: null,
        technicianId: 'current-tech-id',
        technicianName: 'Tech Name',
        metadata: {
          source: 'repair-task',
          repairStepId: imageFlagTarget.stepId,
          repairStepLabel: imageFlagTarget.stepLabel,
          causeId: imageFlagTarget.causeId ?? null,
          analysisId: imageFlagTarget.analysisId ?? null,
          flaggedImage: {
            imageId: imageFlagTarget.image.id || null,
            label: imageFlagTarget.image.label || null,
            fileUrl: imageFlagTarget.image.fileUrl || null,
            linkRelation: imageFlagTarget.image.linkRelation || null,
            linkScore: imageFlagTarget.image.linkScore ?? null,
            primaryPartNumber: imageFlagTarget.image.primaryPartNumber || null,
            partNumbers: Array.isArray(imageFlagTarget.image.partNumbers)
              ? imageFlagTarget.image.partNumbers
              : [],
            components: Array.isArray(imageFlagTarget.image.components)
              ? imageFlagTarget.image.components
              : [],
            subassemblies: Array.isArray(imageFlagTarget.image.subassemblies)
              ? imageFlagTarget.image.subassemblies
              : [],
            systemPaths: Array.isArray(imageFlagTarget.image.systemPaths)
              ? imageFlagTarget.image.systemPaths
              : [],
            viewType: imageFlagTarget.image.viewType || null,
            viewTags: Array.isArray(imageFlagTarget.image.viewTags)
              ? imageFlagTarget.image.viewTags
              : []
          },
          technicianNotes: trimmedNotes || undefined
        }
      });

      closeImageFlagModal();
      await showInAppAlert('Flagged reference image for documentation feedback.', {
        title: 'Image flagged'
      });
    } catch (submitError) {
      const message =
        submitError instanceof Error
          ? submitError.message
          : 'Failed to flag this reference image. Please try again.';
      setError(message);
    } finally {
      setImageFlagSubmitting(false);
    }
  }, [
    closeImageFlagModal,
    imageFlagNotes,
    imageFlagTarget,
    workspace,
    workspaceReadOnly
  ]);

  useEffect(() => {
    if (!feedbackModal) return;
    if (feedbackScope === 'CAUSE') {
      if (feedbackItemId !== null) {
        setFeedbackItemId(null);
      }
      return;
    }
    const scoped = feedbackModal.availableItems.filter(item => item.type === feedbackScope);
    if (scoped.length === 0) {
      if (feedbackItemId !== null) {
        setFeedbackItemId(null);
      }
      return;
    }
    if (!feedbackItemId || !scoped.some(item => item.id === feedbackItemId)) {
      setFeedbackItemId(scoped[0].id);
    }
  }, [feedbackModal, feedbackItemId, feedbackScope]);

  const feedbackScopedItems = useMemo(() => {
    if (!feedbackModal) return [];
    return feedbackModal.availableItems.filter(item => item.type === feedbackScope);
  }, [feedbackModal, feedbackScope]);
  const feedbackSelectedItem =
    feedbackScopedItems.find(item => item.id === feedbackItemId) ?? null;

  const handleStartEditReport = useCallback(() => {
    if (!reportSummary || taskStatus !== 'COMPLETED') return;
    setReportDraft(reportSummary);
    setReportEditError(null);
    setReportEditing(true);
  }, [reportSummary, taskStatus]);

  const handleCancelEditReport = useCallback(() => {
    setReportDraft('');
    setReportEditError(null);
    setReportEditing(false);
  }, []);

  const handleSaveEditedReport = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (!caseId || reportSaving) return;
    if (taskStatus !== 'COMPLETED') {
      setReportEditError('Complete the repair before editing the customer summary.');
      return;
    }
    const trimmed = reportDraft.trim();
    if (!trimmed) {
      setReportEditError('Enter a summary before saving.');
      return;
    }
    try {
      setReportSaving(true);
      setReportEditError(null);
      const response = await repairApi.saveReport(caseId, trimmed);
      const summaryText =
        typeof response?.summary === 'string' ? response.summary.trim() : trimmed;
      setReportSummary(summaryText || null);
      const savedTimestamp =
        typeof response?.generatedAt === 'string'
          ? response.generatedAt
          : new Date().toISOString();
      setReportGeneratedAt(savedTimestamp);
      const customFlag =
        typeof response?.isCustom === 'boolean' ? response.isCustom : true;
      setReportIsCustom(customFlag);
      if (typeof response?.statusReason === 'string') {
        setStatusReason(response.statusReason);
      }
      setReportEditing(false);
      setReportDraft('');
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to save summary.';
      setReportEditError(message);
    } finally {
      setReportSaving(false);
    }
  }, [caseId, reportDraft, reportSaving, setStatusReason, taskStatus, workspaceReadOnly]);

  const handleToggleChecklist = useCallback(
    (stepId: string, type: ChecklistType, key: string) => {
      const targetStep = steps.find(step => step.id === stepId);
      if (!targetStep || targetStep.metadata.deactivated) {
        return;
      }
      updateStep(stepId, step => {
        const items = buildChecklist(step.metadata, type);
        const match = findChecklistItem(items, key);
        const targetId = match ? match.id : key;
        const targetValue = match ? match.value : key;

        const currentRaw =
          type === 'verification'
            ? Array.isArray(step.metadata.verificationCompleted)
              ? step.metadata.verificationCompleted
              : []
            : Array.isArray(step.metadata.repairCompleted)
            ? step.metadata.repairCompleted
            : [];

        let currentNormalized: string[] = [];
        if (type === 'verification') {
          const fromMetadata = Array.isArray(step.metadata.normalizedVerificationCompleted)
            ? step.metadata.normalizedVerificationCompleted.filter(id =>
                items.some(item => item.id === id)
              )
            : [];
          currentNormalized =
            fromMetadata.length > 0 ? fromMetadata : normalizeChecklistValues(currentRaw, items);
        } else {
          const fromMetadata = Array.isArray(step.metadata.normalizedRepairCompleted)
            ? step.metadata.normalizedRepairCompleted.filter(id =>
                items.some(item => item.id === id)
              )
            : [];
          currentNormalized =
            fromMetadata.length > 0 ? fromMetadata : normalizeChecklistValues(currentRaw, items);
        }

        const normalizedSet = new Set(currentNormalized);
        const hasEntry = normalizedSet.has(targetId);

        let nextRaw: string[] = currentRaw;
        if (hasEntry) {
          normalizedSet.delete(targetId);
          nextRaw = currentRaw.filter(value => {
            const rawMatch = findChecklistItem(items, value);
            const canonical = rawMatch ? rawMatch.id : value;
            return canonical !== targetId;
          });
        } else {
          normalizedSet.add(targetId);
          const alreadyPresent = currentRaw.some(value => {
            const rawMatch = findChecklistItem(items, value);
            const canonical = rawMatch ? rawMatch.id : value;
            return canonical === targetId;
          });
          if (!alreadyPresent) {
            nextRaw = [...currentRaw, targetValue];
          }
        }

        const nextNormalized = Array.from(normalizedSet);

        const nextMetadata =
          type === 'verification'
            ? {
                verificationCompleted: nextRaw,
                normalizedVerificationCompleted: nextNormalized
              }
            : {
                repairCompleted: nextRaw,
                normalizedRepairCompleted: nextNormalized
              };
        const details = computeStepStatusDetails(step, nextMetadata);
        return {
          metadata:
            type === 'verification'
              ? {
                  verificationCompleted: nextRaw,
                  normalizedVerificationCompleted: details.verificationCompletedIds
                }
              : {
                  repairCompleted: nextRaw,
                  normalizedRepairCompleted: details.repairCompletedIds
                },
          status: details.status
        };
      });
    },
    [steps, updateStep]
  );

  const handleSaveChecklistDraft = useCallback(
    (stepId: string, type: ChecklistType, draft: string): string | null => {
      const targetStep = steps.find(step => step.id === stepId);
      if (!targetStep) {
        return 'Repair step not found.';
      }

      updateStep(stepId, step => {
        const existingStructured =
          type === 'verification'
            ? step.metadata.structuredTestingSteps
            : step.metadata.structuredRepairSteps;
        const parsed = parseChecklistDraft(draft, existingStructured);
        const metadataOverride: StepMetadata =
          type === 'verification'
            ? {
                structuredTestingSteps: parsed.structured,
                testingSteps: parsed.flattened
              }
            : {
                structuredRepairSteps: parsed.structured,
                repairSteps: parsed.flattened
              };
        const previousItems = buildChecklist(step.metadata, type);
        const nextItems = buildChecklist({ ...step.metadata, ...metadataOverride }, type);
        const preservedCompletion = preserveChecklistCompletionAfterEdit(
          step.metadata,
          type,
          previousItems,
          nextItems
        );
        const metadataWithCompletion =
          type === 'verification'
            ? {
                ...metadataOverride,
                verificationCompleted: preservedCompletion.raw,
                normalizedVerificationCompleted: preservedCompletion.normalized
              }
            : {
                ...metadataOverride,
                repairCompleted: preservedCompletion.raw,
                normalizedRepairCompleted: preservedCompletion.normalized
              };
        const details = computeStepStatusDetails(step, metadataWithCompletion);

        return {
          metadata:
            type === 'verification'
              ? {
                  ...metadataOverride,
                  verificationCompleted: preservedCompletion.raw,
                  normalizedVerificationCompleted: details.verificationCompletedIds
                }
              : {
                  ...metadataOverride,
                  repairCompleted: preservedCompletion.raw,
                  normalizedRepairCompleted: details.repairCompletedIds
                },
          status: details.status
        };
      });

      return null;
    },
    [steps, updateStep]
  );

  const handleCaptureChange = useCallback(
    (stepId: string, itemId: string, rawValue: string) => {
      const targetStep = steps.find(step => step.id === stepId);
      if (!targetStep || targetStep.metadata.deactivated) {
        return;
      }
      updateStep(stepId, step => {
        const currentValues =
          step.metadata.captureValues && typeof step.metadata.captureValues === 'object'
            ? { ...step.metadata.captureValues }
            : {};
        const trimmed = rawValue.trim();
        if (trimmed) {
          currentValues[itemId] = trimmed;
        } else {
          delete currentValues[itemId];
        }
        return {
          metadata: {
            captureValues: currentValues
          }
        };
      });
    },
    [steps, updateStep]
  );

  const handleNotesChange = useCallback(
    (stepId: string, notes: string) => {
      updateStep(stepId, () => ({ notes }));
    },
    [updateStep]
  );

  const handleAddPart = useCallback(
    (stepId: string, part: PartUsage) => {
      const targetStep = steps.find(step => step.id === stepId);
      if (!targetStep || targetStep.metadata.deactivated) {
        return;
      }
      updateStep(stepId, step => {
        const current = Array.isArray(step.metadata.partsUsed) ? step.metadata.partsUsed : [];
        const quantity =
          part.quantity === null || part.quantity === undefined
            ? null
            : toSafeQuantity(part.quantity);
        const normalizedPart: PartUsage = {
          id: part.id || createPartUsageId(),
          partNumber: toSafeString(part.partNumber),
          description: toSafeString(part.description),
          batchNumber: toSafeString(part.batchNumber),
          quantity
        };
        const nextParts = [...current, normalizedPart];
        const details = computeStepStatusDetails(step, { partsUsed: nextParts });
        return {
          metadata: {
            partsUsed: nextParts
          },
          status: details.status
        };
      });
    },
    [steps, updateStep]
  );

  const handleRemovePart = useCallback(
    (stepId: string, partId: string) => {
      const targetStep = steps.find(step => step.id === stepId);
      if (!targetStep || targetStep.metadata.deactivated) {
        return;
      }
      updateStep(stepId, step => {
        const current = Array.isArray(step.metadata.partsUsed) ? step.metadata.partsUsed : [];
        const nextParts = current.filter(part => part.id !== partId);
        const details = computeStepStatusDetails(step, { partsUsed: nextParts });
        return {
          metadata: {
            partsUsed: nextParts
          },
          status: details.status
        };
      });
    },
    [steps, updateStep]
  );

  const handleAddAttachment = useCallback(
    (stepId: string, attachment: StepAttachment) => {
      const targetStep = steps.find(step => step.id === stepId);
      if (!targetStep || targetStep.metadata.deactivated) {
        return;
      }
      if (!attachment || typeof attachment.dataUrl !== 'string' || !attachment.dataUrl.startsWith('data:')) {
        return;
      }
      updateStep(stepId, step => {
        const current = Array.isArray(step.metadata.attachments) ? step.metadata.attachments : [];
        if (current.length >= MAX_ATTACHMENTS_PER_STEP) {
          return {};
        }
        const normalized: StepAttachment = {
          id: attachment.id || createAttachmentId(),
          name: attachment.name || 'Photo',
          mimeType: attachment.mimeType || 'image/jpeg',
          size:
            typeof attachment.size === 'number' && Number.isFinite(attachment.size) && attachment.size >= 0
              ? attachment.size
              : 0,
          dataUrl: attachment.dataUrl,
          addedAt: attachment.addedAt || new Date().toISOString()
        };
        return {
          metadata: {
            attachments: [...current, normalized]
          }
        };
      });
    },
    [steps, updateStep]
  );

  const handleRemoveAttachment = useCallback(
    (stepId: string, attachmentId: string) => {
      const targetStep = steps.find(step => step.id === stepId);
      if (!targetStep || targetStep.metadata.deactivated) {
        return;
      }
      updateStep(stepId, step => {
        const current = Array.isArray(step.metadata.attachments) ? step.metadata.attachments : [];
        const next = current.filter(item => item.id !== attachmentId);
        return {
          metadata: {
            attachments: next
          }
        };
      });
    },
    [steps, updateStep]
  );

  const loadFunctionalTestReference = useCallback(
    async (type: FunctionalTestType): Promise<FunctionalTestReference | null | undefined> => {
      if (!caseId) return null;
      if (Object.prototype.hasOwnProperty.call(functionalTestReferenceCache, type)) {
        return functionalTestReferenceCache[type] ?? null;
      }
      setFunctionalTestReferenceLoading(type);
      setError(null);
      try {
        const response = (await repairApi.getFunctionalTestReference(
          caseId,
          type
        )) as FunctionalTestReferenceResponse;
        const normalizedReference = normalizeFunctionalTestReference(response?.reference ?? null);
        setFunctionalTestReferenceCache(prev => ({
          ...prev,
          [type]: normalizedReference
        }));
        return normalizedReference;
      } catch (err) {
        console.error('Failed to load final functional test reference', err);
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load final functional test reference'
        );
        return undefined;
      } finally {
        setFunctionalTestReferenceLoading(current => (current === type ? null : current));
      }
    },
    [caseId, functionalTestReferenceCache]
  );

  const handleSelectFunctionalTest = useCallback(
    async (type: FunctionalTestType) => {
      if (workspaceReadOnly) return;
      if (taskLocked) {
        setError('Repair task is completed and locked. Re-open the repair before changing the final test.');
        return;
      }

      const currentType = functionalTestState.selectedType;
      const hasExistingProgress =
        Boolean(currentType) &&
        (functionalTestState.completedItemIds.length > 0 ||
          functionalTestState.notes.trim().length > 0);

      if (currentType && currentType !== type && hasExistingProgress) {
        const confirmed = await showInAppConfirm(
          'Switch the selected final functional test? Current final-test progress will be replaced.',
          {
            title: 'Switch final functional test?',
            confirmLabel: 'Switch',
            tone: 'danger'
          }
        );
        if (!confirmed) return;
      }

      if (currentType === type && functionalTestState.reference) {
        setRepairStepsCollapsed(true);
        setFunctionalTestCollapsed(false);
        return;
      }

      const reference = await loadFunctionalTestReference(type);
      if (reference === undefined) {
        return;
      }
      if (!reference) {
        showInAppAlert(FUNCTIONAL_TEST_TYPE_COPY[type].unavailableMessage, {
          title: 'Final functional test unavailable'
        });
        return;
      }

      setFunctionalTestState({
        selectedType: type,
        reference,
        completedItemIds: [],
        normalizedCompletedItemIds: [],
        notes: '',
        status: 'PENDING',
        savedAt: null
      });
      setFunctionalTestDirty(true);
      setRepairStepsCollapsed(true);
      setFunctionalTestCollapsed(false);
      setError(null);
    },
    [
      functionalTestState.completedItemIds.length,
      functionalTestState.notes,
      functionalTestState.reference,
      functionalTestState.selectedType,
      loadFunctionalTestReference,
      taskLocked,
      workspaceReadOnly
    ]
  );

  const handleToggleFunctionalTestItem = useCallback(
    (itemId: string) => {
      if (taskLocked || workspaceReadOnly) return;
      setFunctionalTestState(prev => {
        if (!prev.reference || !prev.selectedType) return prev;
        const nextCompleted = new Set(prev.normalizedCompletedItemIds);
        if (nextCompleted.has(itemId)) {
          nextCompleted.delete(itemId);
        } else {
          nextCompleted.add(itemId);
        }
        const normalizedCompletedItemIds = Array.from(nextCompleted);
        return {
          ...prev,
          completedItemIds: normalizedCompletedItemIds,
          normalizedCompletedItemIds,
          status: deriveFunctionalTestStatus(prev.reference, normalizedCompletedItemIds, prev.notes)
        };
      });
      setFunctionalTestDirty(true);
    },
    [taskLocked, workspaceReadOnly]
  );

  const handleFunctionalTestNotesChange = useCallback(
    (value: string) => {
      if (taskLocked || workspaceReadOnly) return;
      setFunctionalTestState(prev =>
        prev.reference
          ? {
              ...prev,
              notes: value,
              status: deriveFunctionalTestStatus(prev.reference, prev.normalizedCompletedItemIds, value)
            }
          : prev
      );
      setFunctionalTestDirty(true);
    },
    [taskLocked, workspaceReadOnly]
  );

  const handleClearFunctionalTest = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (taskLocked) {
      setError('Repair task is completed and locked. Re-open the repair before changing the final test.');
      return;
    }
    if (!functionalTestState.selectedType) return;

    const hasProgress =
      functionalTestState.completedItemIds.length > 0 || functionalTestState.notes.trim().length > 0;
    if (hasProgress) {
      const confirmed = await showInAppConfirm(
        'Remove the selected final functional test and discard its progress?',
        {
          title: 'Remove final functional test?',
          confirmLabel: 'Remove',
          tone: 'danger'
        }
      );
      if (!confirmed) return;
    }

    setFunctionalTestState(EMPTY_FUNCTIONAL_TEST_STATE);
    setFunctionalTestDirty(true);
    setFunctionalTestCollapsed(false);
    setRepairStepsCollapsed(false);
  }, [
    functionalTestState.completedItemIds.length,
    functionalTestState.notes,
    functionalTestState.selectedType,
    taskLocked,
    workspaceReadOnly
  ]);

  const handleSaveFunctionalTest = useCallback(async () => {
    if (workspaceReadOnly || !functionalTestState.selectedType || !functionalTestDirty || saving) return;
    await handleSave();
  }, [functionalTestDirty, functionalTestState.selectedType, handleSave, saving, workspaceReadOnly]);

  const handleRunNotesChange = useCallback(
    (notes: string) => {
      if (taskLocked || workspaceReadOnly) return;
      setRunNotes(notes);
      setTopLevelDirty(true);
    },
    [taskLocked, workspaceReadOnly]
  );

  const handleTotalTimeChange = useCallback(
    (value: string) => {
      if (taskLocked || workspaceReadOnly) return;
      if (/^\d*\.?\d*$/.test(value)) {
        setTotalTimeHours(value);
        const parsedMinutes = parseHoursInputToMinutes(value);
        const matchesSuggestion =
          parsedMinutes !== null && Math.abs(parsedMinutes - suggestedTotalTimeMinutes) < 0.01;
        setTotalTimeManualOverride(value.trim() === '' ? true : !matchesSuggestion);
        setTopLevelDirty(true);
      }
    },
    [suggestedTotalTimeMinutes, taskLocked, workspaceReadOnly]
  );

  const refreshFromAnalysis = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (taskLocked) {
      setError('Repair task is completed and locked. Re-open the repair before refreshing from TSFAD.');
      return;
    }
    if (!caseId) return;
    if (hasUnsavedChanges) {
      const confirmed = await showInAppConfirm(
        'You have unsaved changes. Continue and discard them?',
        {
          title: 'Discard unsaved changes?',
          confirmLabel: 'Discard',
          tone: 'danger'
        }
      );
      if (!confirmed) return;
    }
    setRefreshing(true);
    setError(null);
    try {
      const response = await repairApi.refresh(caseId);
      const { refreshedAt, ...workspacePayload } = response as any;
      applyWorkspace(workspacePayload as WorkspacePayload, {
        savedAt: typeof refreshedAt === 'string' ? refreshedAt : null
      });
    } catch (err) {
      console.error('Failed to refresh repair task', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh repair task');
    } finally {
      setRefreshing(false);
    }
  }, [applyWorkspace, caseId, hasUnsavedChanges, taskLocked, workspaceReadOnly]);

  const clearProgress = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (taskLocked) {
      setError('Repair task is completed and locked. Re-open the repair before clearing progress.');
      return;
    }
    if (!caseId) return;
    if (hasUnsavedChanges) {
      const confirmed = await showInAppConfirm(
        'You have unsaved changes. Clear progress anyway?',
        {
          title: 'Clear progress?',
          confirmLabel: 'Clear',
          tone: 'danger'
        }
      );
      if (!confirmed) return;
    }
    setClearing(true);
    setError(null);
    try {
      const currentRunId = workspace?.run?.id || '';
      const response = await repairApi.clear(caseId);
      const { clearedAt, ...workspacePayload } = response as any;
      if (currentRunId) {
        clearRepairTimeTrackingAnchor(currentRunId);
      }
      if (typeof workspacePayload?.run?.id === 'string' && workspacePayload.run.id.trim()) {
        clearRepairTimeTrackingAnchor(workspacePayload.run.id);
      }
      applyWorkspace(workspacePayload as WorkspacePayload, {
        savedAt: typeof clearedAt === 'string' ? clearedAt : null
      });
    } catch (err) {
      console.error('Failed to clear repair task', err);
      setError(err instanceof Error ? err.message : 'Failed to clear repair task');
    } finally {
      setClearing(false);
    }
  }, [applyWorkspace, caseId, hasUnsavedChanges, taskLocked, workspace, workspaceReadOnly]);

  const handleAddManualEntryStep = useCallback(async () => {
    if (workspaceReadOnly) return;
    if (taskLocked) {
      setError('Repair task is completed and locked. Re-open the repair before adding steps.');
      return;
    }
    if (!caseId || !workspace?.run?.id) {
      setError('Repair run is not ready yet. Refresh the workspace and try again.');
      return;
    }

    setManualStepSaving(true);
    setError(null);
    try {
      const response = await repairApi.createStep(caseId, {
        runId: workspace.run.id,
        label: 'Manual repair entry',
        summary: '',
        verificationSteps: [],
        repairSteps: []
      });
      const { createdAt, ...workspacePayload } = response as any;
      applyWorkspace(workspacePayload as WorkspacePayload, {
        savedAt: typeof createdAt === 'string' ? createdAt : new Date().toISOString()
      });
    } catch (err) {
      console.error('Failed to create manual repair step', err);
      setError(err instanceof Error ? err.message : 'Failed to add manual repair entry');
    } finally {
      setManualStepSaving(false);
    }
  }, [applyWorkspace, caseId, taskLocked, workspace, workspaceReadOnly]);

  const caseSummary = workspace?.caseSummary;
  const analysis = workspace?.analysis;
  const selectedFunctionalTestCopy = functionalTestState.selectedType
    ? FUNCTIONAL_TEST_TYPE_COPY[functionalTestState.selectedType]
    : null;
  const functionalTestStatusChipStyle = useMemo<React.CSSProperties>(() => {
    if (functionalTestState.status === 'COMPLETE') {
      return {
        ...pageStyles.functionalTestStatusChip,
        background: BRAND_SUCCESS_SOFT,
        color: BRAND_SUCCESS_TEXT,
        border: `1px solid ${BRAND_SUCCESS_BORDER}`
      };
    }
    if (functionalTestState.status === 'IN_PROGRESS') {
      return {
        ...pageStyles.functionalTestStatusChip,
        background: BRAND_ACCENT_SOFT,
        color: BRAND_ACCENT_TEXT,
        border: `1px solid ${BRAND_ACCENT}`
      };
    }
    return {
      ...pageStyles.functionalTestStatusChip,
      background: BRAND_TEXT_SOFT,
      color: BRAND_TEXT,
      border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`
    };
  }, [functionalTestState.status]);
  const caseNumber =
    caseSummary?.caseCode ||
    caseSummary?.caseNumber ||
    caseSummary?.id ||
    'Case';
  const problemDescription =
    (caseSummary?.problemDescription && caseSummary.problemDescription.trim()) ||
    (analysis?.summary && analysis.summary.trim()) ||
    'No problem description provided.';
  const activeDeviceSlaAssignment = useMemo(
    () =>
      getCurrentDeviceSlaAssignment(
        caseSummary?.device?.slaAssignments || [],
        caseSummary?.openedAt || caseSummary?.createdAt || null
      ),
    [caseSummary?.createdAt, caseSummary?.device?.slaAssignments, caseSummary?.openedAt]
  );
  const slaHeroSummary = useMemo(() => {
    if (activeDeviceSlaAssignment?.slaTemplate) {
      const branding =
        String(
          activeDeviceSlaAssignment.slaTemplate.brandingName ||
            activeDeviceSlaAssignment.slaTemplate.name ||
            'SLA'
        ).trim() || 'SLA';
      const tier = String(activeDeviceSlaAssignment.slaTemplate.tierBrandingName || '').trim();
      const slaType = String(activeDeviceSlaAssignment.slaTemplate.slaType || '').trim();
      const periodLabel = getDeviceSlaPeriodLabel(activeDeviceSlaAssignment);
      const noteParts = [tier, slaType].filter(Boolean);
      if (periodLabel && periodLabel !== 'Not recorded') {
        noteParts.push(`term ${periodLabel}`);
      }
      return {
        value: branding,
        note: noteParts.length > 0 ? noteParts.join(' | ') : 'Active device SLA'
      };
    }

    return {
      value: 'No active SLA',
      note: 'This repair is not currently linked to an active device SLA.'
    };
  }, [activeDeviceSlaAssignment]);

  return (
    <div style={pageStyles.page}>
      <div style={pageStyles.topBar}>
        <button style={pageStyles.backLink} onClick={() => navigate('/tech')}>
          Back to Cases
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={pageStyles.saveMeta}>
            {savingStepId
              ? 'Saving step...'
              : saving
              ? 'Saving...'
              : hasUnsavedChanges
              ? 'Unsaved changes'
              : lastSavedAt
              ? `Last saved ${formatDateTime(lastSavedAt)}`
              : 'Not saved yet'}
          </div>
          <button
            style={{
              ...pageStyles.buttonPrimary,
              opacity:
                savingStepId ||
                saving ||
                taskLocked ||
                workspaceReadOnly ||
                !hasUnsavedChanges ||
                !workspace?.task?.id ||
                !workspace?.run?.id
                  ? 0.65
                  : 1,
              cursor:
                savingStepId ||
                saving ||
                taskLocked ||
                workspaceReadOnly ||
                !hasUnsavedChanges ||
                !workspace?.task?.id ||
                !workspace?.run?.id
                  ? 'not-allowed'
                  : 'pointer'
            }}
            onClick={() => handleSave()}
            disabled={
              Boolean(savingStepId) ||
              saving ||
              taskLocked ||
              workspaceReadOnly ||
              !hasUnsavedChanges ||
              !workspace?.task?.id ||
              !workspace?.run?.id
            }
          >
            {saving ? 'Saving...' : 'Save repair'}
          </button>
        </div>
      </div>

      {workspaceReadOnly && (
        <div
          style={{
            background: BRAND_TEXT_SOFT,
            color: BRAND_TEXT,
            borderRadius: '1rem',
            border: `1px solid ${BRAND_TEXT_SOFT_BORDER}`,
            padding: '0.9rem 1.2rem',
            fontWeight: 600
          }}
        >
          Read-only view. Only the assigned technician can edit this task
          {assignedLabel ? ` (assigned to ${assignedLabel}).` : '.'}
        </div>
      )}

      {error && (
        <div
          style={{
            background: BRAND_DANGER_SOFT,
            color: BRAND_DANGER_TEXT,
            borderRadius: '1rem',
            border: `1px solid ${BRAND_DANGER_BORDER}`,
            padding: '0.9rem 1.2rem',
            fontWeight: 600
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ ...pageStyles.section, alignItems: 'center' }}>
          <div style={pageStyles.muted}>Loading repair workspace...</div>
        </div>
      ) : !workspace ? (
        <div style={{ ...pageStyles.section, alignItems: 'center' }}>
          <div style={pageStyles.muted}>No repair data available.</div>
        </div>
      ) : (
        <>
          <fieldset
            disabled={workspaceReadOnly}
            style={{
              border: 'none',
              margin: 0,
              padding: 0,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '1.75rem'
            }}
          >
          <div style={pageStyles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: BRAND_TEXT }}>
                  Repair For Reliability (R4R)
                </div>
                <div
                  style={{
                    fontWeight: 600,
                    color: BRAND_TEXT,
                    maxWidth: '52rem',
                    lineHeight: 1.4
                  }}
                >
                  {problemDescription}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  style={{
                    ...pageStyles.buttonPrimary,
                    opacity: refreshing || taskLocked || workspaceReadOnly ? 0.75 : 1
                  }}
                  onClick={refreshFromAnalysis}
                  disabled={refreshing || taskLocked || workspaceReadOnly}
                >
                  {refreshing ? 'Refreshing...' : 'Refresh from TSFAD'}
                </button>
                <button
                  style={{
                    ...pageStyles.buttonSecondary,
                    opacity: clearing || taskLocked || workspaceReadOnly ? 0.75 : 1
                  }}
                  onClick={clearProgress}
                  disabled={clearing || taskLocked || workspaceReadOnly}
                >
                  {clearing ? 'Clearing...' : 'Clear local progress'}
                </button>
              </div>
            </div>

            <div style={pageStyles.grid}>
              <div>
                <div style={pageStyles.fieldLabel}>Case</div>
                <div style={pageStyles.fieldValue}>{caseNumber}</div>
              </div>
              <div>
                <div style={pageStyles.fieldLabel}>Customer</div>
                <div style={pageStyles.fieldValue}>
                  {caseSummary?.customer?.name || 'Unknown customer'}
                </div>
              </div>
              <div>
                <div style={pageStyles.fieldLabel}>Device</div>
                <div style={pageStyles.fieldValue}>
                  {caseSummary?.device?.modelNumber
                    ? `${caseSummary.device.modelNumber}${caseSummary.device.serialNumber ? ` (Serial ${caseSummary.device.serialNumber})` : ''}`
                    : 'Unspecified device'}
                </div>
              </div>
              <div>
                <div style={pageStyles.fieldLabel}>Location</div>
                <div style={pageStyles.fieldValue}>
                  {caseSummary?.location?.name || 'Unspecified location'}
                </div>
              </div>
              <div>
                <div style={pageStyles.fieldLabel}>Contact</div>
                <div style={pageStyles.fieldValue}>
                  {caseSummary?.contact?.name || 'Not provided'}
                </div>
              </div>
              <div>
                <div style={pageStyles.fieldLabel}>SLA</div>
                <div style={pageStyles.fieldValue}>{slaHeroSummary.value}</div>
                <div style={pageStyles.statusMetaSubnote}>{slaHeroSummary.note}</div>
              </div>
            </div>
          </div>

          <div style={pageStyles.section}>
            <div style={pageStyles.statusHeader}>
              <div style={pageStyles.statusLeft}>
                <div>
                  <div style={pageStyles.fieldLabel}>Status</div>
                  <div style={pageStyles.statusChipRow}>
                    <span style={statusChipStyle}>{statusLabel}</span>
                  </div>
                  {statusReason && (
                    <div style={pageStyles.statusNote}>Status note: {statusReason}</div>
                  )}
                </div>
                <div style={pageStyles.statusMetaRow}>
                  <div style={pageStyles.statusMetaItem}>
                    <div style={pageStyles.fieldLabel}>Causes investigated</div>
                    <div style={pageStyles.fieldValue}>
                      {progress.completed} of {progress.total}
                    </div>
                  </div>
                  <div style={pageStyles.statusMetaItem}>
                    <div style={pageStyles.fieldLabel}>Final functional test</div>
                    <div style={pageStyles.fieldValue}>{functionalTestSummaryLabel}</div>
                    <div style={pageStyles.statusMetaSubnote}>{functionalTestSummaryNote}</div>
                  </div>
                  <div style={{ ...pageStyles.statusMetaItem, maxWidth: '14rem' }}>
                    <div style={pageStyles.fieldLabel}>Total time (hours)</div>
                    <input
                      style={{
                        ...pageStyles.textInput,
                        ...(taskLocked || workspaceReadOnly ? pageStyles.textInputDisabled : {}),
                        background: '#ffffff'
                      }}
                      value={totalTimeHours}
                      onChange={event => handleTotalTimeChange(event.target.value)}
                      placeholder={suggestedTotalTimeHours || 'e.g. 2.5'}
                      disabled={taskLocked || workspaceReadOnly}
                    />
                    <div style={pageStyles.statusMetaSubnote}>{totalTimeInputNote}</div>
                  </div>
              </div>
            </div>
            <div style={pageStyles.statusRight}>
              <div style={pageStyles.fieldLabel}>Actions</div>
              <div style={pageStyles.primaryActions}>
                <button
                  type="button"
                  style={{
                    ...pageStyles.statusActionComplete,
                    ...((taskStatus === 'COMPLETED' || saving || workspaceReadOnly)
                      ? pageStyles.statusActionDisabled
                      : {})
                  }}
                  onClick={handleMarkComplete}
                  disabled={taskStatus === 'COMPLETED' || saving || workspaceReadOnly}
                >
                  Mark complete
                </button>
                <button
                  type="button"
                  style={{
                    ...pageStyles.statusActionHold,
                    ...((taskStatus === 'ON_HOLD' ||
                    taskStatus === 'COMPLETED' ||
                    saving ||
                    workspaceReadOnly)
                      ? pageStyles.statusActionDisabled
                      : {})
                  }}
                  onClick={handlePlaceOnHold}
                  disabled={
                    taskStatus === 'ON_HOLD' ||
                    taskStatus === 'COMPLETED' ||
                    saving ||
                    workspaceReadOnly
                  }
                >
                  Place on hold
                </button>
                <button
                  type="button"
                  style={{
                    ...pageStyles.statusActionReopen,
                    ...((taskStatus === 'IN_PROGRESS' || saving || workspaceReadOnly)
                      ? pageStyles.statusActionDisabled
                      : {})
                  }}
                  onClick={handleReopen}
                  disabled={taskStatus === 'IN_PROGRESS' || saving || workspaceReadOnly}
                >
                  Re-open
                </button>
              </div>
              <button
                type="button"
                style={{
                  ...pageStyles.reportButton,
                  ...((taskStatus !== 'COMPLETED' || reportLoading || saving || workspaceReadOnly)
                    ? pageStyles.reportButtonDisabled
                    : {})
                }}
                onClick={handleGenerateReport}
                disabled={taskStatus !== 'COMPLETED' || reportLoading || saving || workspaceReadOnly}
              >
                {reportLoading ? 'Generating...' : reportSummary ? 'Regenerate summary' : 'Generate summary'}
              </button>
              {reportSummary && reportGeneratedAt && (
                <div style={pageStyles.reportInfo}>
                  {reportIsCustom ? 'Last updated' : 'Last generated'}{' '}
                  {formatDateTime(reportGeneratedAt) || reportGeneratedAt}
                </div>
              )}
            </div>
          </div>
          <div style={{ width: '100%' }}>
            <div style={pageStyles.fieldLabel}>Task notes</div>
            <textarea
                style={{ ...pageStyles.textarea, background: '#ffffff' }}
                value={runNotes}
                onChange={event => handleRunNotesChange(event.target.value)}
                placeholder="Overall notes, risks, follow-ups..."
                disabled={taskLocked || workspaceReadOnly}
              />
            </div>
          </div>

          {reportSummary && (
            <div style={pageStyles.section}>
              <div style={pageStyles.reportHeader}>
                <div>
                  <div style={pageStyles.reportTitle}>Customer summary</div>
                  {reportGeneratedAt && (
                    <div style={pageStyles.reportMeta}>
                      {reportIsCustom ? 'Last updated' : 'Generated'}{' '}
                      {formatDateTime(reportGeneratedAt) || reportGeneratedAt}
                    </div>
                  )}
                </div>
                <div style={pageStyles.reportActions}>
                  {workspace?.serviceReport && (
                    <button
                      type="button"
                      style={pageStyles.reportEditButton}
                      onClick={handleViewServiceReport}
                    >
                      View service report
                    </button>
                  )}
                  <button
                    type="button"
                    style={{
                      ...pageStyles.reportEditButton,
                      ...(taskStatus !== 'COMPLETED' ||
                      reportSaving ||
                      serviceReportLoading ||
                      serviceReportSaving ||
                      serviceReportSending ||
                      savedServiceReportSent ||
                      workspaceReadOnly
                        ? pageStyles.reportEditButtonDisabled
                        : {})
                    }}
                    onClick={handleCreateServiceReport}
                    disabled={
                      taskStatus !== 'COMPLETED' ||
                      reportSaving ||
                      serviceReportLoading ||
                      serviceReportSaving ||
                      serviceReportSending ||
                      savedServiceReportSent ||
                      workspaceReadOnly
                    }
                  >
                    {serviceReportLoading
                      ? 'Building service report...'
                      : workspace?.serviceReport?.document
                      ? 'Regenerate service report'
                      : 'Create service report'}
                  </button>
                  {!reportEditing && (
                    <button
                      type="button"
                      style={{
                        ...pageStyles.reportEditButton,
                        ...(reportSaving || taskStatus !== 'COMPLETED' || workspaceReadOnly
                          ? pageStyles.reportEditButtonDisabled
                          : {})
                      }}
                      onClick={handleStartEditReport}
                      disabled={reportSaving || taskStatus !== 'COMPLETED' || workspaceReadOnly}
                    >
                      Edit summary
                    </button>
                  )}
                </div>
              </div>
              {workspace?.serviceReport?.savedAt && (
                <div style={pageStyles.reportMeta}>
                  Service report saved{' '}
                  {formatDateTime(workspace.serviceReport.savedAt) || workspace.serviceReport.savedAt}
                  {workspace.serviceReport.templateName
                    ? ` using ${workspace.serviceReport.templateName}`
                    : ''}
                </div>
              )}
              {workspace?.serviceReport && (
                <div style={pageStyles.reportStatusWrap}>
                  <div
                    style={{
                      ...pageStyles.reportStatusChip,
                      ...(String(workspace.serviceReport.status || '').trim().toUpperCase() === 'SENT'
                        ? pageStyles.reportStatusChipSent
                        : pageStyles.reportStatusChipPending)
                    }}
                  >
                    {String(workspace.serviceReport.status || '').trim().toUpperCase() === 'SENT'
                      ? 'Report Sent'
                      : 'Not Sent'}
                  </div>
                  <div style={pageStyles.reportMeta}>
                    {String(workspace.serviceReport.status || '').trim().toUpperCase() === 'SENT'
                      ? workspace.serviceReport.sentAt
                        ? `Sent ${formatDateTime(workspace.serviceReport.sentAt) || workspace.serviceReport.sentAt}`
                        : 'Sent date unavailable'
                      : 'Visible to the customer only after you send the report.'}
                  </div>
                </div>
              )}
              {serviceReportError && <div style={pageStyles.reportEditError}>{serviceReportError}</div>}
              {reportEditing ? (
                <>
                  <textarea
                    style={pageStyles.reportTextarea}
                    value={reportDraft}
                    onChange={event => setReportDraft(event.target.value)}
                    placeholder="Customer-facing summary..."
                    disabled={reportSaving || workspaceReadOnly}
                  />
                  {reportEditError && <div style={pageStyles.reportEditError}>{reportEditError}</div>}
                  <div style={pageStyles.reportEditFooter}>
                    <button
                      type="button"
                      style={{
                        ...pageStyles.reportEditSecondary,
                        ...(reportSaving || workspaceReadOnly
                          ? pageStyles.reportEditButtonDisabled
                          : {})
                      }}
                      onClick={handleCancelEditReport}
                      disabled={reportSaving || workspaceReadOnly}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      style={{
                        ...pageStyles.reportEditPrimary,
                        ...(reportSaving || !reportDraft.trim() || workspaceReadOnly
                          ? pageStyles.reportEditButtonDisabled
                          : {})
                      }}
                      onClick={handleSaveEditedReport}
                      disabled={reportSaving || !reportDraft.trim() || workspaceReadOnly}
                    >
                      {reportSaving ? 'Saving...' : 'Save changes'}
                    </button>
                  </div>
                </>
              ) : (
                <div style={pageStyles.reportBody}>{reportSummary}</div>
              )}
            </div>
          )}

          <div style={pageStyles.section}>
            <div style={pageStyles.expertSection}>
              <div style={pageStyles.expertHeader}>
                <div style={pageStyles.expertHeaderCopy}>
                  <div style={pageStyles.expertTitle}>Device Expert</div>
                  <div style={pageStyles.expertSub}>
                    Ask about a guided repair step for contextual help, or ask an open device question.
                  </div>
                </div>
                <button
                  type="button"
                  style={pageStyles.buttonSecondary}
                  onClick={() => setDeviceExpertOpen(prev => !prev)}
                >
                  {deviceExpertOpen ? 'Hide' : 'Show'}
                </button>
              </div>
              {deviceExpertOpen && (
                <>
                  <div style={pageStyles.expertGrid}>
                    <div style={pageStyles.expertField}>
                      <label style={pageStyles.expertLabel}>Action plan step</label>
                      <select
                        style={pageStyles.expertSelect}
                        value={deviceExpertStepId}
                        onChange={event => {
                          setDeviceExpertStepId(event.target.value);
                          setDeviceExpertContextKey('');
                          setDeviceExpertError(null);
                        }}
                        disabled={deviceExpertLoading || deviceExpertParentOptions.length === 0}
                      >
                        <option value="">No specific action-plan step selected</option>
                        {deviceExpertParentOptions.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.sequence}. {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={pageStyles.expertField}>
                      <label style={pageStyles.expertLabel}>Internal step</label>
                      <select
                        style={pageStyles.expertSelect}
                        value={deviceExpertContextKey}
                        onChange={event => {
                          setDeviceExpertContextKey(event.target.value);
                          setDeviceExpertError(null);
                        }}
                        disabled={deviceExpertLoading || !selectedDeviceExpertParent}
                      >
                        <option value="">
                          {selectedDeviceExpertParent
                            ? 'No internal step selected'
                            : 'Select an action-plan step first'}
                        </option>
                        {selectedDeviceExpertParent?.verificationItems &&
                          selectedDeviceExpertParent.verificationItems.length > 0 && (
                            <optgroup label="Verification">
                              {buildDeviceExpertContextOptions(
                                selectedDeviceExpertParent.verificationItems,
                                selectedDeviceExpertParent.id,
                                'VERIFICATION'
                              ).map(option => (
                                <option key={option.key} value={option.key}>
                                  {option.label}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        {selectedDeviceExpertParent?.repairItems &&
                          selectedDeviceExpertParent.repairItems.length > 0 && (
                            <optgroup label="Repair">
                              {buildDeviceExpertContextOptions(
                                selectedDeviceExpertParent.repairItems,
                                selectedDeviceExpertParent.id,
                                'REPAIR'
                              ).map(option => (
                                <option key={option.key} value={option.key}>
                                  {option.label}
                                </option>
                              ))}
                            </optgroup>
                          )}
                      </select>
                    </div>
                  </div>
                  <div style={pageStyles.expertField}>
                    <label style={pageStyles.expertLabel}>Question</label>
                    <input
                      style={pageStyles.expertInput}
                      value={deviceExpertQuestion}
                      onChange={event => {
                        setDeviceExpertQuestion(event.target.value);
                        setDeviceExpertError(null);
                      }}
                      placeholder="Ask a repair question, or leave blank to get more detail on the selected internal step."
                      disabled={deviceExpertLoading || !deviceExpertModelId}
                    />
                    <div style={pageStyles.expertHint}>
                      {deviceExpertModelId
                        ? 'Choosing an internal step adds that step as context. With no step selected, your question is treated as an open Device Expert ask.'
                        : 'This repair task has no device-model context yet, so Device Expert is unavailable.'}
                    </div>
                  </div>
                  <div style={pageStyles.expertActions}>
                    <button
                      type="button"
                      style={{
                        ...pageStyles.buttonPrimary,
                        opacity:
                          deviceExpertLoading ||
                          !deviceExpertModelId ||
                          (!deviceExpertQuestion.trim() && !selectedDeviceExpertContext)
                            ? 0.65
                            : 1,
                        cursor:
                          deviceExpertLoading ||
                          !deviceExpertModelId ||
                          (!deviceExpertQuestion.trim() && !selectedDeviceExpertContext)
                            ? 'not-allowed'
                            : 'pointer'
                      }}
                      onClick={askDeviceExpert}
                      disabled={
                        deviceExpertLoading ||
                        !deviceExpertModelId ||
                        (!deviceExpertQuestion.trim() && !selectedDeviceExpertContext)
                      }
                    >
                      {deviceExpertLoading ? 'Asking...' : 'Ask Device Expert'}
                    </button>
                    <button
                      type="button"
                      style={{
                        ...pageStyles.buttonSecondary,
                        opacity: deviceExpertLoading ? 0.65 : 1,
                        cursor: deviceExpertLoading ? 'not-allowed' : 'pointer'
                      }}
                      onClick={() => {
                        setDeviceExpertQuestion('');
                        setDeviceExpertMessages([]);
                        setDeviceExpertError(null);
                        setDeviceExpertConversationId(null);
                      }}
                      disabled={deviceExpertLoading}
                    >
                      Clear
                    </button>
                  </div>
                  {deviceExpertError && <div style={pageStyles.expertError}>{deviceExpertError}</div>}
                  {deviceExpertMessages.length > 0 && (
                    <div style={pageStyles.expertMessages}>
                      {deviceExpertMessages.map(message => (
                        <div
                          key={message.id}
                          style={{
                            ...pageStyles.expertMessage,
                            ...(message.role === 'assistant'
                              ? pageStyles.expertAssistant
                              : pageStyles.expertUser)
                          }}
                        >
                          <strong>{message.role === 'assistant' ? 'Assistant' : 'You'}:</strong>{' '}
                          {message.role === 'assistant'
                            ? formatDeviceExpertText(message.content)
                            : message.content}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div style={pageStyles.section}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <div style={{ fontSize: '1.25rem', fontWeight: 600, color: BRAND_TEXT }}>
                  Repair execution steps
                </div>
                <span style={pageStyles.muted}>
                  {hasGuidedSteps
                    ? 'Track TSFAD verification and repair progress, then add manual extras if needed.'
                    : 'No TSFAD plan is available. Use manual repair entry for notes, parts, and photos.'}
                </span>
              </div>
              {functionalTestState.selectedType && (
                <button
                  type="button"
                  style={pageStyles.repairExecutionToggle}
                  onClick={() => setRepairStepsCollapsed(prev => !prev)}
                >
                  {repairStepsCollapsed ? 'Show repair steps' : 'Collapse repair steps'}
                </button>
              )}
            </div>
            {repairStepsCollapsed ? (
              <div style={pageStyles.repairStepsCollapsedNotice}>
                Repair execution steps are collapsed while the selected final functional test is in focus.
              </div>
            ) : steps.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
                <div style={pageStyles.muted}>No repair steps yet.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {steps.map(step => (
                  <StepCard
                    key={step.id}
                    step={step}
                    analysisRequiredParts={
                      Array.isArray(workspace?.analysis?.requiredParts)
                        ? [...(workspace?.analysis?.requiredParts || [])]
                        : []
                    }
                    partCatalog={Array.isArray(workspace?.partCatalog) ? [...(workspace?.partCatalog || [])] : []}
                    onToggleChecklist={handleToggleChecklist}
                    onSaveChecklistDraft={handleSaveChecklistDraft}
                    onSaveStep={stepId => {
                      void handleSaveStep(stepId);
                    }}
                    onNotesChange={handleNotesChange}
                    onAddPart={handleAddPart}
                    onRemovePart={handleRemovePart}
                    onAddAttachment={handleAddAttachment}
                    onRemoveAttachment={handleRemoveAttachment}
                    onCaptureChange={handleCaptureChange}
                    onOpenFeedback={handleOpenFeedback}
                    onFlagReferenceImage={openImageFlagModal}
                    onReactivate={handleReactivateStep}
                    canEditChecklist={!taskLocked && !workspaceReadOnly}
                    canSaveStep={!taskLocked && !workspaceReadOnly && !saving && !savingStepId}
                    stepDirty={dirtyStepIds.includes(step.id)}
                    stepSaving={savingStepId === step.id}
                  />
                ))}
              </div>
            )}

            <div style={pageStyles.repairFooterActions}>
              <div style={pageStyles.repairFooterLeftActions}>
                {(['MFTP', 'CFTP'] as FunctionalTestType[]).map(type => {
                  const isActive = functionalTestState.selectedType === type;
                  const isLoading = functionalTestReferenceLoading === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      style={{
                        ...pageStyles.functionalTestButton,
                        ...(isActive ? pageStyles.functionalTestButtonActive : {}),
                        opacity:
                          isLoading || manualStepSaving || taskLocked || workspaceReadOnly
                            ? 0.72
                            : 1,
                        cursor:
                          isLoading || manualStepSaving || taskLocked || workspaceReadOnly
                            ? 'not-allowed'
                            : 'pointer'
                      }}
                      onClick={() => {
                        void handleSelectFunctionalTest(type);
                      }}
                      disabled={isLoading || manualStepSaving || taskLocked || workspaceReadOnly}
                    >
                      {isLoading ? 'Loading...' : FUNCTIONAL_TEST_TYPE_COPY[type].buttonLabel}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                style={{
                  ...pageStyles.buttonNavy,
                  opacity:
                    manualStepSaving || taskLocked || workspaceReadOnly || !workspace?.run?.id
                      ? 0.7
                      : 1,
                  cursor:
                    manualStepSaving || taskLocked || workspaceReadOnly || !workspace?.run?.id
                      ? 'not-allowed'
                      : 'pointer'
                }}
                onClick={handleAddManualEntryStep}
                disabled={manualStepSaving || taskLocked || workspaceReadOnly || !workspace?.run?.id}
              >
                {manualStepSaving
                  ? 'Adding...'
                  : steps.length === 0
                  ? 'Start Manual Repair Entry'
                  : 'Add Manual Repair Extras'}
              </button>
            </div>

            {functionalTestState.selectedType && functionalTestState.reference && (
              <div style={pageStyles.functionalTestZone}>
                <div style={pageStyles.functionalTestZoneHeader}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                    <div style={pageStyles.functionalTestZoneTitle}>Final Functional Test Zone</div>
                    <div style={pageStyles.functionalTestZoneMeta}>
                      {selectedFunctionalTestCopy?.description}
                    </div>
                    <div style={pageStyles.functionalTestZoneMeta}>
                      {functionalTestState.reference.title}
                      {functionalTestState.reference.revisionId
                        ? ` | Revision ${functionalTestState.reference.revisionId}`
                        : ''}
                      {functionalTestState.reference.generatedAt
                        ? ` | ${formatDateTime(functionalTestState.reference.generatedAt)}`
                        : ''}
                    </div>
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={functionalTestStatusChipStyle}>{functionalTestSummaryLabel}</span>
                      <span style={pageStyles.functionalTestZoneMeta}>
                        {functionalTestProgress.completed} of {functionalTestProgress.total} items checked
                      </span>
                    </div>
                  </div>
                  <div style={pageStyles.functionalTestZoneActions}>
                    <button
                      type="button"
                      style={pageStyles.repairExecutionToggle}
                      onClick={() => setFunctionalTestCollapsed(prev => !prev)}
                    >
                      {functionalTestCollapsed ? 'Expand final test' : 'Collapse final test'}
                    </button>
                    <button
                      type="button"
                      style={{
                        ...pageStyles.buttonSecondary,
                        opacity: taskLocked || workspaceReadOnly ? 0.65 : 1,
                        cursor: taskLocked || workspaceReadOnly ? 'not-allowed' : 'pointer'
                      }}
                      onClick={() => {
                        void handleClearFunctionalTest();
                      }}
                      disabled={taskLocked || workspaceReadOnly}
                    >
                      Clear final test
                    </button>
                    <button
                      type="button"
                      style={{
                        ...pageStyles.buttonPrimary,
                        opacity:
                          saving || taskLocked || workspaceReadOnly || !functionalTestDirty
                            ? 0.7
                            : 1,
                        cursor:
                          saving || taskLocked || workspaceReadOnly || !functionalTestDirty
                            ? 'not-allowed'
                            : 'pointer'
                      }}
                      onClick={() => {
                        void handleSaveFunctionalTest();
                      }}
                      disabled={saving || taskLocked || workspaceReadOnly || !functionalTestDirty}
                    >
                      {saving ? 'Saving...' : 'Save final functional test'}
                    </button>
                  </div>
                </div>

                {!functionalTestCollapsed && (
                  <>
                    {functionalTestState.reference.steps.length === 0 ? (
                      <div style={pageStyles.functionalTestEmpty}>
                        No checklist entries are defined for this final functional test reference.
                      </div>
                    ) : (
                      functionalTestState.reference.steps.map((referenceStep, index) => (
                        (() => {
                          const checklist = Array.isArray(referenceStep.metadata?.checklist)
                            ? referenceStep.metadata?.checklist ?? []
                            : [];
                          return (
                            <div
                              key={`${referenceStep.stepKey || referenceStep.label}-${index}`}
                              style={pageStyles.functionalTestStepCard}
                            >
                              <div>
                                <div style={pageStyles.functionalTestZoneTitle}>
                                  {referenceStep.label}
                                </div>
                                {referenceStep.metadata?.layout && (
                                  <div style={pageStyles.functionalTestZoneMeta}>
                                    Layout: {String(referenceStep.metadata.layout).toUpperCase()}
                                  </div>
                                )}
                              </div>
                              {checklist.length > 0 ? (
                                <div style={pageStyles.functionalTestChecklist}>
                                  {checklist.map(item => {
                                    const checked = functionalTestState.normalizedCompletedItemIds.includes(
                                      item.id
                                    );
                                    return (
                                      <label
                                        key={item.id}
                                        style={pageStyles.functionalTestChecklistRow}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => handleToggleFunctionalTestItem(item.id)}
                                          disabled={taskLocked || workspaceReadOnly}
                                          style={{ marginTop: '0.15rem' }}
                                        />
                                        <span>
                                          <span style={pageStyles.functionalTestChecklistLabel}>
                                            {item.label}
                                          </span>
                                          {item.spec && (
                                            <div style={pageStyles.functionalTestChecklistSpec}>
                                              {item.spec}
                                            </div>
                                          )}
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div style={pageStyles.functionalTestEmpty}>
                                  No checklist entries are defined for this step.
                                </div>
                              )}
                            </div>
                          );
                        })()
                      ))
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                      <div style={pageStyles.fieldLabel}>Functional test notes</div>
                      <textarea
                        style={{
                          ...pageStyles.functionalTestNotes,
                          ...(taskLocked || workspaceReadOnly ? pageStyles.textInputDisabled : {})
                        }}
                        value={functionalTestState.notes}
                        onChange={event => handleFunctionalTestNotesChange(event.target.value)}
                        placeholder="Add any final verification notes, adjustments, or handover observations."
                        disabled={taskLocked || workspaceReadOnly}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          </fieldset>

          {feedbackModal && (
            <div style={pageStyles.feedbackBackdrop} onClick={closeFeedbackModal}>
              <div style={pageStyles.feedbackModal} onClick={event => event.stopPropagation()}>
                <div style={pageStyles.feedbackHeader}>
                  <div>
                    <div style={pageStyles.feedbackTitle}>Flag Documentation Issue</div>
                    <div style={{ fontSize: '0.85rem', color: BRAND_MUTED }}>
                      Cause: {feedbackModal.stepLabel}
                    </div>
                  </div>
                  <button
                    type="button"
                    style={pageStyles.feedbackClose}
                    onClick={closeFeedbackModal}
                    aria-label="Close documentation feedback modal"
                  >
                    X
                  </button>
                </div>
                <div style={pageStyles.feedbackBody}>
                  <div style={pageStyles.feedbackContext}>
                    <div>
                      <strong>Cause:</strong> {feedbackModal.stepLabel}
                    </div>
                    {feedbackScope !== 'CAUSE' && feedbackSelectedItem && (
                      <div>
                        <strong>
                          {feedbackScope === 'VERIFICATION'
                            ? 'Verification step:'
                            : 'Repair step:'}
                        </strong>{' '}
                        {feedbackSelectedItem.label}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={pageStyles.feedbackLabel}>Applies to *</label>
                    <select
                      style={pageStyles.feedbackSelect}
                      value={feedbackScope}
                      onChange={event => {
                        const value = event.target.value as FeedbackScope;
                        setFeedbackError(null);
                        setFeedbackScope(value);
                        if (value === 'CAUSE') {
                          setFeedbackItemId(null);
                        } else {
                          const first = feedbackModal.availableItems.find(
                            item => item.type === value
                          );
                          setFeedbackItemId(first ? first.id : null);
                        }
                      }}
                      disabled={feedbackSubmitting}
                    >
                      <option value="CAUSE">Entire cause</option>
                      {feedbackModal.availableItems.some(item => item.type === 'VERIFICATION') && (
                        <option value="VERIFICATION">Specific verification step</option>
                      )}
                      {feedbackModal.availableItems.some(item => item.type === 'REPAIR') && (
                        <option value="REPAIR">Specific repair step</option>
                      )}
                    </select>
                  </div>

                  {feedbackScope !== 'CAUSE' && (
                    <div>
                      <label style={pageStyles.feedbackLabel}>Select step *</label>
                      {feedbackScopedItems.length > 0 ? (
                        <select
                          style={pageStyles.feedbackSelect}
                          value={feedbackItemId ?? ''}
                          onChange={event => {
                            setFeedbackError(null);
                            setFeedbackItemId(event.target.value || null);
                          }}
                          disabled={feedbackSubmitting}
                        >
                          {feedbackScopedItems.map(item => (
                            <option key={item.id} value={item.id}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div style={pageStyles.feedbackContext}>
                          No steps available for this category.
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label style={pageStyles.feedbackLabel}>Issue type *</label>
                    <select
                      style={pageStyles.feedbackSelect}
                      value={feedbackForm.feedbackType}
                      onChange={event => {
                        setFeedbackError(null);
                        setFeedbackForm(prev => ({ ...prev, feedbackType: event.target.value }));
                      }}
                      disabled={feedbackSubmitting}
                    >
                      {FEEDBACK_TYPE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={pageStyles.feedbackLabel}>Severity *</label>
                    <select
                      style={pageStyles.feedbackSelect}
                      value={feedbackForm.severity}
                      onChange={event => {
                        setFeedbackError(null);
                        setFeedbackForm(prev => ({ ...prev, severity: event.target.value }));
                      }}
                      disabled={feedbackSubmitting}
                    >
                      {FEEDBACK_SEVERITY_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={pageStyles.feedbackLabel}>Issue title *</label>
                    <input
                      style={pageStyles.feedbackInput}
                      type="text"
                      placeholder="Brief description of the issue"
                      value={feedbackForm.title}
                      onChange={event => {
                        setFeedbackError(null);
                        setFeedbackForm(prev => ({ ...prev, title: event.target.value }));
                      }}
                      disabled={feedbackSubmitting}
                    />
                  </div>

                  <div>
                    <label style={pageStyles.feedbackLabel}>Detailed description *</label>
                    <textarea
                      style={pageStyles.feedbackTextarea}
                      placeholder="Explain the documentation issue in detail..."
                      value={feedbackForm.description}
                      onChange={event => {
                        setFeedbackError(null);
                        setFeedbackForm(prev => ({ ...prev, description: event.target.value }));
                      }}
                      disabled={feedbackSubmitting}
                    />
                  </div>

                  <div>
                    <label style={pageStyles.feedbackLabel}>Impact (optional)</label>
                    <input
                      style={pageStyles.feedbackInput}
                      type="text"
                      placeholder="e.g., '30 minutes wasted', 'safety risk'"
                      value={feedbackForm.impact}
                      onChange={event => {
                        setFeedbackError(null);
                        setFeedbackForm(prev => ({ ...prev, impact: event.target.value }));
                      }}
                      disabled={feedbackSubmitting}
                    />
                  </div>

                  {feedbackError && <div style={pageStyles.feedbackError}>{feedbackError}</div>}
                </div>
                <div style={pageStyles.feedbackFooter}>
                  <button
                    type="button"
                    style={pageStyles.feedbackCancel}
                    onClick={closeFeedbackModal}
                    disabled={feedbackSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{
                      ...pageStyles.feedbackSubmit,
                      ...(feedbackSubmitting ? pageStyles.feedbackSubmitDisabled : {})
                    }}
                    onClick={handleSubmitFeedback}
                    disabled={feedbackSubmitting || workspaceReadOnly}
                  >
                    {feedbackSubmitting ? 'Submitting...' : 'Submit feedback'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {imageFlagTarget && (
            <div style={pageStyles.feedbackBackdrop} onClick={closeImageFlagModal}>
              <div style={pageStyles.feedbackModal} onClick={event => event.stopPropagation()}>
                <div style={pageStyles.feedbackHeader}>
                  <div>
                    <div style={pageStyles.feedbackTitle}>Flag Reference Image</div>
                    <div style={{ fontSize: '0.85rem', color: BRAND_MUTED }}>
                      Cause: {imageFlagTarget.stepLabel}
                    </div>
                  </div>
                  <button
                    type="button"
                    style={pageStyles.feedbackClose}
                    onClick={closeImageFlagModal}
                    aria-label="Close image feedback modal"
                  >
                    X
                  </button>
                </div>
                <div style={pageStyles.feedbackBody}>
                  <div style={pageStyles.feedbackContext}>
                    <div>
                      <strong>Cause:</strong> {imageFlagTarget.stepLabel}
                    </div>
                    <div>
                      <strong>Image:</strong>{' '}
                      {imageFlagTarget.image.label || 'Untitled reference image'}
                    </div>
                    {imageFlagTarget.summary && (
                      <div>
                        <strong>Summary:</strong> {imageFlagTarget.summary}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={pageStyles.feedbackLabel}>Notes (optional)</label>
                    <textarea
                      style={pageStyles.feedbackTextarea}
                      value={imageFlagNotes}
                      onChange={event => setImageFlagNotes(event.target.value)}
                      placeholder="Describe what is wrong or misleading about this reference image."
                      disabled={imageFlagSubmitting}
                    />
                  </div>
                </div>
                <div style={pageStyles.feedbackFooter}>
                  <button
                    type="button"
                    style={pageStyles.feedbackCancel}
                    onClick={closeImageFlagModal}
                    disabled={imageFlagSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{
                      ...pageStyles.feedbackSubmit,
                      ...(imageFlagSubmitting ? pageStyles.feedbackSubmitDisabled : {})
                    }}
                    onClick={() => void handleSubmitImageFlag()}
                    disabled={imageFlagSubmitting || workspaceReadOnly}
                  >
                    {imageFlagSubmitting ? 'Submitting...' : 'Submit flag'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <RepairServiceReportModal
            open={serviceReportModalOpen}
            document={serviceReportDraft}
            saving={serviceReportSaving}
            regenerating={serviceReportLoading}
            sending={serviceReportSending}
            error={serviceReportError}
            mode={serviceReportMode}
            subtitle={
              serviceReportMode === 'view'
                ? 'Locked saved service report snapshot'
                : 'Preview the template-filled report generated from this repair summary.'
            }
            hint={
              serviceReportMode === 'view'
                ? savedServiceReportSent
                  ? 'This service report has already been sent to the customer.'
                  : 'This saved service report has not been sent yet. Regenerate it after summary or template changes, then save the refreshed report before sending.'
                : 'Review the generated service report before saving it to the case.'
            }
            canRegenerate={serviceReportMode === 'view' && canRebuildServiceReport}
            regenerateLabel="Regenerate service report"
            onRegenerate={handleCreateServiceReport}
            saveLabel={workspace?.serviceReport?.document ? 'Save refreshed report' : 'Create report'}
            canSend={
              serviceReportMode === 'view' &&
              !workspaceReadOnly &&
              taskStatus === 'COMPLETED' &&
              Boolean(workspace?.serviceReport?.id) &&
              !savedServiceReportSent
            }
            sendLabel="Send to customer"
            onClose={closeServiceReportModal}
            onSave={handleSaveServiceReport}
            onSend={handleSendServiceReport}
          />

          {reportError && (
            <div style={pageStyles.dialogBackdrop}>
              <div style={pageStyles.dialog}>
                <div style={pageStyles.dialogTitle}>Summary generation failed</div>
                <div style={pageStyles.dialogDescription}>{reportError}</div>
                <div style={pageStyles.dialogButtons}>
                  <button
                    type="button"
                    style={pageStyles.dialogPrimary}
                    onClick={() => setReportError(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {statusDialog && dialogCopy && (
            <div style={pageStyles.dialogBackdrop}>
              <div style={pageStyles.dialog}>
                <div style={pageStyles.dialogTitle}>{dialogCopy.title}</div>
                <div style={pageStyles.dialogDescription}>{dialogCopy.description}</div>
                <textarea
                  style={pageStyles.dialogInput}
                  value={statusDialog.reason}
                  onChange={event =>
                    setStatusDialog(prev =>
                      prev ? { ...prev, reason: event.target.value } : prev
                    )
                  }
                  placeholder={statusDialog.requireReason ? 'Required reason...' : 'Optional note...'}
                  disabled={saving}
                />
                {dialogError && <div style={pageStyles.dialogError}>{dialogError}</div>}
                <div style={pageStyles.dialogButtons}>
                  <button
                    type="button"
                    style={pageStyles.dialogSecondary}
                    onClick={closeStatusDialog}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={pageStyles.dialogPrimary}
                    onClick={submitStatusDialog}
                    disabled={saving || workspaceReadOnly}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RepairTask;







